import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { authAPI, productsAPI, configAPI, adminItemsAPI, jobsAPI, proxyImageUrl, proxyClothingImageUrl, groupsAPI, clothingAPI, retailAPI, photoFixAPI } from '../api/client';
import { useAuthStore } from '../store';
import ManageProductsTab from '../components/ManageProductsTab';
import AnalyticsTab from '../components/AnalyticsTab';
import SelectionTab from '../components/SelectionTab';
import CheeseTab from '../components/CheeseTab';
import RetailTab from '../components/RetailTab';
import PhotoFixTab from '../components/PhotoFixTab';
import PhotoCatalogSection from '../components/PhotoCatalogSection';
import AutomationTab from '../components/AutomationTab';
import { CropModal, CATALOG_RATIO, RATIO_TOLERANCE } from '../components/CropModal';
import { manageAPI } from '../api/client';
import { formatKyivDateTime, formatKyivDate, formatKyivTime } from '../utils/kyivTime';
import '../styles/admin.css';
import '../styles/analytics.css';

// ── Парсинг цветов из названия товара ────────────────────────────────────────
const _COLORS_MULTI = [
  'Rose Gold', 'Neon Green', 'Neon Yellow',
  'Dark Blue', 'Dark Grey', 'Light Blue', 'Light Green', 'Light Grey', 'Light Violet',
];
const _COLORS_SINGLE = [
  'Aqua', 'Beige', 'Black', 'Blue', 'Bordo', 'Bronze', 'Brown', 'Burgundy',
  'Camo', 'Caramel', 'Chameleon', 'Chestnut', 'Chocolate', 'Clear', 'Copper',
  'Coral', 'Cream', 'Fuchsia', 'Ginger', 'Glitter', 'Gold', 'Graphite',
  'Green', 'Greige', 'Grey', 'Gum', 'Hologram', 'Ivory', 'Khaki',
  'Leopard', 'Lilac', 'Lime', 'Magenta', 'Metallic', 'Milk', 'Mint',
  'Mocha', 'Multicolor', 'Mustard', 'Navy', 'Nude', 'Olive', 'Orange',
  'Peach', 'Pink', 'Purple', 'Red', 'Reflective', 'Rose', 'Silver',
  'Turquoise', 'Violet', 'White', 'Yellow',
];

function parseColorsFromName(name) {
  if (!name || !name.trim()) return '';
  const matches = [];
  const used = [];
  const check = (color) => {
    const re = new RegExp(`\\b${color}\\b`, 'gi');
    let m;
    while ((m = re.exec(name)) !== null) {
      const s = m.index, e = s + m[0].length;
      if (!used.some(([a, b]) => s < b && e > a)) {
        used.push([s, e]);
        matches.push({ index: s, color });
      }
    }
  };
  _COLORS_MULTI.forEach(check);
  _COLORS_SINGLE.forEach(check);
  matches.sort((a, b) => a.index - b.index);
  return matches.map(m => m.color).join(', ');
}

const CatalogCardImage = ({ src, alt, proxyFn }) => {
  const [err, setErr]       = useState(false);
  const [loaded, setLoaded] = useState(false);
  const proxy = proxyFn || proxyImageUrl;
  if (!src || err) return <div className="queue-catalog-card-nophoto">нет фото</div>;
  return (
    <>
      <img
        className="queue-catalog-card-img"
        src={proxy(src)}
        alt={alt}
        loading="lazy"
        style={loaded ? {} : { display: 'none' }}
        onLoad={() => setLoaded(true)}
        onError={() => setErr(true)}
      />
      {!loaded && <div className="queue-catalog-card-nophoto queue-catalog-card-loading" />}
    </>
  );
};

// Фото с авторетраем и фолбэком на прямую ссылку:
// 1. Пробуем через прокси (нужен для imgbox/Google)
// 2. При ошибке прокси (403 — хост не в whitelist) — пробуем прямую ссылку
// 3. Если и прямая не работает — ретраи с задержкой 2/4/6 сек, потом onFailed
const RetryImage = ({ src, alt, className, style, onClick, onFailed }) => {
  const [attempt, setAttempt]       = useState(0);
  const [key, setKey]               = useState(0);
  const [failed, setFailed]         = useState(false);
  const [useDirect, setUseDirect]   = useState(false); // true = прямая ссылка без прокси
  const timerRef                    = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setKey(0);
    setFailed(false);
    setUseDirect(false);
    return () => clearTimeout(timerRef.current);
  }, [src]);

  const handleError = () => {
    // Первая ошибка через прокси → пробуем напрямую
    if (!useDirect) {
      setUseDirect(true);
      return;
    }
    // Прямая ссылка тоже не работает → ретраи
    if (attempt < 3) {
      const delay = (attempt + 1) * 2000;
      timerRef.current = setTimeout(() => setKey(k => k + 1), delay);
      setAttempt(a => a + 1);
    } else {
      setFailed(true);
      onFailed?.();
    }
  };

  if (!src || failed) return null;
  return (
    <img
      key={`${key}-${useDirect}`}
      src={useDirect ? src : proxyImageUrl(src)}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onError={handleError}
    />
  );
};


// DEBUG_TODO: remove after testing — set to false to use normal flow
const DEBUG_STEP_MODE = true;

// ── Helpers ───────────────────────────────────────────────────────────────────

// Таблицы замены раскладки: при вводе в неверной раскладке символ автоматически корректируется
const _RU_TO_EN = { й:'q',ц:'w',у:'e',к:'r',е:'t',н:'y',г:'u',ш:'i',щ:'o',з:'p',х:'[',ъ:']',ф:'a',ы:'s',в:'d',а:'f',п:'g',р:'h',о:'j',л:'k',д:'l',ж:';',э:"'",я:'z',ч:'x',с:'c',м:'v',и:'b',т:'n',ь:'m',б:',',ю:'.' };
const _EN_TO_RU = { q:'й',w:'ц',e:'у',r:'к',t:'е',y:'н',u:'г',i:'ш',o:'щ',p:'з','[':'х',']':'ъ',a:'ф',s:'ы',d:'в',f:'а',g:'п',h:'р',j:'о',k:'л',l:'д',';':'ж',"'":'э',z:'я',x:'ч',c:'с',v:'м',b:'и',n:'т',m:'ь',',':'б','.':'ю' };

function _convertLayout(str, layout) {
  if (!layout) return str;
  const map = layout === 'en' ? _RU_TO_EN : _EN_TO_RU;
  return str.split('').map(ch => {
    const lo = ch.toLowerCase();
    const mapped = map[lo];
    if (!mapped) return ch;
    return ch !== lo ? mapped.toUpperCase() : mapped;
  }).join('');
}

// Case-insensitive autocomplete (replaces native <datalist> which is case-sensitive in most browsers)
// Uses a portal-like fixed-position dropdown to avoid clipping by overflow:hidden parents
// layout="en" → auto-converts Russian input to Latin; layout="ru" → Latin input to Russian
function ComboBox({ value, onChange, options = [], placeholder, className, layout }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [rect, setRect] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setQ(value || ''); }, [value]);

  const openDropdown = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
  };

  const filtered = q.trim()
    ? options.filter(o => o.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <>
      <input
        ref={inputRef}
        className={className}
        value={q}
        placeholder={placeholder}
        onFocus={openDropdown}
        onChange={e => {
          const val = _convertLayout(e.target.value, layout);
          setQ(val);
          onChange(val);
          openDropdown();
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && filtered.length > 0 && rect && (
        <div style={{
          position: 'fixed',
          zIndex: 9999,
          left: rect.left,
          top: rect.bottom + 2,
          width: rect.width,
          background: 'var(--bg-primary, #1a1a2e)',
          border: '1px solid var(--border-color, #444)',
          borderRadius: 6,
          maxHeight: 220,
          overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}>
          {filtered.map(o => (
            <div key={o}
              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #2a2a3e)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => { onChange(o); setQ(o); setOpen(false); }}
            >
              {o}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const SyncPanel = ({ title, status, onSync, syncing }) => (
  <div className="admin-sync-panel">
    <div className="admin-sync-info">
      <span className="admin-sync-title">{title}</span>
      {status?.last_sync && (
        <span className="admin-sync-meta">
          Обновлено: {formatKyivDateTime(status.last_sync)}
          {status.total != null && ` · ${status.total} записей`}
          {status.added > 0 && ` · +${status.added} новых`}
          {status.updated > 0 && ` · ${status.updated} изменено`}
        </span>
      )}
      {!status?.last_sync && !status?.last_error && (
        <span className="admin-sync-meta admin-sync-never">Ещё не синхронизировался</span>
      )}
      {status?.last_error && (
        <span className="admin-sync-error" title={status.last_error}>
          Ошибка: {status.last_error}
          {status.last_error_at && (
            <span className="admin-sync-error-time">
              {' '}· {formatKyivTime(status.last_error_at)}
            </span>
          )}
        </span>
      )}
    </div>
    <button
      className={`admin-btn-sync${status?.last_error ? ' admin-btn-sync--error' : ''}`}
      onClick={onSync}
      disabled={syncing}
    >
      {syncing ? 'Синхронизация...' : status?.last_error ? 'Повторить' : 'Синхронизировать'}
    </button>
  </div>
);

const ImageCacheResizePanel = () => {
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollStatus = async () => {
    try {
      const res = await apiClient.get('/admin/images/resize-cache/status');
      setStatus(res.data);
      if (res.data.state !== 'running') stopPolling();
    } catch {}
  };

  // При монтировании — проверяем текущий статус и запускаем поллинг если нужно
  useEffect(() => {
    pollStatus();
    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    setStatus({ state: 'running' });
    try {
      await apiClient.post('/admin/images/resize-cache');
      stopPolling();
      pollRef.current = setInterval(pollStatus, 2000);
    } catch (e) {
      setStatus({ state: 'error', error: e.response?.data?.detail || e.message });
    }
  };

  const running = status?.state === 'running';

  return (
    <div className="admin-sync-panel">
      <div className="admin-sync-info">
        <span className="admin-sync-title">Ресайз кэша изображений</span>
        <span className="admin-sync-meta">Уменьшает все фото до макс. 1280px по длинной стороне</span>
        {status?.state === 'running' && (
          <span className="admin-sync-meta">
            Выполняется: {status.checked ?? 0}{status.total ? ` / ${status.total}` : ''} файлов
            {status.resized > 0 && ` · уменьшено ${status.resized}`}
            {status.total > 0 && (
              <span style={{ display: 'block', marginTop: 4, height: 4, borderRadius: 2, background: 'var(--lg-border)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', borderRadius: 2, background: 'var(--lg-accent)', width: `${Math.round((status.checked / status.total) * 100)}%`, transition: 'width 0.3s' }} />
              </span>
            )}
          </span>
        )}
        {status?.state === 'done' && (
          <span className="admin-sync-meta">
            Готово · Проверено: {status.checked} · Уменьшено: {status.resized} · Ошибок: {status.errors}
          </span>
        )}
        {status?.state === 'error' && <span className="admin-sync-error">{status.error}</span>}
      </div>
      <button className="admin-btn-sync" onClick={handleRun} disabled={running}>
        {running ? 'Обработка...' : 'Запустить'}
      </button>
    </div>
  );
};

const ImageCachePurgePanel = () => {
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollStatus = async () => {
    try {
      const res = await apiClient.get('/admin/images/purge-cache/status');
      setStatus(res.data);
      if (res.data.state !== 'running') stopPolling();
    } catch {}
  };

  useEffect(() => {
    pollStatus();
    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    if (!window.confirm('Удалить все невалидные файлы кэша (HTML-страницы ошибок)?\nВалидные фото не затронет.')) return;
    setStatus({ state: 'running' });
    try {
      await apiClient.post('/admin/images/purge-cache');
      stopPolling();
      pollRef.current = setInterval(pollStatus, 2000);
    } catch (e) {
      setStatus({ state: 'error', error: e.response?.data?.detail || e.message });
    }
  };

  const running = status?.state === 'running';

  return (
    <div className="admin-sync-panel">
      <div className="admin-sync-info">
        <span className="admin-sync-title">Очистка мусора в кэше</span>
        <span className="admin-sync-meta">Удаляет HTML-страницы ошибок, сохранённые вместо фото (~29% файлов)</span>
        {status?.state === 'running' && (
          <span className="admin-sync-meta">
            Выполняется: {status.checked ?? 0}{status.total ? ` / ${status.total}` : ''} файлов
            {status.deleted > 0 && ` · удалено ${status.deleted}`}
            {status.total > 0 && (
              <span style={{ display: 'block', marginTop: 4, height: 4, borderRadius: 2, background: 'var(--lg-border)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', borderRadius: 2, background: '#e05', width: `${Math.round((status.checked / status.total) * 100)}%`, transition: 'width 0.3s' }} />
              </span>
            )}
          </span>
        )}
        {status?.state === 'done' && (
          <span className="admin-sync-meta">
            Готово · Проверено: {status.checked} · Удалено: {status.deleted} · Из БД: {status.db_removed} · Ошибок: {status.errors}
          </span>
        )}
        {status?.state === 'error' && <span className="admin-sync-error">{status.error}</span>}
      </div>
      <button className="admin-btn-sync" onClick={handleRun} disabled={running}
        style={!running ? { background: '#c0392b' } : {}}>
        {running ? 'Удаление...' : 'Очистить'}
      </button>
    </div>
  );
};

const RestorePlaceholdersPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRun = async () => {
    if (!window.confirm(
      'Восстановить записи в БД для некэшированных фото?\n' +
      'Браузер будет получать быстрый 404 от нашего nginx вместо медленных внешних серверов.'
    )) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await apiClient.post('/admin/images/restore-broken-placeholders');
      setStatus({ ok: true, ...res.data });
    } catch (e) {
      setStatus({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-sync-panel">
      <div className="admin-sync-info">
        <span className="admin-sync-title">Восстановить записи кэша</span>
        <span className="admin-sync-meta">
          Вставляет placeholder-записи для URL без локального кэша — фото будут быстро показывать «не найдено» вместо ожидания внешних серверов
        </span>
        {status?.ok && (
          <span className="admin-sync-meta">
            Готово · Добавлено: {status.inserted} записей из {status.total_uncached} некэшированных URL
          </span>
        )}
        {status && !status.ok && (
          <span className="admin-sync-error">{status.error}</span>
        )}
      </div>
      <button className="admin-btn-sync" onClick={handleRun} disabled={loading}
        style={!loading ? { background: '#2980b9' } : {}}>
        {loading ? 'Восстановление...' : 'Восстановить'}
      </button>
    </div>
  );
};

// Метаданные групп и полей конфига
const CONFIG_GROUPS = [
  {
    id: 'bots',
    title: 'Telegram боти',
    icon: '🤖',
    keys: [
      {
        key: 'telegram_bot_token',
        sensitive: true,
        bot: '@alldropai_bot',
        desc: 'Пошук товарів по фото, завантаження кешу imgbox, підбірки для покупців',
      },
      {
        key: 'cheese_tg_bot_token',
        sensitive: true,
        bot: 'Cheese бот',
        desc: 'Публікує нові товари Cheese у Telegram-канал',
      },
      {
        key: 'photo_send_tg_bot_token',
        sensitive: true,
        desc: 'Розсилка всіх фото з кешу адміну в Telegram (бекап)',
      },
      {
        key: 'cheese_tg_chat_id',
        desc: 'Канал або чат куди Cheese-бот публікує нові товари',
      },
      {
        key: 'cheese_tg_file_chat_id',
        desc: 'Чат куди відправляється Excel-файл закупки Cheese',
      },
    ],
  },
  {
    id: 'cheese_tpl',
    title: 'Cheese — шаблон посту',
    icon: '📝',
    keys: [{ key: 'cheese_tg_template' }],
  },
  {
    id: 'sheets_shoes',
    title: 'Google Sheets — Взуття',
    icon: '📊',
    keys: [
      { key: 'catalog_sheet_id' },
      { key: 'catalog_sheet_name' },
      { key: 'suppliers_sheet_id' },
      { key: 'suppliers_sheet_name' },
    ],
  },
  {
    id: 'sheets_clothing',
    title: 'Google Sheets — Одяг',
    icon: '📊',
    keys: [
      { key: 'clothing_catalog_sheet_id' },
      { key: 'clothing_catalog_sheet_name' },
      { key: 'clothing_models_sheet_id' },
      { key: 'clothing_models_sheet_name' },
      { key: 'clothing_suppliers_sheet_id' },
      { key: 'clothing_suppliers_sheet_name' },
    ],
  },
  {
    id: 'finance',
    title: 'Фінансові параметри',
    icon: '💰',
    keys: [
      { key: 'exchange_rate' },
      { key: 'kg_delivery_price' },
    ],
  },
  {
    id: 'system',
    title: 'Система',
    icon: '⚙️',
    keys: [{ key: 'site_url' }],
  },
  {
    id: 'ai',
    title: 'AI / Claude',
    icon: '✨',
    keys: [
      {
        key: 'claude_api_key',
        sensitive: true,
        desc: 'Ключ з platform.claude.com (console) — для генерації назв товарів',
      },
      {
        key: 'claude_site_fields_prompt',
        textarea: true,
        desc: 'Інструкція для AI при генерації всіх полів картки товару (кнопка «Згенерувати всі поля»): описи, SEO, характеристики',
      },
      {
        key: 'claude_site_name_prompt',
        textarea: true,
        desc: 'Інструкція для AI при генерації назви товару (кнопка ✨ при додаванні товару на сайт)',
      },
      {
        key: 'claude_site_name_formats',
        textarea: true,
        desc: 'Формати назви для окремих сайтів. Кожен рядок: «слово з назви або домену сайту | інструкція». Для сайтів, що збіглись, назва береться за цією інструкцією замість загальної',
      },
    ],
  },
];

const HIDDEN_KEYS = new Set(['_internal_catalog_last_sync','_internal_suppliers_last_sync','_internal_clothing_catalog_last_sync','_internal_clothing_models_last_sync','_internal_clothing_suppliers_last_sync','photo_fix_supplier_order']);

const ConfigSection = () => {
  const [config, setConfig] = useState([]);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    configAPI.getConfig()
      .then(res => {
        setConfig(res.data);
        const vals = {};
        res.data.forEach(c => { vals[c.key] = c.value; });
        setEditing(vals);
      })
      .catch(() => {});
  }, []);

  const handleSave = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await configAPI.updateConfig(key, editing[key]);
      setConfig(prev => prev.map(c => c.key === key ? { ...c, value: editing[key] } : c));
    } catch {}
    setSaving(s => ({ ...s, [key]: false }));
  };

  if (config.length === 0) return null;

  const byKey = Object.fromEntries(config.map(c => [c.key, c]));

  // Ключи, которые попали в группы
  const groupedKeys = new Set(CONFIG_GROUPS.flatMap(g => g.keys.map(k => k.key)));

  // Всё что не попало в группы и не скрыто — в "Інше"
  const ungrouped = config.filter(c => !groupedKeys.has(c.key) && !HIDDEN_KEYS.has(c.key));

  const renderField = (meta) => {
    const c = byKey[meta.key];
    if (!c) return null;
    const isTpl = meta.key === 'cheese_tg_template';
    const isTextarea = isTpl || meta.textarea;
    const isSensitive = meta.sensitive;
    const val = editing[meta.key] ?? c.value ?? '';

    return (
      <div key={meta.key} style={{ marginBottom: 14 }}>
        {/* Бот-бейдж */}
        {meta.bot && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, background: 'var(--lg-accent-dim)', color: 'var(--lg-accent)', borderRadius: 5, padding: '2px 8px' }}>{meta.bot}</span>
            {meta.desc && <span style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>{meta.desc}</span>}
          </div>
        )}
        <div className={`config-row${isTextarea ? ' config-row--textarea' : ''}`}>
          <span className="config-label">
            {c.label || meta.key}
            {isSensitive && <span style={{ fontSize: 10, color: 'var(--lg-text-3)', marginLeft: 6 }}>🔒</span>}
          </span>
          {isTextarea ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <textarea
                className="config-input config-textarea"
                value={val}
                onChange={e => setEditing(prev => ({ ...prev, [meta.key]: e.target.value }))}
                rows={isTpl ? 12 : 5}
                style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
              />
              {isTpl && (
                <div className="config-tpl-vars">
                  Змінні:&nbsp;
                  {['{name}','{code}','{manufacturer}','{sizes}','{sizes_range}','{materials}','{box}','{market_price_avg}','{purchase_price_per_pair}','{supplier_article}','{catalog_sku}'].map(v => (
                    <code key={v} className="config-tpl-var" onClick={() => {
                      setEditing(prev => ({ ...prev, [meta.key]: (prev[meta.key] ?? c.value) + v }));
                    }}>{v}</code>
                  ))}
                  <span style={{ opacity: 0.6 }}>— натисніть щоб вставити</span>
                </div>
              )}
            </div>
          ) : (
            <input
              className="config-input"
              type={isSensitive ? 'password' : 'text'}
              value={val}
              onChange={e => setEditing(prev => ({ ...prev, [meta.key]: e.target.value }))}
              placeholder={isSensitive ? 'Введіть новий токен для заміни…' : ''}
              autoComplete="off"
            />
          )}
          <button
            className="config-save-btn"
            disabled={saving[meta.key] || val === (c.value ?? '')}
            onClick={() => handleSave(meta.key)}
          >
            {saving[meta.key] ? '...' : 'Зберегти'}
          </button>
        </div>
        {/* Опис без бота */}
        {!meta.bot && meta.desc && (
          <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginTop: 2, paddingLeft: 2 }}>{meta.desc}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {CONFIG_GROUPS.map(group => {
        const fields = group.keys.map(meta => ({ meta, c: byKey[meta.key] })).filter(f => f.c);
        if (!fields.length) return null;
        return (
          <div key={group.id} className="config-block">
            <h3 className="config-block-title">{group.icon} {group.title}</h3>
            <div className="config-table">
              {group.keys.map(meta => renderField(meta))}
            </div>
          </div>
        );
      })}

      {ungrouped.length > 0 && (
        <div className="config-block">
          <h3 className="config-block-title">⚙️ Інше</h3>
          <div className="config-table">
            {ungrouped.map(c => renderField({ key: c.key }))}
          </div>
        </div>
      )}

      <p className="config-hint">Зміни набудуть чинності при наступній синхронізації.</p>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const AdminPage = ({ onLogout, onGoToCatalog, onSettings }) => {
  // Deep-link з кнопки "Добавить товар" (Розница → Сайт → Аналіз → Відсутні
  // товари) — відкривається в НОВІЙ вкладці браузера на цьому самому URL з
  // query-параметрами, тому читаємо їх один раз при завантаженні (не під час
  // роботи — тут немає повноцінного роутера, просто початковий стан).
  const _addProductDeepLink = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      // Префікс "addp_" навмисно — голий ?sku=... вже зайнятий іншою, старішою
      // логікою (App.jsx hasSku-перевірка, HomePage.jsx) — вона відкриває
      // публічну картку товару в каталозі, а не адмінку.
      if (p.get('action') !== 'add-product' || !p.get('addp_sku') || !p.get('addp_site')) return null;
      return {
        sku: p.get('addp_sku') || '', name: p.get('addp_name') || '', brand: p.get('addp_brand') || '',
        model: p.get('addp_model') || '', sizes: p.get('addp_sizes') || '', color: p.get('addp_color') || '',
        season: p.get('addp_season') || '', photoUrl: p.get('addp_photo_url') || '',
        originSiteId: parseInt(p.get('addp_site'), 10) || null,
      };
    } catch { return null; }
  })();
  const [activeTab, setActiveTab] = useState(() =>
    _addProductDeepLink ? 'roznitsa' : (localStorage.getItem('admin_active_tab') || 'users')
  );
  const [addProductPrefill] = useState(_addProductDeepLink);
  const [staleCount, setStaleCount] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeUserGroupId, setActiveUserGroupId] = useState(() => {
    const v = localStorage.getItem('admin_active_user_group_id');
    if (!v) return null;
    return v === 'none' ? 'none' : parseInt(v, 10);
  });
  const [userGroupsModalOpen, setUserGroupsModalOpen] = useState(false);
  const [usersSubTab, setUsersSubTab] = useState(() => localStorage.getItem('admin_users_subtab') || 'active');
  const [online, setOnline] = useState({ online_users: 0, online_devices: 0, per_user: {} });
  const [usersLoading, setUsersLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [search, setSearch] = useState('');

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState('');
  const [queueCacheAge, setQueueCacheAge] = useState(null); // секунды
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState('');
  const [queueForm, setQueueForm] = useState({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
  const [newItemForm, setNewItemForm]   = useState({ after_id: '', product_name: '', season: '', color: '', new_list_name: '', brand: '' });
  const [newItemConfig, setNewItemConfig] = useState(null);
  const [newItemConfigLoading, setNewItemConfigLoading] = useState(false);
  const [addModelMode, setAddModelMode] = useState(false);
  const [fixSheetMode, setFixSheetMode] = useState(false);
  const [addModelForm, setAddModelForm] = useState({ after_id: '', product_name: '', season: '', color: '', photo_url: '', new_list_name: '', keywords: '', brand: '' });
  const [addModelSaving, setAddModelSaving] = useState(false);
  const [addModelError, setAddModelError] = useState('');
  const [addModelDone, setAddModelDone] = useState('');
  const [addModelSelectedProduct, setAddModelSelectedProduct] = useState(null);
  const [addModelCropFile, setAddModelCropFile] = useState(null);
  const [genNameLoading, setGenNameLoading] = useState(false);
  const [genNameError, setGenNameError] = useState('');

  const handleGenerateProductName = async () => {
    if (!addModelForm.photo_url) { setGenNameError('Спочатку додайте фото'); return; }
    setGenNameError('');
    setGenNameLoading(true);
    try {
      const res = await adminItemsAPI.generateProductName(addModelForm.photo_url, '');
      const v = res.data.name;
      const c = parseColorsFromName(v);
      setAddModelForm(f => ({ ...f, product_name: v, ...(c ? { color: c } : {}) }));
    } catch (e) {
      setGenNameError(e.response?.data?.detail || 'Помилка генерації назви');
    }
    setGenNameLoading(false);
  };

  // Fix Sheet form

  const [fixSheetForm, setFixSheetForm] = useState({ catalog_id: '', new_list_name: '', after_id: '', is_new_model: false, delete_old: false, old_list_name: '', old_after_id: '' });
  const [fixSheetLoading, setFixSheetLoading] = useState(false);
  const [fixSheetResult, setFixSheetResult] = useState(null);
  const [fixSheetError, setFixSheetError] = useState('');

  // Activity state
  const [activity, setActivity] = useState(null);
  const [activityDays, setActivityDays] = useState(30);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showQueueActivity, setShowQueueActivity] = useState(false);
  const [queueFormError, setQueueFormError] = useState('');
  const [queueFormSaving, setQueueFormSaving] = useState(false);
  const [rehostingPhoto, setRehostingPhoto] = useState(false);
  const [debugStepperOpen, setDebugStepperOpen] = useState(false); // DEBUG_TODO
  const [newCatalogStepperOpen, setNewCatalogStepperOpen] = useState(false); // DEBUG_TODO
  const [queueFormDone, setQueueFormDone] = useState('');
  const [refreshingSupplier, setRefreshingSupplier] = useState(null);
  const [articleDetails, setArticleDetails] = useState(null);
  const [articleDetailsLoading, setArticleDetailsLoading] = useState(false);
  const [similarArticles, setSimilarArticles] = useState([]);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameDone, setRenameDone] = useState('');
  const [photoLinkCopied, setPhotoLinkCopied] = useState(false);
  const [articleBdpromUrl, setArticleBdpromUrl] = useState(null); // null=не загружено, ''=нет, 'https://...'=есть
  const [gasAvtoartLoading, setGasAvtoartLoading] = useState(false);
  const [gasAvtoartMsg, setGasAvtoartMsg] = useState('');
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);
  // Локальний пошук по фото (CLIP, працює тільки на цьому Маку) — опційна
  // підказка, ніколи не блокує основну форму, якщо сервіс недоступний.
  const [photoSearchOpen,    setPhotoSearchOpen]    = useState(false);
  const [photoSearchLoading, setPhotoSearchLoading] = useState(false);
  const [photoSearchResults, setPhotoSearchResults] = useState(null);
  const [photoSearchError,   setPhotoSearchError]   = useState('');
  const photoSearchRef = useRef(null); // handlePhotoSearch оголошується нижче
  // Скидаємо результати пошуку і одразу шукаємо заново, коли змінилось фото
  // постачальника (перейшли до іншого товару) — інакше на екрані лишаються
  // підказки від попереднього товару, а нове фото треба шукати вручну.
  useEffect(() => {
    setPhotoSearchOpen(false);
    setPhotoSearchResults(null);
    setPhotoSearchError('');
    const supplierUrl = queueForm.photo_url || articleDetails?.photo_url;
    if (supplierUrl) photoSearchRef.current?.();
  }, [queueForm.photo_url, articleDetails?.photo_url]);
  const [compareProduct, setCompareProduct] = useState(null); // null = закрыто
  const [compareSection, setCompareSection] = useState('shoes'); // секция из которой открыто сравнение
  const [compareIndex, setCompareIndex] = useState(-1);
  const [rotatingUrl, setRotatingUrl]                   = useState(null);
  const [photoTimestamps, setPhotoTimestamps]           = useState(() => {
    try {
      const raw = localStorage.getItem('alldrop_photoTs');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return Object.fromEntries(Object.entries(parsed).filter(([, ts]) => ts > cutoff));
    } catch { return {}; }
  });
  const [photoUploading, setPhotoUploading]             = useState(false);
  const [photoDragOver, setPhotoDragOver]               = useState(false);
  const [catalogPhotoUploading, setCatalogPhotoUploading] = useState(false);
  const [catalogPhotoDragOver, setCatalogPhotoDragOver]   = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const photoInputRef        = useRef(null);
  const catalogPhotoInputRef = useRef(null);

  // Jobs / background queue state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const cbSentinelRef      = useRef(null);
  const cbProductsRef      = useRef(null);
  const scrolledToCatalogIdRef = useRef(null);   // id товару, до якого вже прокручували — не повторюємо на кожне оновлення списку
  const loadMoreRef        = useRef(null);
  const clickTimerRef         = useRef(null);
  const navigateCompareRef    = useRef(null);
  const catalogIdTimerRef     = useRef(null);
  const afterIdTimerRef       = useRef(null);
  const hoverPrefetchRef      = useRef(null); // debounce timer for article hover prefetch
  const articleLoadAbortRef   = useRef(null); // отменяет устаревший запрос деталей артикула
  const enqueueGuardRef       = useRef(false); // защита от двойного клика при добавлении
  const cbAbortRef            = useRef(null); // отменяет устаревший запрос каталога при смене фильтра
  const cbSearchTimerRef      = useRef(null); // debounce для поиска в каталоге
  const cbBrandsGenRef        = useRef(0);    // generation counter для getBrandsModels (отменяет устаревшие ответы)

  // Catalog browser state
  const [cbSection, setCbSection] = useState('shoes'); // 'shoes' | 'clothing'
  const [cbQuery, setCbQuery] = useState('');
  const [cbBrand, setCbBrand] = useState('');
  const [cbModel, setCbModel] = useState('');
  const [cbProducts, setCbProducts] = useState([]);
  const [cbLoading, setCbLoading] = useState(false);
  const [cbPage, setCbPage] = useState(0);
  const [cbHasMore, setCbHasMore] = useState(false);
  const [cbBrandsModels, setCbBrandsModels] = useState({});

  // Clothing catalog browser state
  const [cbClothingStep, setCbClothingStep]   = useState('type'); // 'type' | 'brand' | 'model' | 'products'
  const [cbClothingQuery, setCbClothingQuery] = useState('');
  const [cbClothingType, setCbClothingType]   = useState('');
  const [cbClothingBrand, setCbClothingBrand] = useState('');
  const [cbClothingOcCode, setCbClothingOcCode] = useState('');
  const [cbClothingModels, setCbClothingModels] = useState([]);
  const [cbClothingModelsLoading, setCbClothingModelsLoading] = useState(false);
  const [cbClothingSearchModels, setCbClothingSearchModels] = useState([]);
  const [cbClothingProducts, setCbClothingProducts] = useState([]);
  const [cbClothingLoading, setCbClothingLoading] = useState(false);
  const [cbClothingPage, setCbClothingPage] = useState(0);
  const [cbClothingHasMore, setCbClothingHasMore] = useState(false);
  const [cbClothingNav, setCbClothingNav] = useState({ types: [], brands_by_type: {} });
  const [cbClothingNavFilter, setCbClothingNavFilter] = useState(null); // Set<name> or null=show all
  const cbClothingAbortRef        = useRef(null);
  const cbClothingSentinelRef     = useRef(null);
  const cbClothingProductsRef     = useRef(null);
  const cbClothingLoadMoreRef     = useRef(null);
  const cbClothingSearchTimerRef  = useRef(null);

  // Sync state
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [suppliersStatus, setSuppliersStatus] = useState(null);
  const [syncingSuppliers, setSyncingSuppliers] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);

  // Clothing sync state
  const [clothingCatalogStatus, setClothingCatalogStatus] = useState(null);
  const [clothingSuppliersStatus, setClothingSuppliersStatus] = useState(null);
  const [clothingModelsStatus, setClothingModelsStatus] = useState(null);
  const [syncingClothingCatalog, setSyncingClothingCatalog] = useState(false);
  const [syncingClothingSuppliers, setSyncingClothingSuppliers] = useState(false);
  const [syncingClothingModels, setSyncingClothingModels] = useState(false);
  const [syncingClothingAll, setSyncingClothingAll] = useState(false);

  // Clothing add form state
  const [clothingAddConfig, setClothingAddConfig] = useState(null);
  const [clothingAddConfigLoading, setClothingAddConfigLoading] = useState(false);
  const [clothingAddForm, setClothingAddForm] = useState({
    name: '', photo_url: '', color: '', brand: '', product_type: '',
    gender: '', description: '', material: '', measurements: '', season: '',
    other_colors_mode: '', // '' | 'new' | 'existing' | 'none'
    other_colors_id: '', oc_measurements: '',
    new_brand: false, new_type: false, new_color: false,
  });
  const [clothingAddSaving, setClothingAddSaving] = useState(false);
  const [clothingAddError, setClothingAddError] = useState('');
  const [clothingAddDone, setClothingAddDone] = useState('');

  const currentUser = useAuthStore((state) => state.user);
  const setUser     = useAuthStore((state) => state.setUser);
  const isRealAdmin = !!(currentUser?.is_admin && !currentUser?.impersonated_by);
  const isTeamOnly  = !!(currentUser?.is_team && !currentUser?.is_admin);
  // Доступ до каталогу (взуття/одяг): адмін, команда або передплата з каталогом
  const hasCatalogAccess = !!(currentUser?.is_admin || currentUser?.is_team || currentUser?.subscription_type);
  // Кнопка "Надіслати фото в Telegram" в шапці — ярлик до тієї самої дії, що
  // й у Фікс фото (photoFixAPI.sendToTg), щоб не заходити туди щоразу.
  // Доступна лише цьому конкретному адміну, за явним запитом — не всім адмінам.
  const canSendPhotosToTg = currentUser?.email === 'bodioness@gmail.com';

  // Хелпер: є певне право (адмін у режимі Imperial бачить тільки права цільового юзера)
  const hasPerm = (perm) => {
    if (isRealAdmin) return true;
    return !!(currentUser?.permissions?.includes(perm));
  };

  // Завантажуємо список доступних прав (тільки для реального адміна)
  useEffect(() => {
    if (!isRealAdmin) return;
    authAPI.getPermissionsList()
      .then(res => setPermList(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [isRealAdmin]);

  // Критичні помилки сайтів (Horoshop/Prom auth, синк тощо) — видно з будь-якої
  // вкладки, щоб не пропустити, як з KrossBox (побачили тільки зайшовши в Сайти).
  const [siteErrors, setSiteErrors] = useState([]);
  const [siteErrorsOpen, setSiteErrorsOpen] = useState(false);
  const [retailOpenSiteId, setRetailOpenSiteId] = useState(null);
  useEffect(() => {
    if (!isRealAdmin) return;
    const load = () => retailAPI.getSiteErrors().then(r => setSiteErrors(r.data || [])).catch(() => {});
    load();
    const id = setInterval(load, 120 * 1000); // раз на 2 хв
    return () => clearInterval(id);
  }, [isRealAdmin]);

  // Ярлик "Надіслати фото в Telegram" у шапці — той самий бекенд-процес,
  // що й кнопка в Фікс фото (polling статусу/запуск/зупинка так само).
  const [sendTgRunning, setSendTgRunning] = useState(false);
  const [sendTgResult,  setSendTgResult]  = useState(null);
  const sendTgPollRef = useRef(null);

  useEffect(() => {
    if (!canSendPhotosToTg) return;
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.sendToTgStatus();
        const data = s.data;
        if (!data || data.status !== 'running') return;
        setSendTgRunning(true);
        setSendTgResult(data);
        interval = setInterval(async () => {
          try {
            const r = await photoFixAPI.sendToTgStatus();
            setSendTgResult(r.data);
            if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
              clearInterval(interval);
              setSendTgRunning(false);
            }
          } catch {}
        }, 3000);
        sendTgPollRef.current = interval;
      } catch {}
    })();
    return () => clearInterval(interval);
  }, [canSendPhotosToTg]);

  useEffect(() => () => clearInterval(sendTgPollRef.current), []);

  const handleHeaderSendToTg = async () => {
    setSendTgRunning(true);
    setSendTgResult(null);
    try {
      await photoFixAPI.sendToTg();
    } catch (e) {
      setSendTgResult({ status: 'error', error: e?.response?.data?.detail || 'Ошибка запуска' });
      setSendTgRunning(false);
      return;
    }
    sendTgPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.sendToTgStatus();
        setSendTgResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(sendTgPollRef.current);
          setSendTgRunning(false);
        }
      } catch {}
    }, 3000);
  };

  // Перенаправляємо на доступну вкладку якщо поточна недоступна
  useEffect(() => {
    if (isRealAdmin) return;
    const tabPerms = {
      users: false, tables: false, queue: hasPerm('queue'), manage: false,
      stale: false, jobs: false, activity: false,
      analytics: hasPerm('analytics'), selection: hasPerm('selection'),
      cheese: false, roznitsa: hasPerm('retail_sites') || hasPerm('retail_publish') || hasPerm('retail_site_admin'),
      photo_fix: hasPerm('photo_fix'), photo_catalog: false, automation: false,
    };
    if (tabPerms[activeTab] === false) {
      const first = Object.entries(tabPerms).find(([, ok]) => ok !== false);
      if (first) setActiveTab(first[0]);
      // Не редиректимо в каталог — там теж може не бути доступу (призводить до петлі)
      // Просто залишаємось на admin-сторінці, App.jsx обробить no_access якщо треба
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Deep-link вкладка "Добавить товар" не повинна підміняти звичайну
    // "останню відкриту вкладку" — це одноразова спеціальна вкладка в
    // ОКРЕМОМУ вікні браузера, localStorage спільний на весь сайт.
    if (_addProductDeepLink) return;
    localStorage.setItem('admin_active_tab', activeTab);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setCompareProduct(null); return; }
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') navigateCompareRef.current?.(1);
      if (e.key === 'ArrowLeft')  navigateCompareRef.current?.(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Скролл к предполагаемому товару после загрузки каталога — ОДИН раз на
  // вибір товару, а не щоразу коли оновлюється cbProducts (напр. довантаження
  // наступної сторінки при скролі саме списку) — інакше кожне таке оновлення
  // знову запускало scrollIntoView і користувача постійно відкидало назад
  // до того самого товару, не даючи погортати список далі.
  useEffect(() => {
    if (!selectedCatalogProduct) { scrolledToCatalogIdRef.current = null; return; }
    if (cbProducts.length === 0) return;
    const id = selectedCatalogProduct.id;
    if (scrolledToCatalogIdRef.current === id) return;   // вже прокручували саме до цього товару
    // Двойной rAF: первый ждёт commit React, второй — layout браузера
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const card = cbProductsRef.current?.querySelector(`[data-id="${id}"]`);
        if (card) {
          card.scrollIntoView({ block: 'center', behavior: 'instant' });
          scrolledToCatalogIdRef.current = id;
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [cbProducts, selectedCatalogProduct]);

  // ── Data loaders ──────────────────────────────────────────────────────────

  const loadUsers = useCallback(async () => {
    try {
      const res = await authAPI.getUsers();
      setUsers(res.data);
    } catch {}
    finally { setUsersLoading(false); }
  }, []);

  const loadGroups = useCallback(async () => {
    try { setGroups(await groupsAPI.list()); } catch {}
  }, []);

  const reloadUsersAndGroups = useCallback(async () => {
    await Promise.all([loadUsers(), loadGroups()]);
  }, [loadUsers, loadGroups]);

  const loadOnline = useCallback(async () => {
    try { const res = await authAPI.getOnline(); setOnline(res.data); } catch {}
  }, []);

  const loadSyncStatuses = useCallback(async () => {
    try {
      const [cat, sup] = await Promise.allSettled([
        productsAPI.getSyncStatus(),
        productsAPI.getSuppliersStatus(),
      ]);
      if (cat.status === 'fulfilled') setCatalogStatus(cat.value.data);
      if (sup.status === 'fulfilled') setSuppliersStatus(sup.value.data);
    } catch {}
    try {
      const [ccat, csup, cmod] = await Promise.allSettled([
        clothingAPI.getSyncStatus(),
        clothingAPI.getSuppliersStatus(),
        clothingAPI.getModelsStatus(),
      ]);
      if (ccat.status === 'fulfilled') setClothingCatalogStatus(ccat.value.data);
      if (csup.status === 'fulfilled') setClothingSuppliersStatus(csup.value.data);
      if (cmod.status === 'fulfilled') setClothingModelsStatus(cmod.value.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (isTeamOnly) return; // team users don't have access to admin-only endpoints
    loadUsers();
    loadGroups();
    loadOnline();
    loadSyncStatuses();
    manageAPI.getStaleProducts(6).then(res => setStaleCount(res.data.total)).catch(() => {});
    const interval = setInterval(loadOnline, 30 * 1000);
    return () => clearInterval(interval);
  }, [isTeamOnly, loadUsers, loadGroups, loadOnline, loadSyncStatuses]);

  // ── Queue handlers ────────────────────────────────────────────────────────

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueError('');
    try {
      const res = await adminItemsAPI.getQueue();
      setQueue(res.data.items ?? res.data);
      setQueueCacheAge(res.data.cache_age_seconds ?? null);
    } catch (e) {
      setQueueError(e?.response?.data?.detail || 'Ошибка загрузки очереди');
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const handleRefreshCache = async () => {
    setCacheRefreshing(true);
    const snapSupplier = selectedSupplier;
    const snapArticle  = selectedArticle;
    try {
      await adminItemsAPI.refreshCache();
      // Ждём ~6 сек и перезагружаем очередь — фоновое обновление успеет завершиться
      setTimeout(async () => {
        try {
          await loadQueue();
          // Если был выбран поставщик но не выбран артикул — подгрузить первый
          if (snapSupplier && !snapArticle) {
            try {
              const res = await adminItemsAPI.getSupplierQueue(snapSupplier.supplier);
              const fresh = res.data;
              setSelectedSupplier(fresh);
              setQueue(prev => prev.map(s => s.supplier === fresh.supplier ? fresh : s));
              if (fresh.all_articles.length > 0) {
                handleSelectArticle(fresh.all_articles[0]);
              }
            } catch {}
          }
        } finally {
          setCacheRefreshing(false);
        }
      }, 6000);
    } catch {
      setCacheRefreshing(false);
    }
  };

  const loadActivity = async (days = activityDays) => {
    setActivityLoading(true);
    try {
      const res = await adminItemsAPI.getActivity(days);
      setActivity(res.data);
    } catch {}
    finally { setActivityLoading(false); }
  };

  // Загружаем задачи сразу при монтировании — чтобы бейдж с количеством отображался до перехода во вкладку
  useEffect(() => { loadJobs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'queue') {
      if (queue.length === 0 && !queueLoading) loadQueue();
      if (cbProducts.length === 0 && !cbLoading) {
        loadCatalogBrowser({ page: 0 });
        loadCbBrandsModels();
      }
    }
    if (activeTab === 'activity') {
      loadActivity(activityDays);
    }
    if (activeTab === 'jobs') {
      loadJobs();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling: обновляем задачи каждые 3с когда вкладка активна, иначе 6с пока есть активные задачи
  useEffect(() => {
    const hasActive = () => jobs.some(j => j.status === 'pending' || j.status === 'running');
    if (activeTab !== 'jobs' && !hasActive()) return;
    const ms = activeTab === 'jobs' ? 3000 : 6000;
    const id = setInterval(() => { loadJobs(); }, ms);
    return () => clearInterval(id);
  }, [activeTab, jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefreshSupplier = async (e, supplier) => {
    if (e) e.stopPropagation();
    setRefreshingSupplier(supplier);
    try {
      const res = await adminItemsAPI.getSupplierQueue(supplier, { refresh: true });
      const fresh = res.data;
      setQueue(prev => prev.map(s => s.supplier === supplier ? fresh : s).filter(s => s.count > 0));
      if (selectedSupplier?.supplier === supplier) {
        setSelectedSupplier(fresh);
        setQueueForm({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
        setQueueFormError('');
        setQueueFormDone('');
        // Если артикул не выбран — подгружаем первый из обновлённого списка
        if (!selectedArticle && fresh.all_articles.length > 0) {
          handleSelectArticle(fresh.all_articles[0]);
        } else {
          setSelectedArticle('');
        }
      }
    } catch {}
    finally { setRefreshingSupplier(null); }
  };

  const handleSelectSupplier = (entry) => {
    setSelectedSupplier(entry);
    setSelectedArticle('');
    setQueueForm({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
    setQueueFormError('');
    setQueueFormDone('');
    setArticleDetails(null);
    setSelectedCatalogProduct(null);
    setSimilarArticles([]);
    setRenameDone('');
    setArticleBdpromUrl(null);
    // Прогреваем кэши GSheets заранее, чтобы первый клик на артикул был быстрым
    adminItemsAPI.prefetchSupplier(entry.supplier).catch(() => {});
    // Получаем полный список артикулов из личного листа (в фоне обновляет all_articles)
    adminItemsAPI.getSupplierQueue(entry.supplier)
      .then(res => {
        const fresh = res.data;
        // Убираем артикулы которые уже в очереди задач (pending/running) — бэкенд их ещё не обработал
        const inQueue = new Set(
          jobs
            .filter(j => j.supplier === fresh.supplier && (j.status === 'pending' || j.status === 'running'))
            .map(j => j.supplier_article)
            .filter(Boolean)
        );
        const articles = fresh.all_articles.filter(a => !inQueue.has(a));
        setSelectedSupplier(prev =>
          prev?.supplier === fresh.supplier ? { ...prev, all_articles: articles, count: articles.length } : prev
        );
        setQueue(prev => prev.map(s => s.supplier === fresh.supplier ? { ...s, all_articles: articles, count: articles.length } : s));
      })
      .catch(() => {});
  };

  const handlePhotoUrlBlur = async (val) => {
    if (!val || !val.startsWith('https://shoesland.ua/uploads/shop')) return;
    setRehostingPhoto(true);
    try {
      const res = await adminItemsAPI.rehostPhoto(val);
      if (res.data.rehosted) {
        setQueueForm(f => ({ ...f, photo_url: res.data.url }));
      }
    } catch (e) {
      setQueueFormError('Не удалось загрузить фото ShoesLand на imgbox: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRehostingPhoto(false);
    }
  };

  const handleRenameArticle = async (oldArticle) => {
    setRenameLoading(true);
    setQueueFormError('');
    try {
      await jobsAPI.enqueueRenameArticle(selectedSupplier.supplier, oldArticle, selectedArticle);
      setRenameDone(`Переименование «${oldArticle}» → «${selectedArticle}» добавлено в очередь`);
      setSimilarArticles([]);
      // Находим следующий артикул ДО очистки списка
      const currentList = selectedSupplier?.all_articles || [];
      const currentIdx = currentList.indexOf(selectedArticle);
      const nextArticle = currentIdx >= 0 ? currentList[currentIdx + 1] : null;
      // Убираем новый артикул из очереди — он уже обрабатывается как старый
      setQueue(prev => prev.map(s =>
        s.supplier === selectedSupplier.supplier
          ? { ...s, count: s.count - 1, all_articles: s.all_articles.filter(a => a !== selectedArticle) }
          : s
      ).filter(s => s.count > 0));
      setSelectedSupplier(prev => prev ? {
        ...prev,
        count: prev.count - 1,
        all_articles: prev.all_articles.filter(a => a !== selectedArticle),
      } : null);
      setSelectedArticle('');
      if (nextArticle) handleSelectArticle(nextArticle);
    } catch (e) {
      setQueueFormError(e.response?.data?.detail || 'Ошибка постановки в очередь');
    } finally {
      setRenameLoading(false);
    }
  };

  const handleSelectArticle = async (article) => {
    // Отменяем предыдущий незавершённый запрос
    if (articleLoadAbortRef.current) articleLoadAbortRef.current.abort();
    const abortCtrl = new AbortController();
    articleLoadAbortRef.current = abortCtrl;
    const stale = () => abortCtrl.signal.aborted;

    setSelectedArticle(article);
    setQueueForm({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
    setQueueFormError('');
    setQueueFormDone('');
    setSelectedCatalogProduct(null);
    setArticleDetails(null);
    setSimilarArticles([]);
    setRenameDone('');
    setArticleBdpromUrl(null);
    setArticleDetailsLoading(true);

    // Независимый запрос EASYBDPROM — не блокирует основной поток
    adminItemsAPI.getEasybdpromUrl(selectedSupplier.supplier, article)
      .then(r => { if (!stale()) setArticleBdpromUrl(r.data.url || ''); })
      .catch(() => { if (!stale()) setArticleBdpromUrl(''); });

    // Параллельно запрашиваем похожие артикулы
    adminItemsAPI.findSimilarArticles(selectedSupplier.supplier, article)
      .then(r => { if (!stale()) setSimilarArticles(r.data || []); })
      .catch(() => {});

    try {
      const res = await adminItemsAPI.getArticleDetails(selectedSupplier.supplier, article, abortCtrl.signal);
      if (stale()) return; // пока грузились — выбрали другой артикул

      const d = res.data;
      setArticleDetails(d);

      const formPatch = {};
      if (d.suggested_catalog_id) formPatch.catalog_id = d.suggested_catalog_id;
      if (d.photo_link) formPatch.photo_link = d.photo_link;
      if (d.photo_url)  formPatch.photo_url  = d.photo_url;
      const kw = typeof d.keywords === 'string' ? d.keywords.trim() : '';
      if (kw && !kw.startsWith('{')) formPatch.keywords = kw;
      if (Object.keys(formPatch).length) setQueueForm(f => ({ ...f, ...formPatch }));

      // Найти товар и открыть группу в каталоге
      if (d.suggested_catalog_id) {
        try {
          const pr = await productsAPI.listProducts(0, 1, { search: d.suggested_catalog_id });
          if (stale()) return;
          const match = (pr.data || []).find(p => p.sku === d.suggested_catalog_id);
          if (match) {
            setSelectedCatalogProduct(match);
            jumpToCatalogProduct(match);
          }
        } catch {}
      }
    } catch {}
    finally { if (!stale()) setArticleDetailsLoading(false); }
  };

  const selectCatalogRef = useRef(null);
  selectCatalogRef.current = (p) => {
    setSelectedCatalogProduct(p);
    if (addModelMode) {
      setAddModelSelectedProduct(p);
      setAddModelForm(f => ({ ...f, after_id: p.sku }));
      return;
    }
    if (queueForm.is_new && cbSection === 'clothing') {
      // Заполняем форму добавления одежды данными из каталога
      const oc = p.other_colors || '';
      setClothingAddForm({
        name: p.name || '',
        photo_url: p.photo_url || '',
        color: p.color || '',
        brand: p.brand || '',
        product_type: p.product_type || '',
        gender: p.gender || '',
        description: p.description || '',
        material: p.material || '',
        measurements: p.measurements || '',
        season: p.season || '',
        other_colors_mode: oc ? 'existing' : '',
        other_colors_id: oc, oc_measurements: '',
        new_brand: false,
        new_type: false,
        new_color: false,
      });
    } else if (queueForm.is_new) {
      setNewItemForm(f => ({ ...f, after_id: p.sku, season: p.season || f.season }));
    } else {
      setQueueForm(f => ({ ...f, catalog_id: p.sku }));
    }
  };
  const handleSelectCatalogProduct = (p) => selectCatalogRef.current?.(p);

  // ── Локальний пошук по фото (localhost:5001) ───────────────────────────────
  // Працює тільки на цьому Маку, поки запущений AllDrop Photo Search.
  // Будь-яка помилка (сервіс не запущений, немає інтернету до нього тощо)
  // гаситься тихо — жодного впливу на основну форму додавання товару.
  const PHOTO_SEARCH_BASE = 'https://localhost:5001';

  // ── Автоіндексація нового товару в локальному пошуку по фото ───────────────
  // Фонова задача додавання товару пише в Google Таблиці, а не одразу в нашу
  // БД — товар з'являється лише після наступної синхронізації каталогу.
  // Тому чекаємо саме завершення ЗАДАЧІ (там уже відомий фінальний артикул),
  // а не одразу індексуємо. Повністю тихо: без запущеного локального сервісу
  // просто нічого не станеться, ніяких повідомлень користувачу.
  const pollJobThenAutoIndex = async (jobId, { photoUrl, name = '', brand = '', collection = 'shoes' }) => {
    if (!jobId || !photoUrl) return;
    for (let i = 0; i < 30; i++) {                       // до ~90с очікування
      await new Promise(res => setTimeout(res, 3000));
      try {
        const r = await jobsAPI.list();
        const job = (r.data?.jobs || []).find(j => j.id === jobId);
        if (!job) continue;
        if (job.status === 'completed') {
          // catalog_id — для взуття (new_catalog_item / catalog_model_only),
          // sm_id — для одягу/сумок (clothing_item, КаталогТоваровОдягу)
          const sku = (job.step_results || [])
            .map(sr => sr?.result?.catalog_id || sr?.result?.sm_id)
            .find(Boolean);
          if (sku) {
            fetch(`${PHOTO_SEARCH_BASE}/index-one`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sku, photo_url: photoUrl, name, brand, collection }),
            }).catch(() => {});
          }
          return;
        }
        if (job.status === 'failed' || job.status === 'cancelled') return;
      } catch { /* локальний сервіс чи мережа недоступні — тихо виходимо */ }
    }
  };

  // ── Синхронізація з ALLDROP Photo Search: фото ПОСТАЧАЛЬНИКА (не каталожне
  // студійне) одразу йде як підтверджений приклад для перенавчання + шукаємо
  // цю ж модель серед уже відсканованих, ще не перевірених фото ІНШИХ
  // постачальників — щоб зекономити ручну роботу на /review. sku тут —
  // артикул каталогу, вже відомий У МОМЕНТ КЛІКУ (не чекаємо завершення
  // фонової задачі): для ІСНУЮЧОГО товару його вводить сам оператор, для
  // НОВОГО — це наступний артикул за порядком (newItemConfig.next_article /
  // config.next_sm_id), який сервер і так присвоїть саме цьому товару.
  //
  // targetWindow — вкладка, яку відкрили СИНХРОННО, прямо в обробнику кліку
  // (ще до будь-якого await) — браузер блокує window.open як попап, якщо
  // викликати його вже ПІСЛЯ мережевого запиту (поза "жестом користувача"),
  // тому відкриваємо порожню вкладку одразу при кліку і лише пізніше, коли
  // прийде відповідь, перенаправляємо її на сторінку з похожими — або
  // закриваємо, якщо нічого не знайшлось.
  const syncPhotoSearch = ({ sku, supplierPhotoUrl, collection = 'shoes', targetWindow = null }) => {
    if (!sku || !supplierPhotoUrl) {
      if (targetWindow && !targetWindow.closed) targetWindow.close();
      return;
    }
    fetch(`${PHOTO_SEARCH_BASE}/sync/add-confirmed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sku, photo_url: supplierPhotoUrl, collection }),
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!targetWindow || targetWindow.closed) return;
        if (data?.similar_groups?.length) {
          targetWindow.location.href = `${PHOTO_SEARCH_BASE}/similar/${data.photo_id}`;
        } else {
          targetWindow.close();
        }
      })
      .catch(() => { if (targetWindow && !targetWindow.closed) targetWindow.close(); });
  };

  const handlePhotoSearch = async () => {
    const supplierUrl = queueForm.photo_url || articleDetails?.photo_url;
    if (!supplierUrl) return;
    setPhotoSearchOpen(true);
    setPhotoSearchLoading(true);
    setPhotoSearchError('');
    setPhotoSearchResults(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 10000);
      const imgRes = await fetch(
        `${PHOTO_SEARCH_BASE}/img?url=${encodeURIComponent(supplierUrl)}`,
        { signal: ctrl.signal }
      );
      if (!imgRes.ok) throw new Error('img');
      const blob = await imgRes.blob();
      const fd = new FormData();
      fd.append('file', blob, 'photo.jpg');
      // Переключення обувь/одежда у каталозі додавання товару веде за собою
      // і те, в якій колекції векторів шукати — інакше по фото сумки
      // шукались б серед кросівок.
      const psCollection = cbSection === 'clothing' ? 'clothing' : 'shoes';
      const searchRes = await fetch(`${PHOTO_SEARCH_BASE}/search?collection=${psCollection}`, {
        method: 'POST', body: fd, signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (!searchRes.ok) throw new Error('search');
      const data = await searchRes.json();
      const results = data.results || [];
      setPhotoSearchResults(results);
      // Доповнюємо розмірами (measurements) з каталогу — сумки можуть бути
      // схожі на фото, але зовсім різного розміру, тож розмір треба бачити
      // одразу у списку, не клікаючи по кожному. Тільки для одягу/сумок —
      // у взуття measurements не заповнюється.
      if (results.length && psCollection === 'clothing') {
        try {
          const enrichRes = await clothingAPI.getBySkus(results.map(r => r.sku));
          const info = enrichRes.data || {};
          setPhotoSearchResults(results.map(r => ({ ...r, measurements: info[r.sku]?.measurements || '' })));
        } catch {}
      }
    } catch {
      setPhotoSearchResults(null);
      setPhotoSearchError('Локальний пошук недоступний — переконайтесь, що на Маку запущено AllDrop Photo Search');
    } finally {
      setPhotoSearchLoading(false);
    }
  };
  photoSearchRef.current = handlePhotoSearch;

  // Фото-основа в індексі пошуку іноді "підтягується" з сайту постачальника
  // неправильно (не той товар під тим SKU) — показує впевнений, але хибний
  // збіг щоразу. index_id — рядок у vectors.npy (Photo Search сам його не
  // видаляє фізично, лише обнулює вектор — решта фото того ж товару не
  // страждають).
  const handleDeleteFromIndex = async (r, e) => {
    if (e) e.stopPropagation();
    if (!r.index_id && r.index_id !== 0) return;
    if (!window.confirm(`Убрать это фото (${r.sku}) из поиска похожих? Оно больше не будет предлагаться как совпадение.`)) return;
    try {
      await fetch(`${PHOTO_SEARCH_BASE}/index/delete-vector`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: r.index_id, collection: cbSection === 'clothing' ? 'clothing' : 'shoes' }),
      });
      setPhotoSearchResults(prev => (prev || []).filter(x => x.sku !== r.sku));
    } catch {
      alert('Не удалось удалить — проверьте, что на Маке запущен AllDrop Photo Search');
    }
  };

  const handlePickPhotoSearchResult = async (r) => {
    try {
      if (cbSection === 'clothing') {
        const cr = await clothingAPI.getBySku(r.sku);
        const match = cr.data;
        if (!match) return;
        // Відкриваємо список товарів САМЕ ЦІЄЇ моделі (oc_code = other_colors) —
        // так само як при кліку на тайл моделі, а не сам знайдений товар
        // і не порожній вибір моделей.
        const t  = match.product_type || '';
        const b  = match.brand || '';
        const oc = match.other_colors || '';
        setCbClothingQuery('');
        setCbClothingNavFilter(null);
        setCbClothingType(t);
        setCbClothingBrand(b);
        setCbClothingOcCode(oc);
        setCbClothingStep('products');
        loadClothingCatalogBrowser({ query: '', type: t, brand: b, oc_code: oc, page: 0 });
        // Власне вибір знайденого товару — раніше тут тільки відкривався
        // список моделі, а сам товар доводилось вибирати ще раз вручну.
        selectCatalogRef.current?.(match);
        // Підвантажуємо моделі бренду так само, як при звичайному кліку на
        // тайл бренду — інакше повернення назад по хлібних крихтах до кроку
        // "модель" покаже порожньо ("Нет моделей"), бо cbClothingModels
        // ще ніколи не заповнювався для цього бренду.
        setCbClothingModelsLoading(true);
        setCbClothingModels([]);
        clothingAPI.getModels(t, b)
          .then(mr => setCbClothingModels(mr.data.models || []))
          .catch(() => setCbClothingModels([]))
          .finally(() => setCbClothingModelsLoading(false));
      } else {
        const pr = await productsAPI.listProducts(0, 1, { search: r.sku });
        const match = (pr.data || []).find(p => p.sku === r.sku);
        if (!match) return;
        selectCatalogRef.current?.(match);
        // Відкриваємо групу товару в каталозі — так само як при автопідборі по артикулу
        jumpToCatalogProduct(match);
      }
      // Панель результатів пошуку лишаємо відкритою — вона тепер окрема
      // колонка поруч з каталогом, а не заміняє його, тож нічого закривати
    } catch {}
  };

  const handleIsNewToggle = async (checked) => {
    setQueueForm(f => ({ ...f, is_new: checked }));
    if (checked) {
      if (cbSection === 'clothing' && !clothingAddConfig && !clothingAddConfigLoading) {
        setClothingAddConfigLoading(true);
        setClothingAddError('');
        adminItemsAPI.getClothingItemConfig()
          .then(r => setClothingAddConfig(r.data))
          .catch(e => setClothingAddError(e?.response?.data?.detail || 'Не удалось загрузить конфиг. Попробуйте ещё раз.'))
          .finally(() => setClothingAddConfigLoading(false));
      } else if (cbSection !== 'clothing' && !newItemConfig) {
        setNewItemConfigLoading(true);
        try {
          const res = await adminItemsAPI.getNewItemConfig();
          setNewItemConfig(res.data);
        } catch (e) {
          setQueueFormError(e?.response?.data?.detail || 'Не удалось загрузить конфиг нового товара');
        } finally {
          setNewItemConfigLoading(false);
        }
      }
    }
  };

  const handleCatalogIdChange = (value) => {
    setQueueForm(f => ({ ...f, catalog_id: value }));
    clearTimeout(catalogIdTimerRef.current);
    if (!value.trim()) {
      setSelectedCatalogProduct(null);
      return;
    }
    catalogIdTimerRef.current = setTimeout(async () => {
      try {
        const res = await productsAPI.listProducts(0, 1, { search: value.trim() });
        const match = (res.data || []).find(p => p.sku === value.trim());
        if (match) {
          setSelectedCatalogProduct(match);
          jumpToCatalogProduct(match);
        } else {
          setSelectedCatalogProduct(null);
        }
      } catch {}
    }, 400);
  };

  const handleAfterIdChange = (value) => {
    setNewItemForm(f => ({ ...f, after_id: value }));
    clearTimeout(afterIdTimerRef.current);
    if (!value.trim()) return;
    afterIdTimerRef.current = setTimeout(async () => {
      try {
        const res = await productsAPI.listProducts(0, 1, { search: value.trim() });
        const match = (res.data || []).find(p => p.sku === value.trim());
        if (match?.season) {
          setNewItemForm(f => ({ ...f, season: match.season }));
        }
      } catch {}
    }, 400);
  };

  const loadCatalogBrowser = useCallback(async (opts = {}) => {
    const { query = cbQuery, brand = cbBrand, model = cbModel, page = 0, append = false } = opts;

    // Отменяем предыдущий запрос при новом поиске/фильтре (не при подгрузке следующей страницы)
    if (!append) {
      cbAbortRef.current?.abort();
      cbAbortRef.current = new AbortController();
      setCbProducts([]); // мгновенно убираем старые фото
    }
    const signal = cbAbortRef.current?.signal;

    setCbLoading(true);
    try {
      const limit = 48;
      const filters = {};
      if (query) filters.search = query;
      if (brand)  filters.brand = brand;
      if (model)  filters.model = model;
      const res = await productsAPI.listProducts(page * limit, limit, filters, signal);
      const items = res.data;
      if (append) {
        setCbProducts(prev => [...prev, ...items]);
      } else {
        setCbProducts(items);
      }
      setCbPage(page);
      setCbHasMore(items.length === limit);
    } catch (e) {
      // Игнорируем ошибки отменённых запросов
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
    }
    finally {
      if (!signal?.aborted) setCbLoading(false);
    }
  }, [cbQuery, cbBrand, cbModel]);

  // Перехід до конкретного товару в каталозі: спочатку питаємо бекенд, на
  // якій позиції товар лежить у межах тих самих фільтрів (бренд+модель) і
  // сортування, що й сам список — і ОДРАЗУ відкриваємо ТУ сторінку (як і
  // було раніше) — перехід не чекає нічого зайвого. Паралельно, вже ПІСЛЯ
  // переходу, у фоні довантажуємо попередні сторінки (товари тієї самої
  // моделі, що лежать РАНІШЕ за знайдений) і дописуємо їх ЗВЕРХУ списку —
  // інакше вони так ніколи й не потрапляють у DOM (довантаження вниз є,
  // вгору — нема), саме це виглядало як "частина товарів моделі не
  // відображається". Компенсуємо приріст висоти зверху зсувом scrollTop —
  // щоб дописування не зіштовхувало вже відкриту картку з центру екрана.
  const MAX_BACKFILL_PAGES = 20;   // запобіжник від аномально великої групи

  const jumpToCatalogProduct = useCallback(async (match) => {
    const b = match.brand || '';
    const m = match.model || '';
    setCbQuery('');
    setCbBrand(b);
    setCbModel(m);
    const limit = 48;
    let targetPage = 0;
    try {
      const posRes = await productsAPI.findProductPosition(match.sku, { brand: b, model: m });
      const offset = posRes.data?.offset;
      if (typeof offset === 'number') targetPage = Math.floor(offset / limit);
    } catch {}

    await loadCatalogBrowser({ query: '', brand: b, model: m, page: targetPage });

    if (targetPage > 0) {
      const cappedFrom = Math.max(0, targetPage - MAX_BACKFILL_PAGES);
      for (let p = targetPage - 1; p >= cappedFrom; p--) {
        try {
          const res = await productsAPI.listProducts(p * limit, limit, { brand: b, model: m });
          const items = res.data;
          if (!items?.length) continue;
          const root = cbProductsRef.current;
          const prevScrollTop = root?.scrollTop || 0;
          const prevScrollHeight = root?.scrollHeight || 0;
          setCbProducts(prev => [...items, ...prev]);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (root) {
                const delta = root.scrollHeight - prevScrollHeight;
                root.scrollTop = prevScrollTop + delta;
              }
            });
          });
        } catch {}
      }
    }
  }, [loadCatalogBrowser]);

  const loadCbBrandsModels = useCallback(async (query = '') => {
    cbBrandsGenRef.current += 1;
    const myGen = cbBrandsGenRef.current;
    try {
      const res = await productsAPI.getBrandsModels(query, [], false, false);
      if (cbBrandsGenRef.current === myGen) {
        setCbBrandsModels(res.data); // { brand: [model, ...] }
      }
    } catch {}
  }, []);

  const loadClothingCatalogBrowser = useCallback(async (opts = {}) => {
    const { query = cbClothingQuery, type = cbClothingType, brand = cbClothingBrand, oc_code = cbClothingOcCode, page = 0, append = false } = opts;
    if (!append) {
      cbClothingAbortRef.current?.abort();
      cbClothingAbortRef.current = new AbortController();
      setCbClothingProducts([]);
    }
    const signal = cbClothingAbortRef.current?.signal;
    setCbClothingLoading(true);
    try {
      const limit = 48;
      const filters = {};
      if (query)   filters.search = query;
      if (type)    filters.product_type = type;
      if (brand)   filters.brand = brand;
      if (oc_code) filters.oc_code = oc_code;
      const res = await clothingAPI.list(page * limit, limit, filters, signal);
      const items = res.data;
      if (append) {
        setCbClothingProducts(prev => [...prev, ...items]);
      } else {
        setCbClothingProducts(items);
      }
      setCbClothingPage(page);
      setCbClothingHasMore(items.length === limit);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
    } finally {
      if (!signal?.aborted) setCbClothingLoading(false);
    }
  }, [cbClothingQuery, cbClothingType, cbClothingBrand, cbClothingOcCode]);

  const loadClothingNav = useCallback(async () => {
    try {
      const res = await clothingAPI.getNav();
      setCbClothingNav(res.data);
    } catch {}
  }, []);

  const updateClothingNavFilter = useCallback(async (q, step, type, brand = '') => {
    if (!q) { setCbClothingNavFilter(null); setCbClothingSearchModels([]); return; }
    setCbClothingNavFilter(null);
    setCbClothingSearchModels([]);
    try {
      const res = await clothingAPI.searchNav(
        q,
        (step === 'brand' || step === 'model') ? type : undefined,
        step === 'model' ? brand : undefined,
      );
      const key = step === 'model' ? 'oc_code' : 'name';
      setCbClothingNavFilter(new Set(res.data.items.map(i => i[key]).filter(Boolean)));
      if (step === 'model') setCbClothingSearchModels(res.data.items || []);
    } catch { setCbClothingNavFilter(null); setCbClothingSearchModels([]); }
  }, []);

  // Всегда актуальная функция подгрузки следующей страницы (для IntersectionObserver)
  loadMoreRef.current = () => {
    if (cbHasMore && !cbLoading) {
      loadCatalogBrowser({ query: cbQuery, brand: cbBrand, model: cbModel, page: cbPage + 1, append: true });
    }
  };

  // IntersectionObserver — shoes (primary mechanism)
  useEffect(() => {
    if (activeTab !== 'queue' || cbSection !== 'shoes') return;
    const el   = cbSentinelRef.current;
    const root = cbProductsRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current?.(); },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, cbSection, cbPage, cbHasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback: после каждой успешной загрузки проверяем виден ли сентинел
  // (IntersectionObserver не всегда повторно срабатывает если элемент остался видимым)
  useEffect(() => {
    if (activeTab !== 'queue' || cbSection !== 'shoes') return;
    if (cbLoading || !cbHasMore) return;
    const root = cbProductsRef.current;
    const sentinel = cbSentinelRef.current;
    if (!root || !sentinel) return;
    // Сентинел виден если его верхний край меньше высоты скролл-контейнера + запас 200px
    const sentinelTop = sentinel.offsetTop;
    const visibleBottom = root.scrollTop + root.clientHeight + 200;
    if (sentinelTop <= visibleBottom) {
      loadMoreRef.current?.();
    }
  }, [cbLoading, cbHasMore, activeTab, cbSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver — clothing
  cbClothingLoadMoreRef.current = () => {
    if (cbClothingHasMore && !cbClothingLoading) {
      loadClothingCatalogBrowser({ page: cbClothingPage + 1, append: true });
    }
  };
  useEffect(() => {
    if (activeTab !== 'queue' || cbSection !== 'clothing' || cbClothingStep !== 'products') return;
    const el   = cbClothingSentinelRef.current;
    const root = cbClothingProductsRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) cbClothingLoadMoreRef.current?.(); },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, cbSection, cbClothingStep, cbClothingPage, cbClothingHasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Автозагрузка clothing при первом переключении на раздел
  useEffect(() => {
    if (cbSection === 'clothing' && activeTab === 'queue') {
      if (cbClothingProducts.length === 0 && !cbClothingLoading) {
        loadClothingCatalogBrowser({ page: 0 });
      }
      if (cbClothingNav.types.length === 0) {
        loadClothingNav();
      }
      // Загружаем конфиг добавления одежды если ещё не загружен
      if (!clothingAddConfig && !clothingAddConfigLoading) {
        setClothingAddConfigLoading(true);
        setClothingAddError('');
        adminItemsAPI.getClothingItemConfig()
          .then(res => { setClothingAddConfig(res.data); })
          .catch(() => setClothingAddError('Не удалось загрузить конфиг. Нажмите "Попробовать снова".'))
          .finally(() => setClothingAddConfigLoading(false));
      }
    }
  }, [cbSection, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Всегда актуальная функция навигации (используется в keydown без re-subscribe)
  navigateCompareRef.current = (dir) => {
    const list = compareSection === 'clothing' ? cbClothingProducts : cbProducts;
    const hasMore = compareSection === 'clothing' ? cbClothingHasMore : cbHasMore;
    const loading = compareSection === 'clothing' ? cbClothingLoading : cbLoading;
    setCompareIndex(prev => {
      let base = prev;
      if (base === -1) {
        base = list.findIndex(x => x.id === compareProduct?.id);
        if (base === -1) return prev;
      }
      const next = base + dir;
      if (next < 0 || next >= list.length) return base;
      setCompareProduct(list[next]);
      if (next >= list.length - 5 && hasMore && !loading) {
        if (compareSection === 'clothing') cbClothingLoadMoreRef.current?.();
        else loadMoreRef.current?.();
      }
      return next;
    });
  };

  // Одиночный клик — показать сравнение; двойной — выбрать товар
  const handleCardClick = useCallback((p) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      selectCatalogRef.current?.(p);
      setCompareProduct(null);
    } else {
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        const idx = cbProducts.findIndex(x => x.id === p.id);
        setCompareSection('shoes');
        setCompareIndex(idx);
        setCompareProduct(p);
      }, 240);
    }
  }, [cbProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClothingCardClick = useCallback((p) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      selectCatalogRef.current?.(p);
      setCompareProduct(null);
    } else {
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        const idx = cbClothingProducts.findIndex(x => x.id === p.id);
        setCompareSection('clothing');
        setCompareIndex(idx);
        setCompareProduct(p);
      }, 240);
    }
  }, [cbClothingProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setPhotoUploading(true);
    setQueueFormError('');
    try {
      const res = await adminItemsAPI.uploadPhoto(file);
      if (addModelMode) {
        setAddModelForm(f => ({ ...f, photo_url: res.data.url }));
      } else {
        setQueueForm(f => ({ ...f, photo_url: res.data.url }));
      }
    } catch (e) {
      setQueueFormError('Ошибка загрузки фото: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPhotoUploading(false);
    }
  };

  const _uploadCatalogFile = async (file) => {
    setCatalogPhotoUploading(true);
    setQueueFormError('');
    try {
      const res = await adminItemsAPI.uploadPhoto(file);
      setQueueForm(f => ({ ...f, catalog_photo_url: res.data.url }));
    } catch (e) {
      setQueueFormError('Ошибка загрузки фото каталога: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCatalogPhotoUploading(false);
    }
  };

  const handleCatalogPhotoUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = img.width / img.height;
      if (Math.abs(ratio - CATALOG_RATIO) > RATIO_TOLERANCE) {
        setCropFile(file); // соотношение не совпадает — открываем кроппер
      } else {
        _uploadCatalogFile(file); // соотношение ок — грузим напрямую
      }
    };
    img.src = url;
  };

  const _uploadAddModelFile = async (file) => {
    setPhotoUploading(true);
    setAddModelError('');
    try {
      const res = await adminItemsAPI.uploadPhoto(file);
      setAddModelForm(f => ({ ...f, photo_url: res.data.url }));
    } catch (e) {
      setAddModelError('Ошибка загрузки фото: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleAddModelPhotoUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = img.width / img.height;
      if (Math.abs(ratio - CATALOG_RATIO) > RATIO_TOLERANCE) {
        setAddModelCropFile(file);
      } else {
        _uploadAddModelFile(file);
      }
    };
    img.src = url;
  };

  // ── Поворот фото ──────────────────────────────────────────────────────────

  const _OUR_ORIGIN = 'https://alldrop.ink/images';

  const isOurImage = (url) => typeof url === 'string' && url.startsWith(_OUR_ORIGIN);

  const bustUrl = (url) => {
    if (!url) return url;
    const base = url.split('?')[0];
    return `${base}?t=${Date.now()}`;
  };

  const handleRotate = async (url, degrees) => {
    if (!url || rotatingUrl) return;
    setRotatingUrl(url);
    try {
      await adminItemsAPI.rotatePhoto(url.split('?')[0], degrees);
      const base = url.split('?')[0];
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (e) {
      alert(`Ошибка поворота: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  const handleRevert = async (url) => {
    if (!url || rotatingUrl) return;
    setRotatingUrl(url);
    try {
      await adminItemsAPI.revertPhoto(url.split('?')[0]);
      const base = url.split('?')[0];
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (e) {
      alert(`Ошибка восстановления: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  const withBust = (url) => {
    if (!url) return url;
    const base = url.split('?')[0];
    const ts = photoTimestamps[base];
    return ts ? `${base}?t=${ts}` : url;
  };

  // ── Jobs API ──────────────────────────────────────────────────────────────

  const loadJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await jobsAPI.list();
      const list = res.data?.jobs || [];
      const blocking = res.data?.blocking_job;
      // blocking_job — задача, яка зараз зупиняє всю чергу (running/failed),
      // окремо від списку (обмеженого 100 найновішими) — без цього стара
      // зависла задача, за якою встигло накопичитись 100+ нових, фізично не
      // видна в списку і до неї не дістатись кнопкою "Повторить".
      const withBlocking = (blocking && !list.some(j => j.id === blocking.id))
        ? [blocking, ...list]
        : list;
      setJobs(withBlocking);
    } catch (_) {}
    finally { setJobsLoading(false); }
  };

  const handleRetryJob = async (jobId) => {
    try { await jobsAPI.retry(jobId); loadJobs(); } catch (_) {}
  };

  const handleCancelJob = async (jobId) => {
    try { await jobsAPI.cancel(jobId); loadJobs(); } catch (_) {}
  };

  // Добавить товар поставщика в фоне
  const handleEnqueueSupplierItem = async () => {
    setQueueFormError('');
    if (!queueForm.catalog_id) { setQueueFormError('Укажите артикул товара из каталога'); return; }
    // Відкриваємо вкладку СИНХРОННО, прямо тут (ще до жодного await) — щоб
    // браузер не сприйняв її як попап і не заблокував.
    const photoSearchWin = queueForm.photo_url ? window.open('', '_blank') : null;
    setQueueFormSaving(true);
    try {
      const res = await jobsAPI.enqueueSupplierItem({
        supplier: selectedSupplier.supplier,
        supplier_article: selectedArticle,
        catalog_id: queueForm.catalog_id,
        photo_link: queueForm.photo_link,
        photo_url: queueForm.photo_url,
        keywords: queueForm.keywords,
        is_new: queueForm.is_new,
      });
      syncPhotoSearch({
        sku: queueForm.catalog_id,
        supplierPhotoUrl: queueForm.photo_url,
        collection: cbSection === 'clothing' ? 'clothing' : 'shoes',
        targetWindow: photoSearchWin,
      });
      _afterEnqueue(res.data.job_id);
    } catch (e) {
      if (photoSearchWin && !photoSearchWin.closed) photoSearchWin.close();
      setQueueFormError(
        e?.response?.data?.detail
          || (!e?.response ? 'Ошибка сети — проверьте соединение' : `Ошибка сервера (${e.response.status})`)
      );
    } finally { setQueueFormSaving(false); }
  };

  // Добавить НОВЫЙ товар в каталог в фоне
  const handleEnqueueNewCatalogItem = async () => {
    if (enqueueGuardRef.current) return; // защита от двойного клика
    setQueueFormError('');
    if (!newItemForm.after_id)     { setQueueFormError('Укажите артикул, после которого вставить'); return; }
    if (!newItemForm.product_name) { setQueueFormError('Укажите название товара'); return; }
    if (!newItemForm.season)       { setQueueFormError('Укажите сезон'); return; }
    if (!queueForm.photo_url)      { setQueueFormError('Фото поставщика обязательно'); return; }
    if (!queueForm.catalog_photo_url) { setQueueFormError('Фото каталога (4:3) обязательно для нового товара'); return; }
    // Артикул для НОВОГО товару генерує сервер, але наступний вільний вже
    // показаний в формі (newItemConfig.next_article) — саме його й
    // присвоять, тож можна скористатись одразу, не чекаючи завершення задачі.
    const predictedSku = newItemConfig?.next_article || '';
    const photoSearchWin = (queueForm.photo_url && predictedSku) ? window.open('', '_blank') : null;
    enqueueGuardRef.current = true;
    setQueueFormSaving(true);
    try {
      const res = await jobsAPI.enqueueNewCatalogItem({
        supplier: selectedSupplier.supplier,
        supplier_article: selectedArticle,
        photo_link: queueForm.photo_link,
        photo_url: queueForm.photo_url,
        catalog_photo_url: queueForm.catalog_photo_url,
        after_id: newItemForm.after_id,
        product_name: newItemForm.product_name,
        season: newItemForm.season,
        color: newItemForm.color,
        new_list_name: newItemForm.new_list_name,
        brand: newItemForm.brand,
        keywords: queueForm.keywords,
      });
      pollJobThenAutoIndex(res.data.job_id, {
        photoUrl: queueForm.catalog_photo_url,
        name: newItemForm.product_name,
        brand: newItemForm.brand,
      });
      syncPhotoSearch({
        sku: predictedSku,
        supplierPhotoUrl: queueForm.photo_url,
        collection: 'shoes',
        targetWindow: photoSearchWin,
      });
      _afterEnqueue(res.data.job_id);
    } catch (e) {
      if (photoSearchWin && !photoSearchWin.closed) photoSearchWin.close();
      setQueueFormError(e?.response?.data?.detail || 'Ошибка');
    } finally {
      enqueueGuardRef.current = false;
      setQueueFormSaving(false);
    }
  };

  // Добавить новую модель в каталог без поставщика (фоновая задача)
  const handleAddCatalogModel = async () => {
    setAddModelError('');
    setAddModelDone('');
    if (!addModelForm.after_id)     { setAddModelError('Укажите артикул, после которого вставить'); return; }
    if (!addModelForm.product_name) { setAddModelError('Укажите название товара'); return; }
    if (!addModelForm.season)       { setAddModelError('Укажите сезон'); return; }
    setAddModelSaving(true);
    try {
      const res = await jobsAPI.enqueueCatalogModel(addModelForm);
      const nextArt = newItemConfig?.next_article || '';
      pollJobThenAutoIndex(res.data.job_id, {
        photoUrl: addModelForm.photo_url,
        name: addModelForm.product_name,
        brand: addModelForm.brand,
      });
      setAddModelDone(`Задача #${res.data.job_id} в очереди${nextArt ? ` — артикул ${nextArt}` : ''}`);
      setAddModelForm({ after_id: '', product_name: '', season: '', color: '', photo_url: '', new_list_name: '', keywords: '', brand: '' });
      setAddModelSelectedProduct(null);
      loadJobs();
    } catch (e) {
      setAddModelError(e?.response?.data?.detail || 'Ошибка');
    } finally {
      setAddModelSaving(false);
    }
  };

  const _afterEnqueue = (jobId) => {
    setQueueFormDone(`Задача #${jobId} в очереди`);
    loadJobs();
    // Находим следующий артикул ДО того как _afterSuccess почистит список
    const currentList = selectedSupplier?.all_articles || [];
    const currentIdx = currentList.indexOf(selectedArticle);
    const nextArticle = currentIdx >= 0 ? currentList[currentIdx + 1] : null;
    _afterSuccess(null);
    if (nextArticle) handleSelectArticle(nextArticle);
  };

  const _afterSuccess = (catalogId) => {
    if (catalogId) setQueueFormDone(`Добавлено: ${selectedArticle} → ${catalogId}`);
    setSelectedSupplier(prev => ({
      ...prev,
      all_articles: prev.all_articles.filter(a => a !== selectedArticle),
      count: prev.count - 1,
    }));
    setQueue(prev => prev.map(s =>
      s.supplier === selectedSupplier.supplier
        ? { ...s, count: s.count - 1, all_articles: s.all_articles.filter(a => a !== selectedArticle) }
        : s
    ).filter(s => s.count > 0));
    setSelectedArticle('');
    setQueueForm({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
    setNewItemForm({ after_id: '', product_name: '', season: '', color: '', new_list_name: '', brand: '' });
    setArticleDetails(null);
    setSelectedCatalogProduct(null);
  };

  const handleQueueSubmit = async () => {
    if (enqueueGuardRef.current) return;
    setQueueFormError('');
    setQueueFormDone('');
    if (!queueForm.catalog_id) { setQueueFormError('Укажите артикул товара из каталога'); return; }
    enqueueGuardRef.current = true;
    setQueueFormSaving(true);
    try {
      await adminItemsAPI.addItem(
        selectedSupplier.supplier,
        selectedArticle,
        queueForm.catalog_id,
        queueForm.photo_link,
        queueForm.photo_url,
        queueForm.keywords,
        queueForm.is_new,
      );
      _afterSuccess(queueForm.catalog_id);
    } catch (e) {
      setQueueFormError(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      enqueueGuardRef.current = false;
      setQueueFormSaving(false);
    }
  };

  const handleNewItemSubmit = async () => {
    setQueueFormError('');
    setQueueFormDone('');
    if (!newItemForm.after_id)    { setQueueFormError('Укажите артикул, после которого вставить'); return; }
    if (!newItemForm.product_name) { setQueueFormError('Укажите название товара'); return; }
    if (!newItemForm.season)       { setQueueFormError('Укажите сезон'); return; }
    if (!queueForm.photo_url)      { setQueueFormError('Фото поставщика обязательно'); return; }
    setQueueFormSaving(true);
    try {
      const res = await adminItemsAPI.addNewCatalogItem({
        supplier: selectedSupplier.supplier,
        supplier_article: selectedArticle,
        photo_link: queueForm.photo_link,
        photo_url: queueForm.photo_url,
        catalog_photo_url: queueForm.catalog_photo_url,
        after_id: newItemForm.after_id,
        product_name: newItemForm.product_name,
        season: newItemForm.season,
        color: newItemForm.color,
        new_list_name: newItemForm.new_list_name,
        brand: newItemForm.brand,
        keywords: queueForm.keywords,
      });
      setNewItemConfig(c => c ? { ...c, last_article: res.data.catalog_id, next_article: res.data.catalog_id } : null);
      _afterSuccess(res.data.catalog_id);
    } catch (e) {
      setQueueFormError(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setQueueFormSaving(false);
    }
  };

  // ── Sync handlers ─────────────────────────────────────────────────────────

  const handleSyncCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const res = await productsAPI.syncProducts();
      setCatalogStatus(prev => ({
        ...prev, ...res.data,
        last_sync: new Date().toISOString(), last_error: null, last_error_at: null,
      }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setCatalogStatus(prev => ({ ...prev, last_error: msg, last_error_at: new Date().toISOString() }));
    } finally { setSyncingCatalog(false); }
  };

  const handleSyncSuppliers = async () => {
    setSyncingSuppliers(true);
    try {
      const res = await productsAPI.syncSuppliers();
      setSuppliersStatus(prev => ({
        ...prev, ...res.data,
        last_sync: new Date().toISOString(), last_error: null, last_error_at: null,
      }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setSuppliersStatus(prev => ({ ...prev, last_error: msg, last_error_at: new Date().toISOString() }));
    } finally { setSyncingSuppliers(false); }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await productsAPI.syncAll();
      const now = new Date().toISOString();
      if (res.data.catalog) {
        const err = res.data.catalog.error;
        setCatalogStatus(prev => ({
          ...prev, ...res.data.catalog,
          last_sync: err ? prev?.last_sync : now,
          last_error: err || null, last_error_at: err ? now : null,
        }));
      }
      if (res.data.suppliers) {
        const err = res.data.suppliers.error;
        setSuppliersStatus(prev => ({
          ...prev, ...res.data.suppliers,
          last_sync: err ? prev?.last_sync : now,
          last_error: err || null, last_error_at: err ? now : null,
        }));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      const now = new Date().toISOString();
      setCatalogStatus(prev => ({ ...prev, last_error: msg, last_error_at: now }));
      setSuppliersStatus(prev => ({ ...prev, last_error: msg, last_error_at: now }));
    } finally { setSyncingAll(false); }
  };

  const handleSyncClothingCatalog = async () => {
    setSyncingClothingCatalog(true);
    try {
      const res = await clothingAPI.sync();
      setClothingCatalogStatus(prev => ({
        ...prev, ...res.data,
        last_sync: new Date().toISOString(), last_error: null, last_error_at: null,
      }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setClothingCatalogStatus(prev => ({ ...prev, last_error: msg, last_error_at: new Date().toISOString() }));
    } finally { setSyncingClothingCatalog(false); }
  };

  const handleSyncClothingSuppliers = async () => {
    setSyncingClothingSuppliers(true);
    try {
      const res = await clothingAPI.syncSuppliers();
      setClothingSuppliersStatus(prev => ({
        ...prev, ...res.data,
        last_sync: new Date().toISOString(), last_error: null, last_error_at: null,
      }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setClothingSuppliersStatus(prev => ({ ...prev, last_error: msg, last_error_at: new Date().toISOString() }));
    } finally { setSyncingClothingSuppliers(false); }
  };

  const handleSyncClothingModels = async () => {
    setSyncingClothingModels(true);
    try {
      const res = await clothingAPI.syncModels();
      setClothingModelsStatus(prev => ({
        ...prev, ...res.data,
        last_sync: new Date().toISOString(), last_error: null, last_error_at: null,
      }));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setClothingModelsStatus(prev => ({ ...prev, last_error: msg, last_error_at: new Date().toISOString() }));
    } finally { setSyncingClothingModels(false); }
  };

  const handleSyncClothingAll = async () => {
    setSyncingClothingAll(true);
    try {
      const res = await clothingAPI.syncAll();
      const now = new Date().toISOString();
      if (res.data.catalog) {
        const err = res.data.catalog.error;
        setClothingCatalogStatus(prev => ({
          ...prev, ...res.data.catalog,
          last_sync: err ? prev?.last_sync : now,
          last_error: err || null, last_error_at: err ? now : null,
        }));
      }
      if (res.data.suppliers) {
        const err = res.data.suppliers.error;
        setClothingSuppliersStatus(prev => ({
          ...prev, ...res.data.suppliers,
          last_sync: err ? prev?.last_sync : now,
          last_error: err || null, last_error_at: err ? now : null,
        }));
      }
      if (res.data.models) {
        const err = res.data.models.error;
        setClothingModelsStatus(prev => ({
          ...prev, ...res.data.models,
          last_sync: err ? prev?.last_sync : now,
          last_error: err || null, last_error_at: err ? now : null,
        }));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      const now = new Date().toISOString();
      setClothingCatalogStatus(prev => ({ ...prev, last_error: msg, last_error_at: now }));
      setClothingSuppliersStatus(prev => ({ ...prev, last_error: msg, last_error_at: now }));
      setClothingModelsStatus(prev => ({ ...prev, last_error: msg, last_error_at: now }));
    } finally { setSyncingClothingAll(false); }
  };

  const handleAddClothingItem = async (supplierPhotoUrl) => {
    if (enqueueGuardRef.current) return;
    setClothingAddError('');
    setClothingAddDone('');
    if (!clothingAddForm.name.trim()) { setClothingAddError('Укажите название товара'); return; }
    if (!clothingAddForm.other_colors_mode) { setClothingAddError('Выберите группу «Другие цвета»'); return; }
    if (clothingAddForm.other_colors_mode === 'existing' && !clothingAddForm.other_colors_id.trim()) {
      setClothingAddError('Укажите ID существующей группы (например OC03434)'); return;
    }
    if (!selectedSupplier || !selectedArticle) {
      setClothingAddError('Выберите поставщика и артикул из очереди');
      return;
    }

    const photoUrl = (supplierPhotoUrl || clothingAddForm.photo_url || '').trim();
    // Відкриваємо вкладку СИНХРОННО, ще до будь-якого await (інакше браузер
    // блокує window.open як попап) — поки конфіг (next_sm_id) ще не
    // відомий, тому результат пошуку підв'яжемо до неї пізніше.
    const photoSearchWin = photoUrl ? window.open('', '_blank') : null;

    setClothingAddSaving(true);

    // Загружаем конфиг если ещё не загружен
    let config = clothingAddConfig;
    if (!config) {
      try {
        const cfgRes = await adminItemsAPI.getClothingItemConfig();
        config = cfgRes.data;
        setClothingAddConfig(config);
      } catch (e) {
        if (photoSearchWin && !photoSearchWin.closed) photoSearchWin.close();
        setClothingAddError(e?.response?.data?.detail || 'Не удалось загрузить конфиг. Попробуйте ещё раз.');
        setClothingAddSaving(false);
        return;
      }
    }

    const mode = clothingAddForm.other_colors_mode;
    const new_oc = mode === 'new';
    const other_colors_id = mode === 'existing' ? clothingAddForm.other_colors_id.trim() : '';

    enqueueGuardRef.current = true;
    try {
      const res = await jobsAPI.enqueueClothingItem({
        supplier: selectedSupplier.supplier,
        supplier_article: selectedArticle,
        sm_id: config.next_sm_id,
        name: clothingAddForm.name.trim(),
        photo_url: photoUrl,
        photo_link: queueForm.photo_link || '',
        color: clothingAddForm.color,
        brand: clothingAddForm.brand.trim(),
        product_type: clothingAddForm.product_type.trim(),
        gender: clothingAddForm.gender,
        description: clothingAddForm.description.trim(),
        material: clothingAddForm.material.trim(),
        measurements: clothingAddForm.measurements.trim(),
        season: clothingAddForm.season,
        other_colors_id,
        new_oc,
        new_oc_id: new_oc ? config.next_oc_id : '',
        oc_measurements: new_oc ? clothingAddForm.oc_measurements.trim() : '',
        new_brand: clothingAddForm.new_brand,
        new_type: clothingAddForm.new_type,
        new_color: clothingAddForm.new_color,
        brand_logo_url: clothingAddForm.brand_logo_url || '',
        type_logo_url: clothingAddForm.type_logo_url || '',
        keywords: queueForm.keywords || '',
      });
      pollJobThenAutoIndex(res.data.job_id, {
        photoUrl: photoUrl,
        name: clothingAddForm.name.trim(),
        brand: clothingAddForm.brand.trim(),
        collection: 'clothing',
      });
      syncPhotoSearch({
        sku: config.next_sm_id,
        supplierPhotoUrl: photoUrl,
        collection: 'clothing',
        targetWindow: photoSearchWin,
      });
      // Конфиг сбрасываем — следующий артикул уже другой
      setClothingAddConfig(null);
      setClothingAddForm({
        name: '', photo_url: '', color: '', brand: '', product_type: '',
        gender: '', description: '', material: '', measurements: '', season: '',
        other_colors_mode: '', other_colors_id: '', oc_measurements: '',
        new_brand: false, new_type: false, new_color: false,
      });
      _afterEnqueue(res.data.job_id);
    } catch (e) {
      if (photoSearchWin && !photoSearchWin.closed) photoSearchWin.close();
      setClothingAddError(e?.response?.data?.detail || 'Ошибка постановки задачи');
    } finally {
      enqueueGuardRef.current = false;
      setClothingAddSaving(false);
    }
  };

  // ── User handlers ─────────────────────────────────────────────────────────

  const handleApprove = async (id) => {
    setBusy(id);
    await authAPI.approveUser(id).catch(console.error);
    await loadUsers();
    setBusy(null);
  };

  const [confirmReject, setConfirmReject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmTeam,   setConfirmTeam]   = useState(null); // { id, action: 'add'|'remove' }

  // Редактор прав
  const [permEditorOpen, setPermEditorOpen] = useState(new Set());
  const [permData,       setPermData]       = useState({});  // draft
  const [permOrigData,   setPermOrigData]   = useState({});  // last saved
  const [permLoading,    setPermLoading]    = useState(new Set());
  const [permSaving,     setPermSaving]     = useState(new Set());
  const [permList,       setPermList]       = useState([]);

  // Доступ до сайтів
  const [retailSitesList,    setRetailSitesList]    = useState(null);
  const [retailSiteGroups,   setRetailSiteGroups]   = useState(null); // [{id,name,site_ids}]
  const [siteAccessData,     setSiteAccessData]     = useState({});  // draft { [userId]: siteIds[] }
  const [siteAccessOrigData, setSiteAccessOrigData] = useState({});  // last saved
  const [siteAccessSaving,   setSiteAccessSaving]   = useState(new Set());

  const handleDelete = async (id) => {
    setConfirmDelete(null);
    setBusy(id);
    await authAPI.deleteUser(id).catch(console.error);
    await loadUsers();
    setBusy(null);
  };

  const handleReject = async (id) => {
    setConfirmReject(null);
    setBusy(id);
    await authAPI.rejectUser(id).catch(console.error);
    await loadUsers();
    setBusy(null);
  };

  const handleToggleTeam = async (id, currentIsTeam) => {
    setConfirmTeam(null);
    setBusy(id);
    await authAPI.updateUser(id, { is_team: !currentIsTeam }).catch(console.error);
    await loadUsers();
    setBusy(null);
  };

  // ── Impersonation ─────────────────────────────────────────────────────────

  const refreshCurrentUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch {}
  };

  const handleStartImpersonate = async (email) => {
    try {
      await authAPI.startImpersonate(email);
      await refreshCurrentUser();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка: користувача не знайдено');
    }
  };

  // ── Права ────────────────────────────────────────────────────────────────

  const handleTogglePermEditor = async (userId) => {
    const opening = !permEditorOpen.has(userId);
    setPermEditorOpen(prev => {
      const next = new Set(prev);
      opening ? next.add(userId) : next.delete(userId);
      return next;
    });
    if (!opening) return;

    const tasks = [];

    // Права
    if (permData[userId] === undefined) {
      setPermLoading(prev => new Set([...prev, userId]));
      tasks.push(
        authAPI.getUserPermissions(userId)
          .then(res => {
            const perms = res.data.permissions || [];
            setPermData(prev => ({ ...prev, [userId]: perms }));
            setPermOrigData(prev => ({ ...prev, [userId]: perms }));
          })
          .catch(() => {
            setPermData(prev => ({ ...prev, [userId]: [] }));
            setPermOrigData(prev => ({ ...prev, [userId]: [] }));
          })
          .finally(() => setPermLoading(prev => { const n = new Set(prev); n.delete(userId); return n; }))
      );
    }

    // Список всіх сайтів і груп (один раз)
    if (retailSitesList === null) {
      tasks.push(
        retailAPI.getSites()
          .then(res => setRetailSitesList((res.data || []).map(s => ({ id: s.id, name: s.name }))))
          .catch(() => setRetailSitesList([]))
      );
    }
    if (retailSiteGroups === null) {
      tasks.push(
        retailAPI.getSiteGroups()
          .then(res => setRetailSiteGroups(res.data || []))
          .catch(() => setRetailSiteGroups([]))
      );
    }

    // Доступ цього користувача до сайтів
    if (siteAccessData[userId] === undefined) {
      tasks.push(
        retailAPI.getUserSiteAccess(userId)
          .then(res => {
            const ids = res.data.site_ids || [];
            setSiteAccessData(prev => ({ ...prev, [userId]: ids }));
            setSiteAccessOrigData(prev => ({ ...prev, [userId]: ids }));
          })
          .catch(() => {
            setSiteAccessData(prev => ({ ...prev, [userId]: [] }));
            setSiteAccessOrigData(prev => ({ ...prev, [userId]: [] }));
          })
      );
    }

    await Promise.all(tasks);
  };

  // Toggle без збереження (draft)
  const handleTogglePerm = (userId, perm) => {
    const current = permData[userId] || [];
    const next = current.includes(perm)
      ? current.filter(p => p !== perm)
      : [...current, perm];
    setPermData(prev => ({ ...prev, [userId]: next }));
  };

  const handleSavePerms = async (userId) => {
    setPermSaving(prev => new Set([...prev, userId]));
    const next = permData[userId] || [];
    await authAPI.setUserPermissions(userId, next).catch(console.error);
    setPermOrigData(prev => ({ ...prev, [userId]: next }));
    setPermSaving(prev => { const n = new Set(prev); n.delete(userId); return n; });
  };

  const handleCancelPerms = (userId) => {
    setPermData(prev => ({ ...prev, [userId]: permOrigData[userId] || [] }));
  };

  // Toggle site access без збереження (draft)
  const handleToggleSiteAccess = (userId, siteId) => {
    const current = siteAccessData[userId] || [];
    const next = current.includes(siteId)
      ? current.filter(id => id !== siteId)
      : [...current, siteId];
    setSiteAccessData(prev => ({ ...prev, [userId]: next }));
  };

  const handleSaveSiteAccess = async (userId) => {
    setSiteAccessSaving(prev => new Set([...prev, userId]));
    const next = siteAccessData[userId] || [];
    await retailAPI.setUserSiteAccess(userId, next).catch(console.error);
    setSiteAccessOrigData(prev => ({ ...prev, [userId]: next }));
    setSiteAccessSaving(prev => { const n = new Set(prev); n.delete(userId); return n; });
  };

  const handleCancelSiteAccess = (userId) => {
    setSiteAccessData(prev => ({ ...prev, [userId]: siteAccessOrigData[userId] || [] }));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteStatus(null);
    try {
      const r = await authAPI.inviteUser(inviteEmail.trim());
      setInviteStatus('ok');
      setInviteEmail('');
      await loadUsers();
      const newUser = r.data?.user;
      if (newUser) {
        // Новий користувач ще без групи — переключаємось на "Всі", щоб він
        // точно потрапив у видиму частину списку, і одразу відкриваємо йому
        // панель прав замість того, щоб змушувати шукати його в списку.
        selectUserGroup(null);
        setSearch('');
        setTimeout(() => {
          handleTogglePermEditor(newUser.id);
          document.getElementById(`user-row-${newUser.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      }
    } catch { setInviteStatus('error'); }
    finally { setInviting(false); }
  };

  // ── Group handlers ────────────────────────────────────────────────────────

  function selectUsersSubTab(tab) {
    setUsersSubTab(tab);
    localStorage.setItem('admin_users_subtab', tab);
  }

  function selectUserGroup(id) {
    setActiveUserGroupId(id);
    if (id === null) localStorage.removeItem('admin_active_user_group_id');
    else             localStorage.setItem('admin_active_user_group_id', String(id));
  }

  const handleDeleteGroup = async (id) => {
    await groupsAPI.delete(id).catch(console.error);
    if (activeUserGroupId === id) selectUserGroup(null);
    await reloadUsersAndGroups();
  };

  const handleSetUserGroup = async (userId, groupId) => {
    if (groupId === '') {
      await authAPI.updateUser(userId, { clear_group: true }).catch(console.error);
    } else {
      await authAPI.updateUser(userId, { group_id: parseInt(groupId) }).catch(console.error);
    }
    await reloadUsersAndGroups();
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const q = search.toLowerCase();
  const filtered = q
    ? users.filter(u =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q)
      )
    : users;

  const pending  = filtered.filter(u => !u.is_active && !u.is_admin && !u.is_rejected);
  const active   = filtered.filter(u => u.is_active && !u.is_admin && !u.is_team);
  const team     = filtered.filter(u => u.is_active && !u.is_admin && u.is_team);
  const admins   = filtered.filter(u => u.is_admin);
  const rejected = filtered.filter(u => !u.is_active && !u.is_admin && u.is_rejected);

  const fmtDate = iso => formatKyivDate(iso);

  const isSyncing = syncingAll || syncingCatalog || syncingSuppliers;

  const SUB_OPTIONS = [
    { value: '',                  label: '— Без каталогу —' },
    { value: 'Обувь',             label: 'Обувь' },
    { value: 'Одежда',            label: 'Одежда' },
    { value: 'Обувь и Одежда',    label: 'Обувь и Одежда' },
  ];

  // ── UserRow ───────────────────────────────────────────────────────────────

  const UserRow = ({ u, action, showGroupSelect = false }) => {
    const deviceCount = online.per_user[u.id] || 0;
    const isOnline = deviceCount > 0;
    const [tgVal,  setTgVal]  = useState(u.tg_id || '');
    const [subVal, setSubVal] = useState(u.subscription_type || '');
    const [saving, setSaving] = useState(false);

    // Синхронізуємо локальний стейт якщо проп змінився ззовні
    useEffect(() => { setSubVal(u.subscription_type || ''); }, [u.subscription_type]);

    const saveTg = async () => {
      const next = tgVal.trim();
      if (next === (u.tg_id || '')) return;
      setSaving(true);
      await authAPI.updateUser(u.id, { tg_id: next }).catch(console.error);
      await loadUsers();
      setSaving(false);
    };

    const saveSub = async (val) => {
      setSubVal(val); // одразу оновлюємо UI
      // val='' → бекенд отримає '' → '' or None → зберігається null (очищення підписки)
      await authAPI.updateUser(u.id, { subscription_type: val }).catch(console.error);
      await loadUsers();
    };

    return (
      <div className="admin-row" id={`user-row-${u.id}`}>
        {/* ── Верхняя строка: аватар + имя/email + дата + кнопки ── */}
        <div className="admin-row-header">
          <div className="admin-user-info">
            <div className="admin-avatar-wrap">
              <div className={`admin-avatar ${u.is_active ? 'admin-avatar-active' : u.is_rejected ? 'admin-avatar-rejected' : ''}`}>
                {(u.full_name || u.email).charAt(0).toUpperCase()}
              </div>
              {isOnline && <span className="online-dot" title={`${deviceCount} устр.`} />}
            </div>
            <div className="admin-user-body">
              <div className="admin-user-name">
                {u.full_name || '—'}
                {isOnline && <span className="online-badge">{deviceCount} устр.</span>}
                {u.is_team && !u.is_admin && <span className="admin-team-badge">👥 Команда</span>}
              </div>
              <div className="admin-user-email">{u.email}</div>
            </div>
          </div>
          <div className="admin-row-right">
            <div className="admin-user-date">{fmtDate(u.created_at)}</div>
            <div className="admin-actions">{action}</div>
          </div>
        </div>

        {/* ── Нижняя строка: TG, подписка, группа ── */}
        <div className="admin-row-meta">
          <span className="admin-meta-label">TG</span>
          <input
            className="admin-meta-input"
            placeholder="ID или @username"
            value={tgVal}
            onChange={e => setTgVal(e.target.value)}
            onBlur={saveTg}
            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            disabled={saving}
            type="text"
            name={`tg-id-${u.id}`}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore
          />
          <div className="admin-meta-divider" />
          <select
            className="admin-meta-select"
            value={subVal}
            onChange={e => saveSub(e.target.value)}
          >
            {SUB_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {showGroupSelect && (
            <>
              <div className="admin-meta-divider" />
              <select
                className="admin-meta-select"
                value={u.group_id || ''}
                onChange={e => handleSetUserGroup(u.id, e.target.value)}
              >
                <option value="">Без группы</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* ── Права + Від імені (тільки для реального адміна) ── */}
        {isRealAdmin && !u.is_admin && (
          <div className="admin-row-perm-section">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className={`admin-btn-team${permEditorOpen.has(u.id) ? ' admin-btn-team--active' : ''}`}
                onClick={() => handleTogglePermEditor(u.id)}
                style={{ fontSize: 12 }}
              >
                Права {permEditorOpen.has(u.id) ? '▲' : '▼'}
              </button>
              <button
                className="admin-btn-team"
                onClick={() => handleStartImpersonate(u.email)}
                style={{ fontSize: 12 }}
                title={`Переглянути як ${u.email}`}
              >
                Від імені
              </button>
            </div>

            {permEditorOpen.has(u.id) && (() => {
              const curPerms  = permData[u.id] || [];
              const origPerms = permOrigData[u.id] || [];
              const permsDirty = JSON.stringify([...curPerms].sort()) !== JSON.stringify([...origPerms].sort());

              const curSites  = siteAccessData[u.id] || [];
              const origSites = siteAccessOrigData[u.id] || [];
              const sitesDirty = JSON.stringify([...curSites].sort()) !== JSON.stringify([...origSites].sort());

              const anyDirty = permsDirty || sitesDirty;

              return (
                <div style={{
                  marginTop: 8, padding: '10px 12px',
                  background: 'var(--bg-secondary,rgba(0,0,0,.04))',
                  borderRadius: 8, fontSize: 13,
                }}>
                  {/* Права */}
                  {permLoading.has(u.id) ? (
                    <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Завантаження...</span>
                  ) : permList.length === 0 ? (
                    <span style={{ color: 'var(--text-secondary)' }}>Немає доступних прав</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 8 }}>
                      {permList.map(({ key, label }) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={curPerms.includes(key)}
                            onChange={() => handleTogglePerm(u.id, key)}
                            style={{ width: 15, height: 15, cursor: 'pointer' }}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Доступ до конкретних сайтів (якщо є право retail_sites) */}
                  {curPerms.includes('retail_sites') && (
                    <div style={{ borderTop: '1px solid var(--border-color,#e0e0e0)', paddingTop: 8, marginTop: 4 }}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        Доступні сайти
                      </div>
                      {(retailSitesList === null || retailSiteGroups === null) ? (
                        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Завантаження...</span>
                      ) : retailSitesList.length === 0 ? (
                        <span style={{ color: 'var(--text-secondary)' }}>Немає сайтів</span>
                      ) : (() => {
                        // Будуємо групи для відображення
                        const siteById = Object.fromEntries(retailSitesList.map(s => [s.id, s]));
                        // Сайти, що входять хоч в одну групу
                        const groupedSiteIds = new Set(retailSiteGroups.flatMap(g => g.site_ids || []));
                        // Сайти без групи
                        const ungrouped = retailSitesList.filter(s => !groupedSiteIds.has(s.id));

                        const SiteCheckbox = ({ site }) => (
                          <label key={site.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={curSites.includes(site.id)}
                              onChange={() => handleToggleSiteAccess(u.id, site.id)}
                              style={{ width: 14, height: 14, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: 13 }}>{site.name}</span>
                          </label>
                        );

                        return (
                          <>
                            {/* Групи */}
                            {retailSiteGroups.map(grp => {
                              const grpSites = (grp.site_ids || []).map(id => siteById[id]).filter(Boolean);
                              if (!grpSites.length) return null;
                              const allChecked = grpSites.every(s => curSites.includes(s.id));
                              const someChecked = grpSites.some(s => curSites.includes(s.id));
                              return (
                                <div key={grp.id} style={{ marginBottom: 8 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <input
                                      type="checkbox"
                                      checked={allChecked}
                                      ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                      onChange={() => {
                                        const ids = grpSites.map(s => s.id);
                                        setSiteAccessData(prev => {
                                          const cur = prev[u.id] || [];
                                          const next = allChecked
                                            ? cur.filter(id => !ids.includes(id))
                                            : [...new Set([...cur, ...ids])];
                                          return { ...prev, [u.id]: next };
                                        });
                                      }}
                                      style={{ width: 14, height: 14, cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                                      {grp.name}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', paddingLeft: 20 }}>
                                    {grpSites.map(site => <SiteCheckbox key={site.id} site={site} />)}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Без групи */}
                            {ungrouped.length > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                {retailSiteGroups.length > 0 && (
                                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 4 }}>
                                    Інші
                                  </div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                                  {ungrouped.map(site => <SiteCheckbox key={site.id} site={site} />)}
                                </div>
                              </div>
                            )}
                            <div style={{ color: curSites.length === 0 ? '#ef4444' : 'var(--text-secondary)', fontSize: 11, marginTop: 5 }}>
                              {curSites.length === 0 ? 'Немає доступу до жодного сайту' : `Доступ: ${curSites.length} сайт(ів)`}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Кнопки Зберегти / Скасувати */}
                  {!permLoading.has(u.id) && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-color,#e0e0e0)' }}>
                      <button
                        className="admin-btn-approve"
                        style={{ fontSize: 12, padding: '4px 14px', opacity: anyDirty ? 1 : 0.45 }}
                        disabled={!anyDirty || permSaving.has(u.id) || siteAccessSaving.has(u.id)}
                        onClick={async () => {
                          if (permsDirty) await handleSavePerms(u.id);
                          if (sitesDirty) await handleSaveSiteAccess(u.id);
                        }}
                      >
                        {(permSaving.has(u.id) || siteAccessSaving.has(u.id)) ? 'Збереження...' : 'Зберегти'}
                      </button>
                      <button
                        className="admin-btn-cancel-confirm"
                        style={{ fontSize: 12, padding: '4px 14px', opacity: anyDirty ? 1 : 0.45 }}
                        disabled={!anyDirty}
                        onClick={() => { handleCancelPerms(u.id); handleCancelSiteAccess(u.id); }}
                      >
                        Скасувати
                      </button>
                      {anyDirty && (
                        <span style={{ fontSize: 11, color: '#f59e0b', alignSelf: 'center' }}>● незбережені зміни</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // ── Дії для активного користувача в загальному списку ─────────────────────

  const activeUserRowActions = (u) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {confirmTeam?.id === u.id ? (
        <div className="admin-confirm-reject">
          <span className="admin-confirm-label">
            {confirmTeam.action === 'add' ? 'Добавить в команду?' : 'Убрать из команды?'}
          </span>
          <button className="admin-btn-team admin-btn-team--active" disabled={busy === u.id} onClick={() => handleToggleTeam(u.id, u.is_team)}>
            {busy === u.id ? '...' : 'Да'}
          </button>
          <button className="admin-btn-cancel-confirm" onClick={() => setConfirmTeam(null)}>Нет</button>
        </div>
      ) : (
        <button
          className={`admin-btn-team${u.is_team ? ' admin-btn-team--active' : ''}`}
          disabled={busy === u.id}
          onClick={() => setConfirmTeam({ id: u.id, action: u.is_team ? 'remove' : 'add' })}
          title={u.is_team ? 'Убрать доступ к аналитике' : 'Дать доступ к аналитике'}
        >
          {u.is_team ? '👥 Команда' : '+ Команда'}
        </button>
      )}
      {confirmReject === u.id ? (
        <div className="admin-confirm-reject">
          <span className="admin-confirm-label">Отозвать?</span>
          <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => handleReject(u.id)}>
            {busy === u.id ? '...' : 'Да'}
          </button>
          <button className="admin-btn-cancel-confirm" onClick={() => setConfirmReject(null)}>Нет</button>
        </div>
      ) : confirmDelete === u.id ? (
        <div className="admin-confirm-reject">
          <span className="admin-confirm-label">Удалить?</span>
          <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => handleDelete(u.id)}>Да</button>
          <button className="admin-btn-cancel-confirm" onClick={() => setConfirmDelete(null)}>Нет</button>
        </div>
      ) : (
        <>
          <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => setConfirmReject(u.id)}>Отозвать</button>
          <button className="admin-btn-delete" disabled={busy === u.id} onClick={() => setConfirmDelete(u.id)} title="Удалить">🗑</button>
        </>
      )}
    </div>
  );

  // ── UserGroupsModal ───────────────────────────────────────────────────────
  // Керування групами користувачів — так само як "⚙️ Групи" у вкладці "Сайти":
  // список груп + форма створення/редагування з чекбоксами учасників.

  const UserGroupsModal = ({ onClose }) => {
    const [editId,      setEditId]      = useState(-1); // -1=форма закрита, null=нова, number=редагування
    const [editName,    setEditName]    = useState('');
    const [editMembers, setEditMembers] = useState([]); // вибрані user_id
    const [saving,      setSaving]      = useState(false);
    const [delId,       setDelId]       = useState(null);
    const [error,       setError]       = useState('');

    const assignable = users.filter(u => u.is_active && !u.is_admin && !u.is_team);
    const formOpen = editId !== -1;

    function openNew() {
      setEditId(null);
      setEditName('');
      setEditMembers([]);
      setError('');
    }
    function openEdit(g) {
      setEditId(g.id);
      setEditName(g.name);
      setEditMembers(g.member_ids || []);
      setError('');
    }
    function toggleMember(id) {
      setEditMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }

    async function saveGroup() {
      const n = editName.trim();
      if (!n) { setError('Укажите название группы'); return; }
      setSaving(true); setError('');
      try {
        let groupId = editId;
        if (editId === null) {
          const created = await groupsAPI.create(n);
          groupId = created.id;
        } else {
          const cur = groups.find(g => g.id === editId);
          if (cur && n !== cur.name) await groupsAPI.rename(editId, n);
        }
        const before = editId === null ? [] : (groups.find(g => g.id === editId)?.member_ids || []);
        const toAdd    = editMembers.filter(id => !before.includes(id));
        const toRemove = before.filter(id => !editMembers.includes(id));
        for (const uid of toAdd)    await handleSetUserGroup(uid, groupId);
        for (const uid of toRemove) await handleSetUserGroup(uid, '');
        await reloadUsersAndGroups();
        setEditId(-1);
      } catch { setError('Ошибка сохранения'); }
      finally { setSaving(false); }
    }

    async function doDelete() {
      await handleDeleteGroup(delId);
      setDelId(null);
    }

    return (
      <div className="rt-overlay" onClick={onClose}>
        <div className="rt-modal" style={{ maxWidth: 540, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
             onClick={e => e.stopPropagation()}>
          <div className="rt-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Группы пользователей</h3>
            <button className="rt-btn rt-btn-sm" onClick={onClose}>✕</button>
          </div>

          {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}

          <div style={{ flex: 1, overflowY: 'auto', marginBottom: 12 }}>
            {groups.length === 0 && !formOpen && (
              <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
                Групп ещё нет. Нажмите «+ Новая группа», чтобы создать.
              </div>
            )}
            {groups.map(g => (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border-color,#e0e0e0)' }}>
                {delId === g.id ? (
                  <>
                    <span style={{ flex: 1 }}>Удалить «{g.name}»?</span>
                    <button className="rt-btn rt-btn-sm" onClick={doDelete}>Да</button>
                    <button className="rt-btn rt-btn-sm" onClick={() => setDelId(null)}>Нет</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontWeight: 500 }}>{g.name}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{g.active_count ?? 0}/{g.total_count ?? 0} активных</span>
                    <button className="rt-btn rt-btn-sm" onClick={() => openEdit(g)}>✏️</button>
                    <button className="rt-btn rt-btn-sm" onClick={() => setDelId(g.id)}>🗑</button>
                  </>
                )}
              </div>
            ))}
          </div>

          {formOpen ? (
            <div style={{ borderTop: '1px solid var(--border-color,#e0e0e0)', paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {editId === null ? 'Новая группа' : 'Редактирование группы'}
              </div>
              <input
                className="rt-input"
                placeholder="Название группы…"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                style={{ width: '100%', marginBottom: 10 }}
                autoFocus
              />
              <div style={{ marginBottom: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                Участники группы:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, maxHeight: 180, overflowY: 'auto' }}>
                {(() => {
                  // Користувачі, що вже в ІНШИХ групах — недоступні для вибору тут
                  // (у користувача, як і в сайту, лише одна група одночасно)
                  const takenIds = new Set(
                    assignable.filter(u => u.group_id && u.group_id !== editId).map(u => u.id)
                  );
                  const available = assignable.filter(u => !takenIds.has(u.id));
                  if (!available.length) return (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Все пользователи уже распределены по группам</span>
                  );
                  return available.map(u => (
                    <label key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13,
                      padding: '3px 8px', border: '1px solid var(--border-color,#e0e0e0)', borderRadius: 6,
                    }}>
                      <input
                        type="checkbox"
                        checked={editMembers.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                        style={{ width: 13, height: 13, cursor: 'pointer' }}
                      />
                      {u.full_name || u.email}
                    </label>
                  ));
                })()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="rt-btn rt-btn-primary" disabled={saving || !editName.trim()} onClick={saveGroup}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button className="rt-btn rt-btn-sm" onClick={() => setEditId(-1)}>Отмена</button>
              </div>
            </div>
          ) : (
            <button className="rt-btn" onClick={openNew}>+ Новая группа</button>
          )}
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="admin-root">
      <header className="admin-header">
        <div className="admin-logo">
          <img src="/logo.ico" alt="ALLDROP" className="admin-logo-img" />
          <span>ALLDROP</span>
          <span className="admin-badge">Администратор</span>
        </div>
        <div className="admin-header-actions">
          {isRealAdmin && siteErrors.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                className="admin-btn-catalog"
                style={{ borderColor: 'var(--lg-danger, #ef4444)', color: 'var(--lg-danger, #ef4444)' }}
                onClick={() => setSiteErrorsOpen(v => !v)}
                title="Критичні помилки сайтів"
              >
                ⚠ {siteErrors.length}
              </button>
              {siteErrorsOpen && (
                <div style={{
                  position: 'absolute', top: '110%', right: 0, zIndex: 200,
                  width: 380, maxHeight: 420, overflowY: 'auto',
                  background: 'var(--lg-bg-deep, #1c1c1e)', border: '1px solid var(--lg-border, #333)',
                  borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,.4)', padding: 8,
                }}>
                  {siteErrors.map(e => (
                    <div
                      key={e.site_id}
                      style={{ padding: '8px 10px', borderRadius: 8, marginBottom: 4, cursor: 'pointer' }}
                      className="admin-site-error-item"
                      onClick={() => { setActiveTab('roznitsa'); setRetailOpenSiteId(e.site_id); setSiteErrorsOpen(false); }}
                      title="Відкрити цей сайт"
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 600 }}>
                        <span>{e.name}</span>
                        <span style={{ color: 'var(--lg-text-3)', fontWeight: 400 }}>{e.platform}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--lg-danger, #ef4444)', marginTop: 2 }}>{e.error}</div>
                      {e.error_at && (
                        <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginTop: 2 }}>
                          з {formatKyivDateTime(e.error_at)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {hasCatalogAccess && <button className="admin-btn-catalog" onClick={onGoToCatalog}>Каталог →</button>}
          {canSendPhotosToTg && (
            <button
              className="admin-btn-catalog"
              onClick={handleHeaderSendToTg}
              disabled={sendTgRunning}
              title="За один клик отправляет 50 фото из кеша в Telegram — как кнопка в «Фикс фото», но без захода туда"
            >
              {sendTgRunning
                ? `Отправка… ${sendTgResult?.sent ?? 0}/${sendTgResult?.total ?? '?'}`
                : 'Надіслати фото в Telegram'}
            </button>
          )}
          {onSettings && (
            <button className="admin-btn-catalog" onClick={onSettings} title="Настройки">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}>
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          )}
          <button className="admin-btn-logout" onClick={onLogout}>Выйти</button>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs-bar">
        {isRealAdmin && <>
          <button
            className={`admin-tab${activeTab === 'users' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Пользователи
            {pending.length > 0 && (
              <span className="admin-tab-badge">{pending.length}</span>
            )}
          </button>
          <button
            className={`admin-tab${activeTab === 'tables' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('tables')}
          >
            Таблицы
            {(catalogStatus?.last_error || suppliersStatus?.last_error) && (
              <span className="admin-tab-badge admin-tab-badge--error">!</span>
            )}
          </button>
        </>}
        {hasPerm('queue') && (
          <button
            className={`admin-tab${activeTab === 'queue' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            Добавить товары
            {queue.length > 0 && (
              <span className="admin-tab-badge">{queue.reduce((s, e) => s + e.count, 0)}</span>
            )}
          </button>
        )}
        {isRealAdmin && <>
          <button
            className={`admin-tab${activeTab === 'manage' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Управление товарами
            {staleCount > 0 && (
              <span className="admin-tab-badge admin-tab-badge--stale">{staleCount}</span>
            )}
          </button>
          <button
            className={`admin-tab${activeTab === 'jobs' ? ' admin-tab--active' : ''}`}
            onClick={() => { setActiveTab('jobs'); loadJobs(); }}
          >
            Задачи
            {jobs.filter(j => j.status === 'pending' || j.status === 'running').length > 0 && (
              <span className="admin-tab-badge admin-tab-badge--jobs">
                {jobs.filter(j => j.status === 'pending' || j.status === 'running').length}
              </span>
            )}
            {jobs.some(j => j.status === 'failed') && (
              <span className="admin-tab-badge admin-tab-badge--error">!</span>
            )}
          </button>
          <button
            className={`admin-tab${activeTab === 'activity' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Активность
          </button>
        </>}
        {hasPerm('analytics') && (
          <button
            className={`admin-tab${activeTab === 'analytics' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Аналитика
          </button>
        )}
        {hasPerm('selection') && (
          <button
            className={`admin-tab${activeTab === 'selection' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('selection')}
          >
            Подборка
          </button>
        )}
        {(isRealAdmin || hasPerm('cheese')) && (
          <button
            className={`admin-tab${activeTab === 'cheese' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('cheese')}
          >
            Cheese
          </button>
        )}
        {(hasPerm('retail_sites') || hasPerm('retail_publish') || hasPerm('retail_site_admin')) && (
          <button
            className={`admin-tab${activeTab === 'roznitsa' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('roznitsa')}
          >
            Розница
          </button>
        )}
        {hasPerm('photo_fix') && (
          <button
            className={`admin-tab${activeTab === 'photo_fix' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('photo_fix')}
          >
            Фікс Фото
          </button>
        )}
        {isRealAdmin && <>
          <button
            className={`admin-tab${activeTab === 'photo_catalog' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('photo_catalog')}
          >
            Каталог Фото
          </button>
          <button
            className={`admin-tab${activeTab === 'automation' ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab('automation')}
          >
            Автоматизация
          </button>
        </>}
      </div>

      {/* Мобильный дропдаун — вместо вкладок на узком экране */}
      <div className="admin-tabs-select-wrap">
        <select
          className="admin-tabs-select"
          value={activeTab}
          onChange={e => {
            const tab = e.target.value;
            setActiveTab(tab);
            if (tab === 'jobs') loadJobs();
          }}
        >
          {isRealAdmin && <>
            <option value="users">Пользователи{pending.length > 0 ? ` (${pending.length})` : ''}</option>
            <option value="tables">Таблицы</option>
          </>}
          {hasPerm('queue') && <option value="queue">Добавить товары{queue.length > 0 ? ` (${queue.reduce((s, e) => s + e.count, 0)})` : ''}</option>}
          {isRealAdmin && <>
            <option value="manage">Управление товарами{staleCount > 0 ? ` (${staleCount})` : ''}</option>
            <option value="jobs">Задачи</option>
            <option value="activity">Активность</option>
          </>}
          {hasPerm('analytics') && <option value="analytics">Аналитика</option>}
          {hasPerm('selection') && <option value="selection">Подборка</option>}
          {(isRealAdmin || hasPerm('cheese')) && <option value="cheese">Cheese</option>}
          {(hasPerm('retail_sites') || hasPerm('retail_publish') || hasPerm('retail_site_admin')) && <option value="roznitsa">Розница</option>}
          {hasPerm('photo_fix') && <option value="photo_fix">Фікс Фото</option>}
          {isRealAdmin && <>
            <option value="photo_catalog">Каталог Фото</option>
            <option value="automation">Автоматизация</option>
          </>}
        </select>
      </div>

      <main className={`admin-main${activeTab === 'queue' ? ' admin-main--queue' : ''}${activeTab === 'analytics' ? ' admin-main--analytics' : ''}${activeTab === 'cheese' ? ' admin-main--cheese' : ''}${activeTab === 'roznitsa' ? ' admin-main--retail' : ''}${activeTab === 'photo_catalog' ? ' admin-main--photo-catalog' : ''}${activeTab === 'users' ? ' admin-main--users' : ''}`}>

        {/* ── ВКЛАДКА: Пользователи ───────────────────────────────────────── */}
        {activeTab === 'users' && isRealAdmin && (
          <>
            <form className="admin-invite-form" onSubmit={handleInvite}>
              <h2 className="admin-section-title" style={{ margin: 0 }}>Добавить пользователя</h2>
              <div className="admin-invite-row">
                <input
                  type="email"
                  className="admin-invite-input"
                  placeholder="email@example.com"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteStatus(null); }}
                  required
                />
                <button type="submit" className="admin-btn-approve" disabled={inviting}>
                  {inviting ? '...' : 'Добавить'}
                </button>
              </div>
              {inviteStatus === 'ok'    && <p className="admin-invite-ok">✓ Пользователь добавлен</p>}
              {inviteStatus === 'error' && <p className="admin-invite-err">Ошибка — проверьте email</p>}
            </form>

            <div className="admin-stats">
              <div className="admin-stat admin-stat-online">
                <span className="admin-stat-num">{online.online_users}</span>
                <span className="admin-stat-label">Онлайн</span>
              </div>
              <div className="admin-stat admin-stat-online">
                <span className="admin-stat-num">{online.online_devices}</span>
                <span className="admin-stat-label">Устройств</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-num">{pending.length}</span>
                <span className="admin-stat-label">Ожидают</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-num">{active.length}</span>
                <span className="admin-stat-label">Активны</span>
              </div>
              <div className="admin-stat admin-stat-rejected">
                <span className="admin-stat-num">{rejected.length}</span>
                <span className="admin-stat-label">Отозваны</span>
              </div>
            </div>

            {usersLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : (
              <>
                <div className="admin-subtabs">
                  <button className={`admin-subtab${usersSubTab === 'admins' ? ' admin-subtab--active' : ''}`} onClick={() => selectUsersSubTab('admins')}>
                    Администраторы <span className="admin-subtab-count">{admins.length}</span>
                  </button>
                  <button className={`admin-subtab${usersSubTab === 'team' ? ' admin-subtab--active' : ''}`} onClick={() => selectUsersSubTab('team')}>
                    Команда <span className="admin-subtab-count">{team.length}</span>
                  </button>
                  <button className={`admin-subtab${usersSubTab === 'active' ? ' admin-subtab--active' : ''}`} onClick={() => selectUsersSubTab('active')}>
                    Пользователи <span className="admin-subtab-count">{active.length}</span>
                  </button>
                  <button className={`admin-subtab${usersSubTab === 'pending' ? ' admin-subtab--active' : ''}`} onClick={() => selectUsersSubTab('pending')}>
                    Ожидают одобрения <span className="admin-subtab-count">{pending.length}</span>
                  </button>
                  <button className={`admin-subtab${usersSubTab === 'rejected' ? ' admin-subtab--active' : ''}`} onClick={() => selectUsersSubTab('rejected')}>
                    Отозванные <span className="admin-subtab-count">{rejected.length}</span>
                  </button>
                </div>

                <div className="admin-search-wrap">
                  <input
                    className="admin-search-input"
                    type="text"
                    placeholder="Поиск по email или имени..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="admin-search-clear" onClick={() => setSearch('')}>×</button>
                  )}
                </div>

                {usersSubTab === 'admins' && (
                  <section className="admin-section admin-section--admins">
                    <h2 className="admin-section-title">Администраторы</h2>
                    {admins.length === 0 ? (
                      <p className="admin-empty">Ничего не найдено</p>
                    ) : (
                      <div className="admin-table">
                        {admins.map(u => (
                          <UserRow key={u.id} u={u} action={
                            <span className="admin-badge-label">Администратор</span>
                          } />
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {usersSubTab === 'team' && (
                  <section className="admin-section admin-section--team">
                    <h2 className="admin-section-title">Команда <span className="admin-section-sub">— доступ к аналитике</span></h2>
                    {team.length === 0 ? (
                      <p className="admin-empty">Ничего не найдено</p>
                    ) : (
                    <div className="admin-table">
                      {team.map(u => (
                        <UserRow key={u.id} u={u} action={
                          <div style={{ display: 'flex', gap: 6 }}>
                            {confirmTeam?.id === u.id ? (
                              <div className="admin-confirm-reject">
                                <span className="admin-confirm-label">Убрать из команды?</span>
                                <button className="admin-btn-team admin-btn-team--active" disabled={busy === u.id} onClick={() => handleToggleTeam(u.id, true)}>
                                  {busy === u.id ? '...' : 'Да'}
                                </button>
                                <button className="admin-btn-cancel-confirm" onClick={() => setConfirmTeam(null)}>Нет</button>
                              </div>
                            ) : (
                              <button
                                className="admin-btn-team admin-btn-team--active"
                                disabled={busy === u.id}
                                onClick={() => setConfirmTeam({ id: u.id, action: 'remove' })}
                                title="Убрать доступ к аналитике"
                              >
                                👥 Команда
                              </button>
                            )}
                            {confirmReject === u.id ? (
                              <div className="admin-confirm-reject">
                                <span className="admin-confirm-label">Отозвать?</span>
                                <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => handleReject(u.id)}>
                                  {busy === u.id ? '...' : 'Да'}
                                </button>
                                <button className="admin-btn-cancel-confirm" onClick={() => setConfirmReject(null)}>Нет</button>
                              </div>
                            ) : (
                              <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => setConfirmReject(u.id)}>Отозвать</button>
                            )}
                          </div>
                        } />
                      ))}
                    </div>
                    )}
                  </section>
                )}

                {usersSubTab === 'pending' && (
                  <section className="admin-section admin-section--pending">
                    <h2 className="admin-section-title">Ожидают одобрения</h2>
                    {pending.length === 0 ? (
                      <p className="admin-empty">Ничего не найдено</p>
                    ) : (
                    <div className="admin-table">
                      {pending.map(u => (
                        <UserRow key={u.id} u={u} action={
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="admin-btn-approve" disabled={busy === u.id} onClick={() => handleApprove(u.id)}>
                              {busy === u.id ? '...' : 'Одобрить'}
                            </button>
                            {confirmDelete === u.id ? (
                              <>
                                <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => handleDelete(u.id)}>Да</button>
                                <button className="admin-btn-cancel-confirm" onClick={() => setConfirmDelete(null)}>Нет</button>
                              </>
                            ) : (
                              <button className="admin-btn-delete" disabled={busy === u.id} onClick={() => setConfirmDelete(u.id)} title="Удалить">🗑</button>
                            )}
                          </div>
                        } />
                      ))}
                    </div>
                    )}
                  </section>
                )}

                {usersSubTab === 'active' && (
                <section className="admin-section admin-section--active">
                  <h2 className="admin-section-title">Пользователи</h2>

                  {/* Группы — фильтр-кнопки, как во вкладке "Сайты" */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
                    <button
                      className={`rt-btn rt-btn-sm${activeUserGroupId === null ? ' rt-btn-primary' : ''}`}
                      onClick={() => selectUserGroup(null)}
                    >
                      Все
                    </button>
                    <button
                      className={`rt-btn rt-btn-sm${activeUserGroupId === 'none' ? ' rt-btn-primary' : ''}`}
                      onClick={() => selectUserGroup('none')}
                    >
                      Без группы
                      <span style={{ marginLeft: 5, opacity: 0.6, fontSize: 11 }}>{active.filter(u => !u.group_id).length}</span>
                    </button>
                    {groups.map(g => (
                      <button
                        key={g.id}
                        className={`rt-btn rt-btn-sm${activeUserGroupId === g.id ? ' rt-btn-primary' : ''}`}
                        onClick={() => selectUserGroup(g.id)}
                      >
                        {g.name}
                        <span style={{ marginLeft: 5, opacity: 0.6, fontSize: 11 }}>{g.total_count ?? 0}</span>
                      </button>
                    ))}
                    <button
                      className="rt-btn rt-btn-sm"
                      style={{ marginLeft: 4, opacity: 0.7 }}
                      onClick={() => setUserGroupsModalOpen(true)}
                      title="Управлять группами"
                    >
                      ⚙️ Группы
                    </button>
                  </div>

                  {(() => {
                    const noGroupSelected = activeUserGroupId === 'none';
                    const activeGroup = noGroupSelected ? null : (groups.find(g => g.id === activeUserGroupId) || null);
                    const list = noGroupSelected
                      ? active.filter(u => !u.group_id)
                      : activeGroup
                        ? active.filter(u => u.group_id === activeGroup.id)
                        : active;

                    if (list.length === 0) {
                      return (
                        <p className="admin-empty">
                          {noGroupSelected ? 'Пользователей без группы не найдено'
                            : activeGroup ? `В группе «${activeGroup.name}» никого не найдено` : 'Нет активных пользователей'}
                        </p>
                      );
                    }
                    return (
                      <div className="admin-table">
                        {list.map(u => (
                          <UserRow key={u.id} u={u} showGroupSelect action={activeUserRowActions(u)} />
                        ))}
                      </div>
                    );
                  })()}

                  {userGroupsModalOpen && (
                    <UserGroupsModal onClose={() => setUserGroupsModalOpen(false)} />
                  )}
                </section>
                )}

                {usersSubTab === 'rejected' && (
                  <section className="admin-section admin-section--rejected">
                    <h2 className="admin-section-title">Отозванные</h2>
                    {rejected.length === 0 ? (
                      <p className="admin-empty">Ничего не найдено</p>
                    ) : (
                    <div className="admin-table">
                      {rejected.map(u => (
                        <UserRow key={u.id} u={u} action={
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="admin-btn-restore" disabled={busy === u.id} onClick={() => handleApprove(u.id)}>
                              {busy === u.id ? '...' : 'Восстановить'}
                            </button>
                            {confirmDelete === u.id ? (
                              <>
                                <button className="admin-btn-reject" disabled={busy === u.id} onClick={() => handleDelete(u.id)}>Да</button>
                                <button className="admin-btn-cancel-confirm" onClick={() => setConfirmDelete(null)}>Нет</button>
                              </>
                            ) : (
                              <button className="admin-btn-delete" disabled={busy === u.id} onClick={() => setConfirmDelete(u.id)} title="Удалить">🗑</button>
                            )}
                          </div>
                        } />
                      ))}
                    </div>
                    )}
                  </section>
                )}
              </>
            )}
          </>
        )}

        {/* ── ВКЛАДКА: Таблицы ──────────────────────────────────────────────── */}
        {activeTab === 'tables' && isRealAdmin && (
          <>
            <section className="admin-section">
              <h2 className="admin-section-title">Синхронизация</h2>

              <SyncPanel
                title="Каталог товаров"
                status={catalogStatus}
                onSync={handleSyncCatalog}
                syncing={syncingCatalog || syncingAll}
              />
              <SyncPanel
                title="Наличие у поставщиков"
                status={suppliersStatus}
                onSync={handleSyncSuppliers}
                syncing={syncingSuppliers || syncingAll}
              />

              <div className="admin-sync-all-row">
                <button
                  className="admin-btn-sync-all"
                  onClick={handleSyncAll}
                  disabled={isSyncing}
                >
                  {syncingAll ? 'Синхронизация...' : 'Синхронизировать всё сразу'}
                </button>
              </div>
            </section>

            <section className="admin-section">
              <h2 className="admin-section-title">Синхронизация — Одежда</h2>

              <SyncPanel
                title="Каталог одежды"
                status={clothingCatalogStatus}
                onSync={handleSyncClothingCatalog}
                syncing={syncingClothingCatalog || syncingClothingAll}
              />
              <SyncPanel
                title="Наличие у поставщиков (одежда)"
                status={clothingSuppliersStatus}
                onSync={handleSyncClothingSuppliers}
                syncing={syncingClothingSuppliers || syncingClothingAll}
              />
              <SyncPanel
                title="Фото моделей одежды"
                status={clothingModelsStatus}
                onSync={handleSyncClothingModels}
                syncing={syncingClothingModels || syncingClothingAll}
              />

              <div className="admin-sync-all-row">
                <button
                  className="admin-btn-sync-all"
                  onClick={handleSyncClothingAll}
                  disabled={syncingClothingCatalog || syncingClothingSuppliers || syncingClothingModels || syncingClothingAll}
                >
                  {syncingClothingAll ? 'Синхронизация...' : 'Синхронизировать одежду'}
                </button>
              </div>
            </section>

            <section className="admin-section">
              <h2 className="admin-section-title">Кэш изображений</h2>
              <ImageCacheResizePanel />
              <ImageCachePurgePanel />
              <RestorePlaceholdersPanel />
            </section>

            <section className="admin-section">
              <h2 className="admin-section-title">Настройки Google Sheets</h2>
              <ConfigSection />
            </section>
          </>
        )}

        {/* ── ВКЛАДКА: Очередь ─────────────────────────────────────────────── */}
        {activeTab === 'queue' && hasPerm('queue') && (
          <>
          <div className="queue-layout">

            {/* Колонка 1 — список поставщиков */}
            <div className="queue-suppliers">
              <div className="queue-panel-header">
                <span>Поставщики</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="queue-refresh-btn"
                    title={showQueueActivity ? 'Скрыть активность' : 'Показать активность'}
                    onClick={() => {
                      if (!showQueueActivity && !activity) loadActivity(activityDays);
                      setShowQueueActivity(v => !v);
                    }}
                    style={{ fontSize: 14 }}
                  >
                    📊
                  </button>
                  <button
                    className="queue-refresh-btn"
                    title="Обновить кэш из Google Sheets"
                    onClick={handleRefreshCache}
                    disabled={queueLoading || cacheRefreshing}
                  >
                    {cacheRefreshing ? '⟳' : queueLoading ? '...' : '↻'}
                  </button>
                </div>
              </div>
              {queueCacheAge !== null && !queueLoading && (
                <div style={{ padding: '3px 16px', fontSize: 10, color: 'var(--lg-text-3)', textTransform: 'none', fontWeight: 'normal', letterSpacing: 'normal', borderBottom: '1px solid var(--lg-border)' }}>
                  {queueCacheAge < 60
                    ? `обновлено ${queueCacheAge}с назад`
                    : queueCacheAge < 3600
                      ? `обновлено ${Math.round(queueCacheAge / 60)} мин назад`
                      : `обновлено ${Math.round(queueCacheAge / 3600)} ч назад`}
                </div>
              )}
              {queueError && <p className="queue-error">{queueError}</p>}
              {!queueLoading && queue.length === 0 && !queueError && (
                <p className="queue-empty">Очередь пуста</p>
              )}
              {queue.map(entry => (
                <div
                  key={entry.supplier}
                  className={`queue-supplier-card${selectedSupplier?.supplier === entry.supplier ? ' active' : ''}`}
                  onClick={() => handleSelectSupplier(entry)}
                >
                  <div className="queue-supplier-name">{entry.supplier}</div>
                  <div className="queue-supplier-card-right">
                    <span className="queue-supplier-count">{entry.count}</span>
                    <button
                      className="queue-supplier-refresh"
                      title="Обновить"
                      disabled={refreshingSupplier === entry.supplier}
                      onClick={e => handleRefreshSupplier(e, entry.supplier)}
                    >
                      {refreshingSupplier === entry.supplier ? '…' : '↻'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Колонка 2 — список артикулов */}
            <div className="queue-articles">
              <div className="queue-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span>Артикулы</span>
                {selectedSupplier && (
                  <button
                    className="btn-sm"
                    disabled={gasAvtoartLoading}
                    title="Запустить GAS avtoart2 для этого поставщика"
                    onClick={async () => {
                      setGasAvtoartLoading(true);
                      setGasAvtoartMsg('');
                      try {
                        await adminItemsAPI.runGasAvtoart(selectedSupplier.supplier);
                        setGasAvtoartMsg('Запущено');
                      } catch (e) {
                        setGasAvtoartMsg(e.response?.data?.detail || 'Ошибка');
                      } finally {
                        setGasAvtoartLoading(false);
                      }
                    }}
                  >
                    {gasAvtoartLoading ? '...' : 'Получить артикулы'}
                  </button>
                )}
              </div>
              {gasAvtoartMsg && (
                <div style={{ padding: '4px 8px', fontSize: 12, color: gasAvtoartMsg === 'Запущено' ? 'var(--color-success, #4caf50)' : 'var(--color-error, #e53935)' }}>
                  {gasAvtoartMsg}
                </div>
              )}
              {!selectedSupplier ? (
                <p className="queue-empty">Выберите поставщика</p>
              ) : selectedSupplier.all_articles.length === 0 ? (
                <p className="queue-empty">Все добавлены ✓</p>
              ) : (
                selectedSupplier.all_articles.map(article => (
                  <div
                    key={article}
                    className={`queue-article-row${selectedArticle === article ? ' active' : ''}`}
                    onClick={() => handleSelectArticle(article)}
                    onMouseEnter={() => {
                      clearTimeout(hoverPrefetchRef.current);
                      hoverPrefetchRef.current = setTimeout(() => {
                        if (article !== selectedArticle) {
                          adminItemsAPI.getArticleDetails(selectedSupplier.supplier, article).catch(() => {});
                        }
                      }, 200);
                    }}
                    onMouseLeave={() => clearTimeout(hoverPrefetchRef.current)}
                  >
                    {article}
                  </div>
                ))
              )}
            </div>

            {/* Колонка 3 — форма + фото */}
            <div className="queue-form-panel">
              <div className="queue-panel-header queue-form-panel-header">
                <button
                  className={`queue-mode-btn${!addModelMode && !fixSheetMode ? ' queue-mode-btn--active' : ''}`}
                  onClick={() => { setAddModelMode(false); setFixSheetMode(false); }}
                >По поставщику</button>
                <button
                  className={`queue-mode-btn${addModelMode ? ' queue-mode-btn--active' : ''}`}
                  onClick={() => { setAddModelMode(true); setFixSheetMode(false); setAddModelError(''); setAddModelDone(''); setAddModelSelectedProduct(null); if (!newItemConfig) adminItemsAPI.getNewItemConfig().then(r => setNewItemConfig(r.data)).catch(() => {}); }}
                >Новая модель</button>
                {cbSection === 'shoes' && (
                  <button
                    className={`queue-mode-btn${fixSheetMode ? ' queue-mode-btn--active' : ''}`}
                    onClick={() => { setFixSheetMode(true); setAddModelMode(false); setFixSheetResult(null); setFixSheetError(''); }}
                  >Исправить лист</button>
                )}
              </div>

              {/* ── Режим: Новая модель ── */}
              {addModelMode && (
                <div className="queue-form">
                  {/* Артикул новой модели */}
                  {newItemConfig?.next_article && (
                    <p className="queue-new-item-hint">
                      Новый артикул: <strong>{newItemConfig.next_article}</strong>
                    </p>
                  )}

                  {/* Фото */}
                  <div className="queue-photos">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) handleAddModelPhotoUpload(e.target.files[0]); e.target.value = ''; }}
                    />
                    <div
                      className={`queue-photo-box queue-photo-box--upload${photoDragOver ? ' queue-photo-box--dragover' : ''}`}
                      onClick={() => photoInputRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                      onDragLeave={() => setPhotoDragOver(false)}
                      onDrop={e => { e.preventDefault(); setPhotoDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleAddModelPhotoUpload(f); }}
                      title="Кликните или перетащите фото (4:3)"
                      style={{ cursor: 'pointer', flex: 1 }}
                    >
                      <div className="queue-photo-label">Фото модели (4:3)</div>
                      {photoUploading ? (
                        <div className="queue-photo-placeholder queue-photo-loading">Загрузка...</div>
                      ) : photoDragOver ? (
                        <div className="queue-photo-placeholder queue-photo-drag">Отпустите фото</div>
                      ) : addModelForm.photo_url ? (
                        <img className="queue-photo-img" src={addModelForm.photo_url} alt="preview" onError={e => { e.target.style.display='none'; }} />
                      ) : (
                        <div className="queue-photo-placeholder queue-photo-upload-hint">
                          <span>+ фото</span><small>клик или перетащить</small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Кроппер для addModelMode */}
                  {addModelCropFile && (
                    <CropModal
                      file={addModelCropFile}
                      onConfirm={(croppedFile) => { setAddModelCropFile(null); _uploadAddModelFile(croppedFile); }}
                      onCancel={() => setAddModelCropFile(null)}
                    />
                  )}

                  {/* Поля формы */}
                  <div className="queue-field">
                    <label className="queue-field-label">Фото (ссылка)</label>
                    <input className="queue-input" value={addModelForm.photo_url}
                      onChange={e => setAddModelForm(f => ({ ...f, photo_url: e.target.value }))}
                      placeholder="https://imgbox.com/… или вставьте ссылку" />
                  </div>

                  <div className="queue-field">
                    <label className="queue-field-label">Название товара *</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="queue-input" value={addModelForm.product_name}
                        onChange={e => { const v = e.target.value; const c = parseColorsFromName(v); setAddModelForm(f => ({ ...f, product_name: v, ...(c ? { color: c } : {}) })); }}
                        placeholder="Nike Air Max 90…" autoFocus style={{ flex: 1 }} />
                      <button
                        type="button"
                        className="queue-after-product-clear"
                        style={{ width: 'auto', padding: '0 10px', borderRadius: 6 }}
                        title="Згенерувати назву по фото (Claude)"
                        disabled={genNameLoading || !addModelForm.photo_url}
                        onClick={handleGenerateProductName}
                      >
                        {genNameLoading ? '...' : '✨'}
                      </button>
                    </div>
                    {genNameError && <p className="queue-form-error" style={{ marginTop: 4 }}>{genNameError}</p>}
                  </div>

                  <div className="queue-field">
                    <label className="queue-field-label">После товара (AfterID) *</label>
                    {addModelSelectedProduct ? (
                      <div className="queue-after-product">
                        <div className="queue-after-product-sku">{addModelSelectedProduct.sku}</div>
                        <div className="queue-after-product-name">{addModelSelectedProduct.name}</div>
                        <button className="queue-after-product-clear" onClick={() => { setAddModelSelectedProduct(null); setAddModelForm(f => ({ ...f, after_id: '' })); }}>×</button>
                      </div>
                    ) : (
                      <input className="queue-input" value={addModelForm.after_id}
                        onChange={e => setAddModelForm(f => ({ ...f, after_id: e.target.value }))}
                        placeholder={newItemConfig?.last_article || 'Выберите из каталога →'} />
                    )}
                    <div className="queue-field-hint">Выберите товар справа или введите артикул вручную</div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="queue-field" style={{ flex: 1 }}>
                      <label className="queue-field-label">Сезон *</label>
                      <select className="queue-input" value={addModelForm.season}
                        onChange={e => setAddModelForm(f => ({ ...f, season: e.target.value }))}>
                        <option value="">— выбрать —</option>
                        <option value="Зима">Зима</option>
                        <option value="Лето">Лето</option>
                        <option value="Весна-Осень-Лето">Весна-Осень-Лето</option>
                      </select>
                    </div>
                    <div className="queue-field" style={{ flex: 1 }}>
                      <label className="queue-field-label">Цвет</label>
                      <input className="queue-input" value={addModelForm.color}
                        onChange={e => setAddModelForm(f => ({ ...f, color: e.target.value }))}
                        placeholder="Чёрный…" />
                    </div>
                  </div>

                  <div className="queue-field">
                    <label className="queue-field-label">Новый лист (только для нового бренда/модели)</label>
                    <input className="queue-input" value={addModelForm.new_list_name}
                      onChange={e => setAddModelForm(f => ({ ...f, new_list_name: e.target.value }))}
                      placeholder="Nike Air Max 90 (оставьте пустым если модель уже есть)" />
                  </div>

                  {addModelForm.new_list_name.trim() && (
                    <div className="queue-field">
                      <label className="queue-field-label">Бренд (столбец E) — если отличается от AfterID</label>
                      <input className="queue-input" value={addModelForm.brand}
                        onChange={e => setAddModelForm(f => ({ ...f, brand: e.target.value }))}
                        placeholder="Nike" />
                    </div>
                  )}

                  <div className="queue-field">
                    <label className="queue-field-label">Ключевые слова</label>
                    <input className="queue-input" value={addModelForm.keywords}
                      onChange={e => setAddModelForm(f => ({ ...f, keywords: e.target.value }))}
                      placeholder="кроссовки мужские белые…" />
                  </div>

                  {addModelError && <p className="queue-form-error">{addModelError}</p>}
                  {addModelDone  && <p className="queue-form-done">{addModelDone}</p>}

                  <button
                    className="queue-form-submit queue-form-submit--new"
                    onClick={handleAddCatalogModel}
                    disabled={addModelSaving || !addModelForm.product_name.trim() || !addModelForm.after_id.trim() || !addModelForm.season}
                  >
                    {addModelSaving ? 'Добавление…' : '▶ Добавить модель в каталог'}
                  </button>
                </div>
              )}

              {/* ── Режим: Исправить лист ── */}
              {fixSheetMode && (
                <div className="queue-form">
                  <div>
                    <label className="queue-form-label">Артикул каталога *</label>
                    <input
                      className="queue-form-input"
                      placeholder="ALL123"
                      value={fixSheetForm.catalog_id}
                      onChange={e => setFixSheetForm(f => ({ ...f, catalog_id: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="queue-form-label">Правильный лист *</label>
                    <input
                      className="queue-form-input"
                      placeholder="Nike Air Force 1"
                      value={fixSheetForm.new_list_name}
                      onChange={e => setFixSheetForm(f => ({ ...f, new_list_name: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="queue-form-label" style={{ whiteSpace: 'normal' }}>
                      {fixSheetForm.is_new_model ? 'AfterID — артикул соседней модели (шаблон) *' : 'AfterID'}
                      {!fixSheetForm.is_new_model && <span style={{ color: 'var(--lg-text-3)', fontWeight: 400, marginLeft: 4 }}>(необязательно)</span>}
                    </label>
                    <input
                      className="queue-form-input"
                      placeholder={fixSheetForm.is_new_model ? 'ALL100 — артикул соседней модели' : 'ALL100 (необязательно)'}
                      value={fixSheetForm.after_id}
                      onChange={e => setFixSheetForm(f => ({ ...f, after_id: e.target.value }))}
                    />
                    {fixSheetForm.is_new_model && (
                      <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginTop: 3 }}>
                        Нужен для ID таблиц ALL/Setting и структуры листа-шаблона
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        style={{ marginTop: 2, flexShrink: 0 }}
                        checked={fixSheetForm.is_new_model}
                        onChange={e => setFixSheetForm(f => ({ ...f, is_new_model: e.target.checked, after_id: '' }))}
                      />
                      Новая модель (создать лист в ALL/Setting)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        style={{ marginTop: 2, flexShrink: 0 }}
                        checked={fixSheetForm.delete_old}
                        onChange={e => setFixSheetForm(f => ({ ...f, delete_old: e.target.checked }))}
                      />
                      <span>Удалить из старого листа <span style={{ color: 'var(--danger)', fontWeight: 600 }}>(удалит 17 строк)</span></span>
                    </label>
                    {fixSheetForm.delete_old && (
                      <div style={{ marginLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <input
                          className="queue-form-input"
                          placeholder="Название старого листа, напр. NB New Balance U9060"
                          value={fixSheetForm.old_list_name}
                          onChange={e => setFixSheetForm(f => ({ ...f, old_list_name: e.target.value }))}
                        />
                        <input
                          className="queue-form-input"
                          placeholder="Артикул из старого листа (для нахождения таблицы)"
                          value={fixSheetForm.old_after_id}
                          onChange={e => setFixSheetForm(f => ({ ...f, old_after_id: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>

                  {fixSheetError && (
                    <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{fixSheetError}</div>
                  )}
                  {fixSheetResult && (
                    <div style={{ background: 'var(--lg-bg)', border: '1px solid var(--lg-border)', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--success)' }}>Готово</div>
                      {fixSheetResult.catalog && (
                        <div>КаталогТоваров строка {fixSheetResult.catalog.row}: <b>{fixSheetResult.catalog.old_sheet || '(пусто)'}</b> → <b>{fixSheetResult.catalog.new_sheet}</b></div>
                      )}
                      {fixSheetResult.slist && (
                        <div style={{ marginTop: 4 }}>
                          Список товаров: {fixSheetResult.slist.ok ? `строка ${fixSheetResult.slist.row} обновлена` : fixSheetResult.slist.reason}
                        </div>
                      )}
                      {fixSheetResult.all_setting && (() => {
                        const as = fixSheetResult.all_setting;
                        const detail = as.detail || {};
                        return (
                          <div style={{ marginTop: 4 }}>
                            <b>ALL/Setting ({as.mode}):</b>
                            {Object.entries(detail).map(([k, v]) => (
                              <div key={k} style={{ marginLeft: 8, color: String(v).startsWith('ОШИБКА') || String(v).startsWith('error') ? 'var(--danger)' : 'var(--success)' }}>
                                {k}: {String(v)}
                              </div>
                            ))}
                            {!Object.keys(detail).length && (
                              <span style={{ marginLeft: 8 }}>{as.ok ? 'OK' : as.reason}</span>
                            )}
                          </div>
                        );
                      })()}
                      {fixSheetResult.delete_old && (() => {
                        const d = fixSheetResult.delete_old;
                        const sd = d.setting_delete || {};
                        const ad = d.all_delete || {};
                        return (
                          <div style={{ marginTop: 4 }}>
                            <b>Удаление из старого листа:</b>
                            <div style={{ marginLeft: 8, color: sd.ok === false ? 'var(--danger)' : sd.ok ? 'var(--success)' : 'var(--lg-text-3)' }}>
                              Setting: {sd.ok ? `удалены строки ${sd.deleted_rows} (лист: ${sd.sheet})` : sd.reason || 'пропущено'}
                              {sd.available?.length > 0 && <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>Листы в таблице: {sd.available.join(', ')}</div>}
                            </div>
                            <div style={{ marginLeft: 8, color: ad.ok === false ? 'var(--danger)' : ad.ok ? 'var(--success)' : 'var(--lg-text-3)' }}>
                              ALL: {ad.ok ? `удалены строки ${ad.deleted_rows} (лист: ${ad.sheet})` : ad.reason || 'пропущено'}
                              {ad.available?.length > 0 && <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>Листы в таблице: {ad.available.join(', ')}</div>}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <button
                    className="queue-submit-btn"
                    disabled={fixSheetLoading || !fixSheetForm.catalog_id.trim() || !fixSheetForm.new_list_name.trim()}
                    onClick={async () => {
                      setFixSheetLoading(true);
                      setFixSheetError('');
                      setFixSheetResult(null);
                      try {
                        const res = await adminItemsAPI.fixItemSheet({
                          catalog_id:    fixSheetForm.catalog_id.trim(),
                          new_list_name: fixSheetForm.new_list_name.trim(),
                          after_id:      fixSheetForm.after_id.trim(),
                          is_new_model:  fixSheetForm.is_new_model,
                          delete_old:    fixSheetForm.delete_old,
                          old_list_name: fixSheetForm.old_list_name.trim(),
                          old_after_id:  fixSheetForm.old_after_id.trim(),
                        });
                        setFixSheetResult(res.data ?? res);
                      } catch (e) {
                        setFixSheetError(e?.response?.data?.detail || e.message || 'Ошибка');
                      } finally {
                        setFixSheetLoading(false);
                      }
                    }}
                  >
                    {fixSheetLoading ? 'Исправляем...' : 'Исправить'}
                  </button>
                </div>
              )}

              {/* ── Режим: По поставщику ── */}
              {!addModelMode && !fixSheetMode && !selectedArticle ? (
                <p className="queue-empty">Выберите артикул</p>
              ) : !addModelMode && !fixSheetMode && (
                <div className="queue-form">
                  {/* Фото: товар поставщика | товар каталога — клик открывает сравнение */}
                  {(() => {
                    const openCompare = () => {
                      const p = selectedCatalogProduct;
                      const idx = p ? cbProducts.findIndex(x => x.id === p.id) : -1;
                      setCompareIndex(idx);
                      setCompareProduct(p || { photo_url: '' });
                    };
                    const canCompare = !!(articleDetails?.photo_url || selectedCatalogProduct);
                    return (
                      <div className="queue-photos">
                        {/* скрытый input для выбора файла */}
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={e => { if (e.target.files[0]) handlePhotoUpload(e.target.files[0]); e.target.value = ''; }}
                        />
                        <div
                          className={`queue-photo-box queue-photo-box--upload${photoDragOver ? ' queue-photo-box--dragover' : ''}${canCompare && !photoDragOver ? ' queue-photo-box--clickable' : ''}`}
                          onClick={() => photoInputRef.current?.click()}
                          onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                          onDragLeave={() => setPhotoDragOver(false)}
                          onDrop={e => {
                            e.preventDefault();
                            setPhotoDragOver(false);
                            const f = e.dataTransfer.files[0];
                            if (f) handlePhotoUpload(f);
                          }}
                          title="Кликните или перетащите фото для загрузки на imgbox"
                        >
                          <div className="queue-photo-label">Фото поставщика</div>
                          {photoUploading ? (
                            <div className="queue-photo-placeholder queue-photo-loading">Загрузка...</div>
                          ) : photoDragOver ? (
                            <div className="queue-photo-placeholder queue-photo-drag">Отпустите фото</div>
                          ) : articleDetailsLoading ? (
                            <div className="queue-photo-placeholder queue-photo-loading">...</div>
                          ) : queueForm.photo_url ? (
                            <RetryImage
                              className="queue-photo-img"
                              src={withBust(queueForm.photo_url)}
                              alt="uploaded"
                            />
                          ) : articleDetails?.photo_url ? (
                            <RetryImage
                              className="queue-photo-img"
                              src={withBust(articleDetails.photo_url)}
                              alt="supplier"
                            />
                          ) : (
                            <div className="queue-photo-placeholder queue-photo-upload-hint">
                              <span>+ фото</span>
                              <small>клик или перетащить</small>
                            </div>
                          )}
                        </div>
                        <div
                          className={`queue-photo-box${canCompare ? ' queue-photo-box--clickable' : ''}`}
                          onClick={canCompare ? openCompare : undefined}
                          title={canCompare ? 'Нажмите для сравнения фото' : undefined}
                        >
                          <div className="queue-photo-label">Фото каталога</div>
                          {selectedCatalogProduct?.photo_url ? (
                            <RetryImage
                              className="queue-photo-img"
                              src={withBust(selectedCatalogProduct.photo_url)}
                              alt="catalog"
                            />
                          ) : (
                            <div className="queue-photo-placeholder">
                              {selectedCatalogProduct ? 'Нет фото' : 'Выберите из каталога →'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {(queueForm.photo_url || articleDetails?.photo_url) && (
                    <div style={{ margin: '4px 0 10px' }}>
                      <button
                        type="button"
                        onClick={handlePhotoSearch}
                        disabled={photoSearchLoading}
                        className="rt-btn rt-btn-sm"
                        title="Найти похожие товары в каталоге по фото — работает только на этом Маке, пока запущен AllDrop Photo Search"
                      >
                        {photoSearchLoading ? '🔍 Ищу...' : '🔍 Найти похожие (локально)'}
                      </button>
                      {photoSearchOpen && (
                        <div style={{ marginTop: 8 }}>
                          {photoSearchError && (
                            <div style={{ fontSize: 12, color: 'var(--lg-text-3, #888)' }}>{photoSearchError}</div>
                          )}
                          {photoSearchResults && photoSearchResults.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--lg-text-3, #888)' }}>Похожих не найдено</div>
                          )}
                          {/* Результати показуються справа, в основному вікні каталогу */}
                          {photoSearchResults && photoSearchResults.length > 0 && (
                            <div style={{ fontSize: 12, color: 'var(--lg-text-3, #888)' }}>
                              Знайдено {photoSearchResults.length} — дивіться праворуч, у вікні каталогу
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="queue-form-supplier">
                    <span className="queue-form-label">Поставщик</span>
                    <span className="queue-form-value">{selectedSupplier.supplier}</span>
                  </div>
                  <div className="queue-form-supplier">
                    {articleBdpromUrl ? (
                      <a
                        href={articleBdpromUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="queue-form-label queue-form-label--link"
                        title="Открыть на EASYBDPROM"
                      >
                        Артикул
                      </a>
                    ) : (
                      <span className="queue-form-label">
                        Артикул
                        {articleBdpromUrl === null && <span className="queue-form-bdprom-loading" />}
                      </span>
                    )}
                    <span className="queue-form-value queue-form-value--mono">{selectedArticle}</span>
                  </div>

                  {renameDone && (
                    <div className="rename-done-banner">{renameDone}</div>
                  )}

                  {!queueForm.is_new && (
                    <div className="queue-form-field">
                      <label className="queue-form-label">Артикул каталога *</label>
                      <input
                        className="queue-form-input"
                        value={queueForm.catalog_id}
                        onChange={e => handleCatalogIdChange(e.target.value.trim())}
                      />
                      {selectedCatalogProduct && (
                        <span className="queue-selected-product-name">
                          {selectedCatalogProduct.brand} {selectedCatalogProduct.model} — {selectedCatalogProduct.name}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="queue-form-field">
                    <label
                      className={`queue-form-label${queueForm.photo_link ? ' queue-form-label--copy' : ''}`}
                      title={queueForm.photo_link ? 'Скопировать ссылку' : undefined}
                      onClick={() => {
                        if (!queueForm.photo_link) return;
                        navigator.clipboard.writeText(queueForm.photo_link).then(() => {
                          setPhotoLinkCopied(true);
                          setTimeout(() => setPhotoLinkCopied(false), 1500);
                        });
                      }}
                    >
                      {photoLinkCopied ? 'Скопировано ✓' : 'Ссылка на фото TG'}
                    </label>
                    <input
                      className="queue-form-input"
                      value={queueForm.photo_link}
                      onChange={e => setQueueForm(f => ({ ...f, photo_link: e.target.value.trim() }))}
                    />
                  </div>

                  <div className="queue-form-field">
                    <label className="queue-form-label">
                      URL фото (прямая ссылка)
                      {rehostingPhoto && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--lg-text-3)' }}>⏳ Загрузка на imgbox…</span>}
                    </label>
                    <input
                      className="queue-form-input"
                      value={queueForm.photo_url}
                      onChange={e => setQueueForm(f => ({ ...f, photo_url: e.target.value.trim() }))}
                      onBlur={e => handlePhotoUrlBlur(e.target.value.trim())}
                      disabled={rehostingPhoto}
                    />
                  </div>

                  {articleDetails?.bag_dimensions && (
                    <div className="queue-bag-dimensions">
                      <span className="queue-bag-dimensions__label">Размер сумки</span>
                      <span className="queue-bag-dimensions__value">{articleDetails.bag_dimensions}</span>
                    </div>
                  )}

                  <div className="queue-form-field">
                    <label className="queue-form-label">Ключевые слова</label>
                    <input
                      className="queue-form-input"
                      value={queueForm.keywords}
                      onChange={e => setQueueForm(f => ({ ...f, keywords: e.target.value }))}
                    />
                  </div>

                  <label className="queue-form-new-toggle">
                    <input
                      type="checkbox"
                      checked={queueForm.is_new}
                      onChange={e => handleIsNewToggle(e.target.checked)}
                    />
                    <span>Новый товар в каталоге</span>
                  </label>

                  {queueForm.is_new && (
                    <div className="queue-new-item-fields">
                      {cbSection === 'clothing' ? (
                        /* ── Форма нового товара ОДЕЖДЫ ── */
                        <>
                          {clothingAddConfigLoading && <p className="queue-new-item-hint">Загрузка конфига...</p>}
                          {!clothingAddConfig && !clothingAddConfigLoading && clothingAddError && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <span className="queue-error-text">{clothingAddError}</span>
                              <button
                                className="queue-btn queue-btn-secondary"
                                style={{ padding: '2px 10px', fontSize: 12 }}
                                onClick={() => {
                                  setClothingAddError('');
                                  setClothingAddConfigLoading(true);
                                  adminItemsAPI.getClothingItemConfig()
                                    .then(r => setClothingAddConfig(r.data))
                                    .catch(() => setClothingAddError('Не удалось загрузить конфиг. Нажмите "Попробовать снова".'))
                                    .finally(() => setClothingAddConfigLoading(false));
                                }}
                              >Попробовать снова</button>
                            </div>
                          )}
                          {clothingAddConfig && (
                            <div className="queue-new-item-next-id">
                              Новый артикул: <strong>{clothingAddConfig.next_sm_id}</strong>
                            </div>
                          )}

                          <div className="queue-form-field">
                            <label className="queue-form-label">Название *</label>
                            <input
                              className="queue-form-input"
                              value={clothingAddForm.name}
                              onChange={e => setClothingAddForm(f => ({ ...f, name: e.target.value }))}
                              placeholder="Nike Tech Fleece Black"
                            />
                          </div>

                          <div className="queue-new-item-row">
                            <div className="queue-form-field">
                              <label className="queue-form-label">
                                Бренд{clothingAddForm.new_brand && <span style={{ color: 'var(--lg-accent)', marginLeft: 4 }}>+ новый</span>}
                              </label>
                              <ComboBox
                                className="queue-form-input"
                                options={clothingAddConfig?.brands || []}
                                value={clothingAddForm.brand}
                                onChange={v => {
                                  const isNew = !!v && !(clothingAddConfig?.brands || []).some(b => b.toLowerCase() === v.toLowerCase());
                                  setClothingAddForm(f => ({ ...f, brand: v, new_brand: isNew }));
                                }}
                                placeholder="Бренд"
                                layout="en"
                              />
                              {clothingAddForm.new_brand && (
                                <input
                                  className="queue-form-input"
                                  style={{ marginTop: 4 }}
                                  value={clothingAddForm.brand_logo_url || ''}
                                  onChange={e => setClothingAddForm(f => ({ ...f, brand_logo_url: e.target.value.trim() }))}
                                  placeholder="Логотип бренда (URL)"
                                />
                              )}
                            </div>
                            <div className="queue-form-field">
                              <label className="queue-form-label">
                                Тип товара{clothingAddForm.new_type && <span style={{ color: 'var(--lg-accent)', marginLeft: 4 }}>+ новый</span>}
                              </label>
                              <ComboBox
                                className="queue-form-input"
                                options={clothingAddConfig?.types || []}
                                value={clothingAddForm.product_type}
                                onChange={v => {
                                  const isNew = !!v && !(clothingAddConfig?.types || []).some(t => t.toLowerCase() === v.toLowerCase());
                                  setClothingAddForm(f => ({ ...f, product_type: v, new_type: isNew }));
                                }}
                                placeholder="Тип товара"
                                layout="ru"
                              />
                              {clothingAddForm.new_type && (
                                <input
                                  className="queue-form-input"
                                  style={{ marginTop: 4 }}
                                  value={clothingAddForm.type_logo_url || ''}
                                  onChange={e => setClothingAddForm(f => ({ ...f, type_logo_url: e.target.value.trim() }))}
                                  placeholder="Фото типа товара (URL)"
                                />
                              )}
                            </div>
                          </div>

                          <div className="queue-new-item-row">
                            <div className="queue-form-field">
                              <label className="queue-form-label">
                                Цвет{clothingAddForm.new_color && <span style={{ color: 'var(--lg-accent)', marginLeft: 4 }}>+ новый</span>}
                              </label>
                              <ComboBox
                                className="queue-form-input"
                                options={clothingAddConfig?.colors || []}
                                value={clothingAddForm.color}
                                onChange={v => {
                                  const isNew = !!v && !(clothingAddConfig?.colors || []).some(c => c.toLowerCase() === v.toLowerCase());
                                  setClothingAddForm(f => ({ ...f, color: v, new_color: isNew }));
                                }}
                                placeholder="Цвет"
                                layout="ru"
                              />
                            </div>
                            <div className="queue-form-field">
                              <label className="queue-form-label">Пол</label>
                              <select
                                className="queue-form-input"
                                value={clothingAddForm.gender}
                                onChange={e => setClothingAddForm(f => ({ ...f, gender: e.target.value }))}
                              >
                                <option value="">— не указан —</option>
                                <option value="Мужское">Мужское</option>
                                <option value="Женское">Женское</option>
                                <option value="Унисекс">Унисекс</option>
                              </select>
                            </div>
                          </div>

                          <div className="queue-new-item-row">
                            <div className="queue-form-field">
                              <label className="queue-form-label">Замеры</label>
                              <input
                                className="queue-form-input"
                                value={clothingAddForm.measurements}
                                onChange={e => setClothingAddForm(f => ({ ...f, measurements: e.target.value }))}
                                placeholder="24x16x7"
                              />
                            </div>
                            <div className="queue-form-field">
                              <label className="queue-form-label">Сезон</label>
                              <select
                                className="queue-form-input"
                                value={clothingAddForm.season}
                                onChange={e => setClothingAddForm(f => ({ ...f, season: e.target.value }))}
                              >
                                <option value="">— не указан —</option>
                                <option value="Зима">Зима</option>
                                <option value="Весна">Весна</option>
                                <option value="Лето">Лето</option>
                                <option value="Осень">Осень</option>
                                <option value="Весна-Осень">Весна-Осень</option>
                                <option value="Всесезон">Всесезон</option>
                              </select>
                            </div>
                          </div>

                          <div className="queue-form-field">
                            <label className="queue-form-label">Материал</label>
                            <input
                              className="queue-form-input"
                              list="ca-materials"
                              value={clothingAddForm.material}
                              onChange={e => setClothingAddForm(f => ({ ...f, material: e.target.value }))}
                              placeholder="Эко-кожа"
                            />
                          </div>

                          <div className="queue-form-field">
                            <label className="queue-form-label">Описание</label>
                            <textarea
                              className="queue-form-input"
                              rows={3}
                              value={clothingAddForm.description}
                              onChange={e => setClothingAddForm(f => ({ ...f, description: e.target.value }))}
                              placeholder="Описание товара..."
                              style={{ resize: 'vertical', minHeight: 64 }}
                            />
                          </div>

                          <div className="queue-form-field">
                            <label className="queue-form-label">Группа «Другие цвета»</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--lg-text-2)' }}>
                                <input type="radio" name="oc-mode" value="new"
                                  checked={clothingAddForm.other_colors_mode === 'new'}
                                  onChange={() => setClothingAddForm(f => ({ ...f, other_colors_mode: 'new' }))} />
                                Новая группа
                                {clothingAddConfig && <span style={{ color: 'var(--lg-text-3)', marginLeft: 4 }}>({clothingAddConfig.next_oc_id})</span>}
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--lg-text-2)' }}>
                                <input type="radio" name="oc-mode" value="existing"
                                  checked={clothingAddForm.other_colors_mode === 'existing'}
                                  onChange={() => setClothingAddForm(f => ({ ...f, other_colors_mode: 'existing' }))} />
                                Существующая группа
                              </label>
                              {clothingAddForm.other_colors_mode === 'existing' && (
                                <input
                                  className="queue-form-input"
                                  value={clothingAddForm.other_colors_id}
                                  onChange={e => setClothingAddForm(f => ({ ...f, other_colors_id: e.target.value.trim() }))}
                                  placeholder="OC03434"
                                  style={{ marginLeft: 24 }}
                                />
                              )}
                              {clothingAddForm.other_colors_mode === 'new' && (
                                <input
                                  className="queue-form-input"
                                  value={clothingAddForm.oc_measurements}
                                  onChange={e => setClothingAddForm(f => ({ ...f, oc_measurements: e.target.value }))}
                                  placeholder="Замеры (столбец E) — необязательно"
                                  style={{ marginLeft: 24 }}
                                />
                              )}
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--lg-text-2)' }}>
                                <input type="radio" name="oc-mode" value="none"
                                  checked={clothingAddForm.other_colors_mode === 'none'}
                                  onChange={() => setClothingAddForm(f => ({ ...f, other_colors_mode: 'none' }))} />
                                Не указывать
                              </label>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* ── Форма нового товара ОБУВИ ── */
                        <>
                          {newItemConfigLoading && <p className="queue-new-item-hint">Загрузка конфига...</p>}
                          {newItemConfig && (
                            <div className="queue-new-item-next-id">
                              Новый артикул: <strong>{newItemConfig.next_article}</strong>
                            </div>
                          )}

                          <div className="queue-form-field">
                            <label className="queue-form-label">После артикула *</label>
                            <span className="queue-new-item-hint">двойной клик на карточке каталога</span>
                            <input
                              className="queue-form-input"
                              placeholder="ALL19754"
                              value={newItemForm.after_id}
                              onChange={e => handleAfterIdChange(e.target.value)}
                            />
                          </div>

                          <div className="queue-form-field">
                            <label className="queue-form-label">Название товара *</label>
                            <input
                              className="queue-form-input"
                              placeholder="Nike Air Max 90 White"
                              value={newItemForm.product_name}
                              onChange={e => { const v = e.target.value; const c = parseColorsFromName(v); setNewItemForm(f => ({ ...f, product_name: v, ...(c ? { color: c } : {}) })); }}
                            />
                          </div>

                          <div className="queue-new-item-row">
                            <div className="queue-form-field">
                              <label className="queue-form-label">Сезон *</label>
                              <select
                                className="queue-form-input"
                                value={newItemForm.season}
                                onChange={e => setNewItemForm(f => ({ ...f, season: e.target.value }))}
                              >
                                <option value="">— выберите —</option>
                                <option value="Зима">Зима</option>
                                <option value="Лето">Лето</option>
                                <option value="Весна-Осень-Лето">Весна-Осень-Лето</option>
                              </select>
                            </div>
                            <div className="queue-form-field">
                              <label className="queue-form-label">Цвет</label>
                              <input
                                className="queue-form-input"
                                placeholder="White"
                                value={newItemForm.color}
                                onChange={e => setNewItemForm(f => ({ ...f, color: e.target.value }))}
                              />
                            </div>
                          </div>

                          <div className="queue-form-field">
                            <label className="queue-form-label">Новый лист</label>
                            <span className="queue-new-item-hint">только если новый бренд/модель</span>
                            <input
                              className="queue-form-input"
                              placeholder="Оставьте пустым если модель уже есть"
                              value={newItemForm.new_list_name}
                              onChange={e => setNewItemForm(f => ({ ...f, new_list_name: e.target.value }))}
                            />
                          </div>

                          {newItemForm.new_list_name.trim() && (
                            <div className="queue-form-field">
                              <label className="queue-form-label">Бренд (столбец E)</label>
                              <span className="queue-new-item-hint">если отличается от AfterID — иначе подставится автоматически</span>
                              <input
                                className="queue-form-input"
                                placeholder="Nike"
                                value={newItemForm.brand}
                                onChange={e => setNewItemForm(f => ({ ...f, brand: e.target.value }))}
                              />
                            </div>
                          )}

                          {/* Фото для КаталогТоваров */}
                          <input
                            ref={catalogPhotoInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={e => { if (e.target.files[0]) handleCatalogPhotoUpload(e.target.files[0]); e.target.value = ''; }}
                          />
                          <div className="queue-form-field">
                            <label className="queue-form-label">Фото для каталога (КаталогТоваров)</label>
                            <span className="queue-new-item-hint">соотношение 4:3 — при несовпадении откроется кроппер</span>
                            {queueForm.photo_url && !queueForm.catalog_photo_url && (
                              <button
                                className="queue-btn-use-supplier-photo"
                                type="button"
                                disabled={catalogPhotoUploading}
                                onClick={() => {
                                  setCatalogPhotoUploading(true);
                                  setQueueFormError('');
                                  adminItemsAPI.proxySupplierPhoto(queueForm.photo_url)
                                    .then(res => {
                                      const blob = res.data;
                                      const f = new File([blob], 'supplier.jpg', { type: blob.type || 'image/jpeg' });
                                      setCatalogPhotoUploading(false);
                                      handleCatalogPhotoUpload(f);
                                    })
                                    .catch(() => {
                                      setCatalogPhotoUploading(false);
                                      setQueueFormError('Не удалось загрузить фото поставщика');
                                    });
                                }}
                              >
                                {catalogPhotoUploading ? 'Загрузка...' : 'Использовать фото поставщика'}
                              </button>
                            )}
                            <div
                              className={`queue-catalog-photo-drop${catalogPhotoDragOver ? ' queue-photo-box--dragover' : ''}`}
                              onClick={() => catalogPhotoInputRef.current?.click()}
                              onDragOver={e => { e.preventDefault(); setCatalogPhotoDragOver(true); }}
                              onDragLeave={() => setCatalogPhotoDragOver(false)}
                              onDrop={e => { e.preventDefault(); setCatalogPhotoDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleCatalogPhotoUpload(f); }}
                              title="Кликните или перетащите фото"
                            >
                              {catalogPhotoUploading ? (
                                <span>Загрузка...</span>
                              ) : catalogPhotoDragOver ? (
                                <span>Отпустите фото</span>
                              ) : queueForm.catalog_photo_url ? (
                                <RetryImage src={withBust(queueForm.catalog_photo_url)} alt="catalog" />
                              ) : (
                                <span>+ фото / перетащить</span>
                              )}
                            </div>
                            {queueForm.catalog_photo_url && (
                              <input
                                className="queue-form-input"
                                value={queueForm.catalog_photo_url}
                                onChange={e => setQueueForm(f => ({ ...f, catalog_photo_url: e.target.value.trim() }))}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Кроппер — поверх всего */}
                  {cropFile && (
                    <CropModal
                      file={cropFile}
                      onConfirm={(croppedFile) => { setCropFile(null); _uploadCatalogFile(croppedFile); }}
                      onCancel={() => setCropFile(null)}
                    />
                  )}

                  {queueFormError && <p className="queue-form-error">{queueFormError}</p>}
                  {queueFormDone && <p className="queue-form-done">{queueFormDone}</p>}

                  {queueForm.is_new ? (
                    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                      {cbSection === 'clothing' ? (
                        <>
                          {clothingAddError && <p className="queue-form-error">{clothingAddError}</p>}
                          {clothingAddDone && <p className="queue-form-done">{clothingAddDone}</p>}
                          <button
                            className="queue-form-submit queue-form-submit--new"
                            onClick={() => handleAddClothingItem(queueForm.photo_url || articleDetails?.photo_url || '')}
                            disabled={clothingAddSaving || !clothingAddForm.name.trim()}
                          >
                            {clothingAddSaving ? 'Добавляем...' : '▶ Добавить в каталог'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="queue-form-submit queue-form-submit--new"
                            onClick={handleEnqueueNewCatalogItem}
                            disabled={queueFormSaving || !newItemForm.after_id || !newItemForm.product_name || !newItemForm.season}
                          >
                            {queueFormSaving ? 'Добавляем...' : '▶ В очередь (новый товар)'}
                          </button>
                          {DEBUG_STEP_MODE && (
                            <button
                              className="queue-form-submit queue-form-submit--debug"
                              onClick={() => {
                                if (!newItemForm.after_id)     { setQueueFormError('Укажите артикул, после которого вставить'); return; }
                                if (!newItemForm.product_name) { setQueueFormError('Укажите название товара'); return; }
                                if (!newItemForm.season)       { setQueueFormError('Укажите сезон'); return; }
                                if (!queueForm.photo_url)      { setQueueFormError('Фото поставщика обязательно'); return; }
                                setQueueFormError('');
                                setNewCatalogStepperOpen(true);
                              }}
                              disabled={!newItemForm.after_id || !newItemForm.product_name || !newItemForm.season}
                            >
                              🔍 Пошагово (debug)
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                      <button
                        className="queue-form-submit"
                        onClick={handleEnqueueSupplierItem}
                        disabled={queueFormSaving || !queueForm.catalog_id}
                      >
                        {queueFormSaving ? 'Добавляем...' : '▶ В очередь'}
                      </button>
                      {DEBUG_STEP_MODE && (
                        <button
                          className="queue-form-submit queue-form-submit--debug"
                          onClick={() => {
                            if (!queueForm.catalog_id) { setQueueFormError('Укажите артикул товара из каталога'); return; }
                            setQueueFormError('');
                            setDebugStepperOpen(true);
                          }}
                          disabled={!queueForm.catalog_id}
                        >
                          🔍 Пошагово (debug)
                        </button>
                      )}
                    </div>
                  )}

                  {!renameDone && similarArticles.length > 0 && !queueForm.is_new && (
                    <div className="rename-warning">
                      <div className="rename-warning-title">Похожий артикул уже есть в базе</div>
                      {similarArticles.map(sa => (
                        <div key={sa.supplier_code} className="rename-warning-item">
                          {/* Фото рядом */}
                          <div className="rename-warning-photos">
                            <div className="rename-warning-photo-col">
                              {sa.photo_url ? (
                                <RetryImage
                                  className="rename-warning-photo"
                                  src={withBust(sa.photo_url)}
                                  alt="было"
                                  onClick={() => setCompareProduct({ photo_url: sa.photo_url })}
                                />
                              ) : (
                                <div className="rename-warning-no-photo">нет фото</div>
                              )}
                            </div>
                            <div className="rename-warning-arrow">→</div>
                            <div className="rename-warning-photo-col">
                              {(queueForm.photo_url || articleDetails?.photo_url) ? (
                                <RetryImage
                                  className="rename-warning-photo"
                                  src={withBust(queueForm.photo_url || articleDetails?.photo_url)}
                                  alt="стало"
                                  onClick={() => setCompareProduct({ photo_url: queueForm.photo_url || articleDetails?.photo_url })}
                                />
                              ) : (
                                <div className="rename-warning-no-photo">нет фото</div>
                              )}
                            </div>
                          </div>
                          {/* Артикулы стопкой */}
                          <div className="rename-warning-articles">
                            {sa.catalog_id && (
                              <div className="rename-warning-article-row">
                                <code className="rename-warning-code rename-warning-code--catalog" title={sa.catalog_id}>{sa.catalog_id}</code>
                              </div>
                            )}
                            <div className="rename-warning-article-row">
                              <code className="rename-warning-code" title={sa.supplier_code}>{sa.supplier_code}</code>
                            </div>
                            <div className="rename-warning-article-row">
                              <code className="rename-warning-code" title={selectedArticle}>{selectedArticle}</code>
                            </div>
                          </div>
                          <div className="rename-warning-actions">
                            <button
                              className="rename-btn rename-btn--confirm"
                              disabled={renameLoading}
                              onClick={() => handleRenameArticle(sa.supplier_code)}
                            >
                              {renameLoading ? 'Обновляем...' : 'Да, это переименование — обновить'}
                            </button>
                            <button
                              className="rename-btn rename-btn--dismiss"
                              onClick={() => setSimilarArticles([])}
                            >
                              Нет, добавить как новый
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Колонка 4 — мини-каталог */}
            <div className="queue-catalog-browser">

              {/* Шапка: переключатель секции + поиск */}
              <div className="queue-panel-header queue-catalog-header">
                <div className="queue-catalog-section-toggle">
                  <button
                    className={`queue-section-btn${cbSection === 'shoes' ? ' active' : ''}`}
                    onClick={() => setCbSection('shoes')}
                  >Обувь</button>
                  <button
                    className={`queue-section-btn${cbSection === 'clothing' ? ' active' : ''}`}
                    onClick={() => setCbSection('clothing')}
                  >Одежда</button>
                </div>
                {cbSection === 'shoes' ? (
                  <>
                    <input
                      className="queue-browser-search"
                      placeholder="Поиск..."
                      value={cbQuery}
                      onChange={e => {
                        const val = e.target.value;
                        setCbQuery(val);
                        clearTimeout(cbSearchTimerRef.current);
                        cbSearchTimerRef.current = setTimeout(() => {
                          loadCatalogBrowser({ query: val, brand: cbBrand, model: cbModel, page: 0 });
                          loadCbBrandsModels(val);
                        }, 250);
                      }}
                    />
                    {cbQuery && (
                      <button
                        className="queue-browser-clear"
                        title="Очистить поиск"
                        onClick={() => {
                          setCbQuery('');
                          loadCatalogBrowser({ query: '', brand: cbBrand, model: cbModel, page: 0 });
                          loadCbBrandsModels('');
                        }}
                      >×</button>
                    )}
                  </>
                ) : (
                  <>
                    <nav className="queue-clothing-breadcrumb">
                      <span
                        className={`queue-clothing-bc-item${cbClothingStep === 'type' ? ' queue-clothing-bc-item--current' : ''}`}
                        onClick={cbClothingStep !== 'type' ? () => {
                          setCbClothingStep('type');
                          setCbClothingType('');
                          setCbClothingBrand('');
                          setCbClothingModels([]);
                          updateClothingNavFilter(cbClothingQuery, 'type', '');
                        } : undefined}
                      >Тип</span>
                      {cbClothingType && (
                        <>
                          <span className="queue-clothing-bc-sep">›</span>
                          <span
                            className={`queue-clothing-bc-item${cbClothingStep === 'brand' ? ' queue-clothing-bc-item--current' : ''}`}
                            onClick={cbClothingStep !== 'brand' ? () => {
                              setCbClothingStep('brand');
                              setCbClothingBrand('');
                              setCbClothingModels([]);
                              updateClothingNavFilter(cbClothingQuery, 'brand', cbClothingType);
                            } : undefined}
                          >{cbClothingType}</span>
                        </>
                      )}
                      {cbClothingBrand && (
                        <>
                          <span className="queue-clothing-bc-sep">›</span>
                          <span
                            className={`queue-clothing-bc-item${cbClothingStep === 'model' ? ' queue-clothing-bc-item--current' : ''}`}
                            onClick={cbClothingStep !== 'model' ? () => {
                              setCbClothingStep('model');
                              setCbClothingOcCode('');
                            } : undefined}
                          >{cbClothingBrand}</span>
                        </>
                      )}
                      {cbClothingStep === 'products' && (
                        <>
                          <span className="queue-clothing-bc-sep">›</span>
                          <span className="queue-clothing-bc-item queue-clothing-bc-item--current">{cbClothingOcCode || 'Все'}</span>
                        </>
                      )}
                    </nav>
                    <input
                      className="queue-browser-search"
                      placeholder="Артикул, название, цвет..."
                      value={cbClothingQuery}
                      onChange={e => {
                        const val = e.target.value;
                        setCbClothingQuery(val);
                        clearTimeout(cbClothingSearchTimerRef.current);
                        cbClothingSearchTimerRef.current = setTimeout(() => {
                          if (cbClothingStep === 'products') {
                            loadClothingCatalogBrowser({ query: val, page: 0 });
                          } else {
                            updateClothingNavFilter(val, cbClothingStep, cbClothingType, cbClothingBrand);
                          }
                        }, 300);
                      }}
                    />
                    {cbClothingQuery && (
                      <button className="queue-browser-clear" onClick={() => {
                        setCbClothingQuery('');
                        setCbClothingNavFilter(null);
                        if (cbClothingStep === 'products') loadClothingCatalogBrowser({ query: '', page: 0 });
                      }}>×</button>
                    )}
                  </>
                )}
              </div>

              {cbSection === 'shoes' ? (
                /* Тело обуви: фильтры-дерево слева + товары справа (+ панель
                   результатів локального пошуку праворуч, коли є — поруч,
                   а не поверх каталогу) */
                <div
                  className="queue-catalog-body"
                  style={(photoSearchResults && photoSearchResults.length > 0)
                    ? { gridTemplateColumns: '170px 2fr 1fr' } : undefined}
                >

                  {/* Боковая панель брендов/моделей */}
                  <div className="queue-filter-sidebar">
                    {Object.keys(cbBrandsModels).sort().map(brand => {
                      const isActive = cbBrand === brand;
                      const models = cbBrandsModels[brand] || [];
                      return (
                        <div key={brand} className="qf-brand-block">
                          <div
                            className={`qf-brand${isActive ? ' active' : ''}`}
                            onClick={() => {
                              const nb = isActive ? '' : brand;
                              setCbBrand(nb); setCbModel('');
                              loadCatalogBrowser({ query: cbQuery, brand: nb, model: '', page: 0 });
                            }}
                          >
                            {brand}
                          </div>
                          {isActive && models.map(model => (
                            <div
                              key={model}
                              className={`qf-model${cbModel === model ? ' active' : ''}`}
                              onClick={() => {
                                const nm = cbModel === model ? '' : model;
                                setCbBrand(brand); setCbModel(nm);
                                loadCatalogBrowser({ query: cbQuery, brand, model: nm, page: 0 });
                              }}
                            >
                              {model}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {/* Сетка товаров обуви */}
                  <div className="queue-catalog-products" ref={cbProductsRef}>
                    {cbLoading && cbProducts.length === 0 ? (
                      <div className="queue-browser-loading">Загрузка...</div>
                    ) : (
                      <div className="queue-catalog-grid">
                        {cbProducts.map(p => (
                          <div
                            key={p.id}
                            data-id={p.id}
                            className={`queue-catalog-card${selectedCatalogProduct?.id === p.id ? ' active' : ''}`}
                            onClick={() => handleCardClick(p)}
                            title="Одинарный клик — сравнить · Двойной — выбрать"
                          >
                            <CatalogCardImage src={withBust(p.photo_url)} alt={p.name} />
                            <div className="queue-catalog-card-info">
                              <span className="queue-catalog-card-sku">{p.sku}</span>
                              <span className="queue-catalog-card-name">{p.brand} {p.model}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Sentinel всегда в DOM — IntersectionObserver не теряет элемент при фильтрации */}
                    <div ref={cbSentinelRef} className="queue-cb-sentinel" />
                    {cbLoading && cbProducts.length > 0 && <div className="queue-browser-loading">Загрузка...</div>}
                  </div>

                  {/* Панель результатів локального пошуку — окрема колонка (1/3),
                      поруч з каталогом (2/3), не поверх нього */}
                  {photoSearchResults && photoSearchResults.length > 0 && (
                    <div className="queue-catalog-products" style={{ borderLeft: '1px solid var(--lg-border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 10px 6px', fontSize: 12, color: 'var(--lg-text-3)' }}>
                        <span>🔍 Схожі ({photoSearchResults.length})</span>
                        <button className="rt-btn rt-btn-sm" onClick={() => setPhotoSearchResults(null)}>✕</button>
                      </div>
                      <div className="queue-catalog-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', padding: '0 10px 10px' }}>
                        {photoSearchResults.map(r => (
                          <div
                            key={r.sku}
                            className={`queue-catalog-card${selectedCatalogProduct?.sku === r.sku ? ' active' : ''}`}
                            onClick={() => handlePickPhotoSearchResult(r)}
                            title={`${r.sku} — збіг ${r.score}% · клік — вибрати`}
                            style={{ position: 'relative' }}
                          >
                            <button
                              type="button"
                              className="rt-btn rt-btn-sm"
                              style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, padding: '2px 5px', fontSize: 11 }}
                              title="Фото не соответствует артикулу — убрать из поиска похожих"
                              onClick={(e) => handleDeleteFromIndex(r, e)}
                            >🗑</button>
                            <CatalogCardImage
                              src={`${PHOTO_SEARCH_BASE}/img?url=${encodeURIComponent(r.photo_url)}`}
                              alt={r.name || r.sku}
                              proxyFn={u => u}
                            />
                            <div className="queue-catalog-card-info">
                              <span className="queue-catalog-card-sku">{r.sku}</span>
                              {r.measurements && <span className="queue-catalog-card-size" title="Розмір">📐 {r.measurements}</span>}
                              <span className="queue-catalog-card-name">{r.score}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              ) : (
                /* Тело одежды: последовательная навигация тип → бренд → товары
                   (+ панель результатів локального пошуку праворуч, коли є —
                   так само, як і в обуви) */
                <div
                  className="queue-catalog-body"
                  style={(photoSearchResults && photoSearchResults.length > 0)
                    ? { gridTemplateColumns: '2fr 1fr' } : { gridTemplateColumns: '1fr' }}
                >
                <div className="queue-clothing-nav">

                  {/* Шаг 1: Тайлы типов */}
                  {cbClothingStep === 'type' && (
                    <div className="queue-clothing-tiles-wrap" ref={cbClothingProductsRef}>
                      {cbClothingNav.types.length === 0 && (
                        <div className="queue-browser-loading">Загрузка...</div>
                      )}
                      <div className="queue-clothing-tiles">
                        {cbClothingNav.types
                          .filter(t => !cbClothingNavFilter || cbClothingNavFilter.has(t.name))
                          .map(t => (
                            <button key={t.name} className="queue-clothing-tile" onClick={() => {
                              setCbClothingType(t.name);
                              setCbClothingBrand('');
                              setCbClothingStep('brand');
                              updateClothingNavFilter(cbClothingQuery, 'brand', t.name);
                            }}>
                              <div className="queue-clothing-tile-img">
                                {t.photo_url
                                  ? <img src={proxyClothingImageUrl(t.photo_url)} alt={t.name} loading="lazy" />
                                  : <span className="queue-clothing-tile-placeholder">{t.name.charAt(0)}</span>
                                }
                              </div>
                              <div className="queue-clothing-tile-name">{t.name}</div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Шаг 2: Тайлы брендов */}
                  {cbClothingStep === 'brand' && (
                    <div className="queue-clothing-tiles-wrap" ref={cbClothingProductsRef}>
                      <div className="queue-clothing-tiles">
                        {/* Все бренды */}
                        <button className="queue-clothing-tile queue-clothing-tile--all" onClick={() => {
                          setCbClothingBrand('');
                          setCbClothingStep('products');
                          setCbClothingNavFilter(null);
                          loadClothingCatalogBrowser({ type: cbClothingType, brand: '', query: cbClothingQuery, page: 0 });
                        }}>
                          <div className="queue-clothing-tile-img">
                            <span className="queue-clothing-tile-placeholder">★</span>
                          </div>
                          <div className="queue-clothing-tile-name">Все</div>
                        </button>
                        {(cbClothingNav.brands_by_type?.[cbClothingType] || [])
                          .filter(b => !cbClothingNavFilter || cbClothingNavFilter.has(b.name))
                          .map(b => (
                            <button key={b.name} className="queue-clothing-tile" onClick={() => {
                              setCbClothingBrand(b.name);
                              setCbClothingOcCode('');
                              setCbClothingStep('model');
                              setCbClothingNavFilter(null);
                              setCbClothingModelsLoading(true);
                              setCbClothingModels([]);
                              clothingAPI.getModels(cbClothingType, b.name)
                                .then(r => setCbClothingModels(r.data.models || []))
                                .catch(() => setCbClothingModels([]))
                                .finally(() => setCbClothingModelsLoading(false));
                              if (cbClothingQuery.trim()) {
                                updateClothingNavFilter(cbClothingQuery, 'model', cbClothingType, b.name);
                              }
                            }}>
                              <div className="queue-clothing-tile-img">
                                {b.photo_url
                                  ? <img src={proxyClothingImageUrl(b.photo_url)} alt={b.name} loading="lazy" />
                                  : <span className="queue-clothing-tile-placeholder">{b.name.charAt(0)}</span>
                                }
                              </div>
                              <div className="queue-clothing-tile-name">{b.name}</div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Шаг 3: Тайлы моделей (группы цветов) */}
                  {cbClothingStep === 'model' && (
                    <div className="queue-clothing-tiles-wrap" ref={cbClothingProductsRef}>
                      {cbClothingModelsLoading && (
                        <div className="queue-browser-loading">Загрузка моделей...</div>
                      )}
                      {!cbClothingModelsLoading && (
                        <div className="queue-clothing-tiles queue-clothing-tiles--models">
                          {/* Все модели бренда без фильтра по oc_code */}
                          <button className="queue-clothing-tile queue-clothing-tile--all" onClick={() => {
                            setCbClothingOcCode('');
                            setCbClothingStep('products');
                            loadClothingCatalogBrowser({ type: cbClothingType, brand: cbClothingBrand, oc_code: '', query: cbClothingQuery, page: 0 });
                          }}>
                            <div className="queue-clothing-tile-img">
                              <span className="queue-clothing-tile-placeholder">★</span>
                            </div>
                            <div className="queue-clothing-tile-name">Все</div>
                          </button>
                          {(cbClothingNavFilter ? cbClothingSearchModels : cbClothingModels)
                            .map(m => (
                            <button key={m.oc_code} className="queue-clothing-tile" onClick={() => {
                              setCbClothingOcCode(m.oc_code);
                              setCbClothingStep('products');
                              loadClothingCatalogBrowser({ type: cbClothingType, brand: cbClothingBrand, oc_code: m.oc_code, query: cbClothingQuery, page: 0 });
                            }}>
                              <div className="queue-clothing-tile-img">
                                {m.photo_url
                                  ? <img src={proxyClothingImageUrl(m.photo_url)} alt={m.name || m.oc_code} loading="lazy" />
                                  : <span className="queue-clothing-tile-placeholder">{(m.name || m.oc_code).charAt(0)}</span>
                                }
                              </div>
                              <div className="queue-clothing-tile-name">{m.name || m.oc_code}</div>
                              {m.measurements && <div className="queue-clothing-tile-meas">{m.measurements}</div>}
                            </button>
                          ))}
                          {!cbClothingNavFilter && cbClothingModels.length === 0 && (
                            <div className="queue-browser-loading">Нет моделей</div>
                          )}
                          {cbClothingNavFilter && cbClothingSearchModels.length === 0 && (
                            <div className="queue-browser-loading">Нет совпадений</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Шаг 4: Сетка товаров */}
                  {cbClothingStep === 'products' && (
                    <div className="queue-catalog-products" ref={cbClothingProductsRef}>
                      {cbClothingLoading && cbClothingProducts.length === 0 ? (
                        <div className="queue-browser-loading">Загрузка...</div>
                      ) : cbClothingProducts.length === 0 ? (
                        <div className="queue-browser-loading">Нет товаров</div>
                      ) : (
                        <div className="queue-catalog-grid">
                          {cbClothingProducts.map(p => (
                            <div
                              key={p.id}
                              data-id={p.id}
                              className={`queue-catalog-card${selectedCatalogProduct?.id === p.id ? ' active' : ''}`}
                              onClick={() => handleClothingCardClick(p)}
                              title="Одинарный клик — сравнить · Двойной — выбрать"
                            >
                              <CatalogCardImage src={withBust(p.photo_url)} alt={p.name} proxyFn={proxyClothingImageUrl} />
                              <div className="queue-catalog-card-info">
                                <span className="queue-catalog-card-sku">{p.sku}</span>
                                {p.measurements
                                  ? <span className="queue-catalog-card-size" title="Розмір">📐 {p.measurements}</span>
                                  : <span className="queue-catalog-card-name">{p.sizes || p.availability}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Sentinel всегда в DOM на шаге products — IntersectionObserver не теряет элемент */}
                      <div ref={cbClothingSentinelRef} className="queue-cb-sentinel" />
                      {cbClothingLoading && cbClothingProducts.length > 0 && <div className="queue-browser-loading">Загрузка...</div>}
                    </div>
                  )}

                </div>

                {/* Панель результатів локального пошуку — окрема колонка (1/3),
                    поруч з навігацією одягу (2/3), не поверх неї */}
                {photoSearchResults && photoSearchResults.length > 0 && (
                  <div className="queue-catalog-products" style={{ borderLeft: '1px solid var(--lg-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 10px 6px', fontSize: 12, color: 'var(--lg-text-3)' }}>
                      <span>🔍 Схожі ({photoSearchResults.length})</span>
                      <button className="rt-btn rt-btn-sm" onClick={() => setPhotoSearchResults(null)}>✕</button>
                    </div>
                    <div className="queue-catalog-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', padding: '0 10px 10px' }}>
                      {photoSearchResults.map(r => (
                        <div
                          key={r.sku}
                          className={`queue-catalog-card${selectedCatalogProduct?.sku === r.sku ? ' active' : ''}`}
                          onClick={() => handlePickPhotoSearchResult(r)}
                          title={`${r.sku} — збіг ${r.score}% · клік — вибрати`}
                          style={{ position: 'relative' }}
                        >
                          <button
                            type="button"
                            className="rt-btn rt-btn-sm"
                            style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, padding: '2px 5px', fontSize: 11 }}
                            title="Фото не соответствует артикулу — убрать из поиска похожих"
                            onClick={(e) => handleDeleteFromIndex(r, e)}
                          >🗑</button>
                          <CatalogCardImage
                            src={`${PHOTO_SEARCH_BASE}/img?url=${encodeURIComponent(r.photo_url)}`}
                            alt={r.name || r.sku}
                            proxyFn={u => u}
                          />
                          <div className="queue-catalog-card-info">
                            <span className="queue-catalog-card-sku">{r.sku}</span>
                            {r.measurements && <span className="queue-catalog-card-size" title="Розмір">📐 {r.measurements}</span>}
                            <span className="queue-catalog-card-name">{r.score}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                </div>
              )}

            </div>

          </div>

          {showQueueActivity && (
            <div style={{ marginTop: 12, borderTop: '1px solid var(--lg-border)', paddingTop: 8 }}>
              <ActivityTab
                activity={activity}
                loading={activityLoading}
                days={activityDays}
                onChangeDays={d => { setActivityDays(d); loadActivity(d); }}
              />
            </div>
          )}
          </>
        )}

        {/* Оверлей сравнения фото */}
        {compareProduct !== null && (() => {
          const cmpList = compareSection === 'clothing' ? cbClothingProducts : cbProducts;
          const cmpHasMore = compareSection === 'clothing' ? cbClothingHasMore : cbHasMore;
          const effectiveIndex = compareIndex === -1
            ? cmpList.findIndex(x => x.id === compareProduct?.id)
            : compareIndex;
          const canPrev = effectiveIndex > 0;
          const canNext = effectiveIndex >= 0 && effectiveIndex < cmpList.length - 1;
          const isSelected = compareProduct?.id && compareProduct.id === selectedCatalogProduct?.id;
          const catalogLabel = compareSection === 'clothing'
            ? `${compareProduct?.brand || ''} ${compareProduct?.name || ''}`.trim()
            : `${compareProduct?.brand || ''} ${compareProduct?.model || ''}`.trim();
          const catalogPhotoSrc = compareSection === 'clothing' && compareProduct?.photo_url
            ? proxyClothingImageUrl(compareProduct.photo_url)
            : compareProduct?.photo_url;
          return (
            <div className="compare-overlay" onClick={() => setCompareProduct(null)}>
              <div className="compare-inner" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

                <div className="compare-toolbar">
                  {compareProduct?.id && !isSelected && (
                    <button
                      className="compare-select-btn"
                      onClick={() => { handleSelectCatalogProduct(compareProduct); setCompareProduct(null); }}
                    >
                      ✓ Выбрать этот товар
                    </button>
                  )}
                  {isSelected && <span className="compare-selected-label">✓ Выбран</span>}
                  {effectiveIndex >= 0 && cmpList.length > 0 && (
                    <span className="compare-counter">
                      {effectiveIndex + 1} / {cmpList.length}{cmpHasMore ? '+' : ''}
                    </span>
                  )}
                  <button className="compare-close" onClick={() => setCompareProduct(null)}>✕</button>
                </div>

                <div className="compare-photos">
                  <button
                    className={`compare-arrow compare-arrow--left${canPrev ? '' : ' disabled'}`}
                    onClick={() => canPrev && navigateCompareRef.current?.(-1)}
                    title="← Предыдущий (стрелка влево)"
                  >‹</button>

                  <div className="compare-photo-side">
                    <div className="compare-photo-label">Поставщик · {selectedSupplier?.supplier} · {selectedArticle}</div>
                    {(() => {
                      const supplierUrl = queueForm.photo_url || articleDetails?.photo_url;
                      return supplierUrl ? (
                        <>
                          <RetryImage className="compare-photo-img"
                            src={withBust(supplierUrl)} alt="supplier"
                            onClick={() => setCompareProduct(null)} style={{cursor:'zoom-out'}} />
                          {isOurImage(supplierUrl) && (
                            <div className="compare-rotate-bar">
                              <button className="compare-rotate-btn" title="Повернуть против часовой (−90°)"
                                disabled={!!rotatingUrl}
                                onClick={() => handleRotate(supplierUrl, -90)}>↺</button>
                              <button className="compare-rotate-btn" title="Повернуть по часовой (+90°)"
                                disabled={!!rotatingUrl}
                                onClick={() => handleRotate(supplierUrl, 90)}>↻</button>
                              <button className="compare-rotate-btn" title="Восстановить оригинал"
                                disabled={!!rotatingUrl}
                                onClick={() => handleRevert(supplierUrl)}>↩</button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="compare-photo-empty">Нет фото поставщика</div>
                      );
                    })()}
                  </div>

                  <div className="compare-divider" />

                  <div className="compare-photo-side">
                    <div className="compare-photo-label">
                      Каталог · {compareProduct?.sku}
                      {catalogLabel && ` · ${catalogLabel}`}
                    </div>
                    {catalogPhotoSrc ? (
                      <>
                        <RetryImage className="compare-photo-img"
                          src={withBust(catalogPhotoSrc)} alt="catalog"
                          onClick={() => setCompareProduct(null)} style={{cursor:'zoom-out'}} />
                        {isOurImage(catalogPhotoSrc) && (
                          <div className="compare-rotate-bar">
                            <button className="compare-rotate-btn" title="Повернуть против часовой (−90°)"
                              disabled={!!rotatingUrl}
                              onClick={() => handleRotate(catalogPhotoSrc, -90)}>↺</button>
                            <button className="compare-rotate-btn" title="Повернуть по часовой (+90°)"
                              disabled={!!rotatingUrl}
                              onClick={() => handleRotate(catalogPhotoSrc, 90)}>↻</button>
                            <button className="compare-rotate-btn" title="Восстановить оригинал"
                              disabled={!!rotatingUrl}
                              onClick={() => handleRevert(catalogPhotoSrc)}>↩</button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="compare-photo-empty">Нет фото каталога</div>
                    )}
                  </div>

                  <button
                    className={`compare-arrow compare-arrow--right${canNext ? '' : ' disabled'}`}
                    onClick={() => canNext && navigateCompareRef.current?.(1)}
                    title="→ Следующий (стрелка вправо)"
                  >›</button>
                </div>

              </div>
            </div>
          );
        })()}

        {/* DEBUG_TODO: remove after testing */}
        {debugStepperOpen && (
          <DebugStepper
            payload={{
              supplier: selectedSupplier?.supplier,
              supplier_article: selectedArticle,
              catalog_id: queueForm.catalog_id,
              photo_link: queueForm.photo_link,
              photo_url: queueForm.photo_url,
              keywords: queueForm.keywords,
              is_new: queueForm.is_new,
            }}
            onClose={() => setDebugStepperOpen(false)}
            onAllDone={() => {
              setDebugStepperOpen(false);
              setQueueFormDone(`Добавлено: ${selectedArticle} → ${queueForm.catalog_id}`);
              setSelectedSupplier(prev => ({
                ...prev,
                all_articles: prev.all_articles.filter(a => a !== selectedArticle),
                count: prev.count - 1,
              }));
              setQueue(prev => prev.map(s =>
                s.supplier === selectedSupplier?.supplier
                  ? { ...s, count: s.count - 1, all_articles: s.all_articles.filter(a => a !== selectedArticle) }
                  : s
              ).filter(s => s.count > 0));
              setSelectedArticle('');
              setQueueForm({ catalog_id: '', photo_link: '', photo_url: '', catalog_photo_url: '', keywords: '', is_new: false });
              setArticleDetails(null);
              setSelectedCatalogProduct(null);
            }}
          />
        )}

        {/* DEBUG_TODO: степпер нового товара */}
        {newCatalogStepperOpen && (
          <NewCatalogDebugStepper
            payload={{
              supplier: selectedSupplier?.supplier,
              supplier_article: selectedArticle,
              photo_link: queueForm.photo_link,
              photo_url: queueForm.photo_url,
              catalog_photo_url: queueForm.catalog_photo_url,
              after_id: newItemForm.after_id,
              product_name: newItemForm.product_name,
              season: newItemForm.season,
              color: newItemForm.color,
              new_list_name: newItemForm.new_list_name,
              keywords: queueForm.keywords,
            }}
            onClose={() => setNewCatalogStepperOpen(false)}
            onAllDone={(catalogId) => {
              setNewCatalogStepperOpen(false);
              setNewItemConfig(c => c ? { ...c, last_article: catalogId, next_article: catalogId } : null);
              _afterSuccess(catalogId);
            }}
          />
        )}


        {/* ── ВКЛАДКА: Управление товарами (изменить/удалить/объединить + под удаление, внутри своих под-вкладок) ── */}
        {activeTab === 'manage' && isRealAdmin && <ManageProductsTab />}

        {/* ── ВКЛАДКА: Активность ──────────────────────────────────────────── */}
        {activeTab === 'activity' && isRealAdmin && (
          <ActivityTab
            activity={activity}
            loading={activityLoading}
            days={activityDays}
            onChangeDays={d => { setActivityDays(d); loadActivity(d); }}
          />
        )}

        {/* ── ВКЛАДКА: Задачи ──────────────────────────────────────────────── */}
        {activeTab === 'jobs' && isRealAdmin && (
          <JobsTab
            jobs={jobs}
            loading={jobsLoading}
            onRetry={handleRetryJob}
            onCancel={handleCancelJob}
          />
        )}

        {/* ── ВКЛАДКА: Аналитика ───────────────────────────────────────────── */}
        {activeTab === 'analytics' && hasPerm('analytics') && <AnalyticsTab />}

        {/* ── ВКЛАДКА: Подборка ────────────────────────────────────────────── */}
        {activeTab === 'selection' && hasPerm('selection') && <SelectionTab />}

        {/* ── ВКЛАДКА: Cheese ──────────────────────────────────────────────── */}
        {activeTab === 'cheese' && (isRealAdmin || hasPerm('cheese')) && <CheeseTab />}

        {/* ── ВКЛАДКА: Розница ─────────────────────────────────────────────── */}
        {activeTab === 'roznitsa' && (hasPerm('retail_sites') || hasPerm('retail_publish') || hasPerm('retail_site_admin')) && (
          <RetailTab openSiteId={retailOpenSiteId} onSiteOpened={() => setRetailOpenSiteId(null)} addProductPrefill={addProductPrefill} />
        )}

        {/* ── ВКЛАДКА: Фікс Фото ───────────────────────────────────────────── */}
        {activeTab === 'photo_fix' && hasPerm('photo_fix') && <PhotoFixTab />}

        {/* ── ВКЛАДКА: Каталог Фото ────────────────────────────────────────── */}
        {activeTab === 'photo_catalog' && isRealAdmin && (
          <div style={{ padding: '12px 8px' }}>
            <PhotoCatalogSection />
          </div>
        )}

        {/* ── ВКЛАДКА: Автоматизація ───────────────────────────────────────── */}
        {activeTab === 'automation' && isRealAdmin && <AutomationTab />}

      </main>
    </div>
  );
};

// DEBUG_TODO: remove after testing ───────────────────────────────────────────

const DEBUG_STEPS = [
  { num: 1, label: 'Запись в основную таблицу', desc: (p) =>
    `Лист «${p.supplier}» · таблица MAIN (1yF59...)\nA=${p.supplier_article}  B=${p.catalog_id}  D=${p.photo_link || '—'}  E=${p.photo_url || '—'}  H=${p.keywords || '—'}` },
  { num: 2, label: 'Запись в личную таблицу поставщика', desc: () =>
    `Лист «ALLDROP ART» · личная таблица поставщика\n(те же данные)` },
  { num: 3, label: 'Лог активности + фото-бот', desc: () =>
    `Запись в БД (addition_log)\nВызов UpdatePhotoSearchBot (если есть фото и артикул ALL-*)` },
];

const DebugStepper = ({ payload, onClose, onAllDone }) => { // DEBUG_TODO
  const [currentStep, setCurrentStep] = useState(0); // 0 = preview, 1/2/3 = done steps
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const step = DEBUG_STEPS[currentStep] || null;
  const allDone = currentStep >= DEBUG_STEPS.length;

  const runStep = async () => {
    const s = DEBUG_STEPS[currentStep];
    setLoading(true);
    setError('');
    try {
      const res = await adminItemsAPI.debugStep(s.num, payload);
      setResults(prev => ({ ...prev, [s.num]: res.data }));
      setCurrentStep(prev => prev + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="debug-overlay"> {/* DEBUG_TODO */}
      <div className="debug-modal">
        <div className="debug-modal-header">
          <span>🔍 Пошаговое добавление товара</span>
          <button className="debug-close" onClick={onClose}>✕</button>
        </div>

        <div className="debug-steps-list">
          {DEBUG_STEPS.map((s, i) => {
            const done = i < currentStep;
            const active = i === currentStep;
            const res = results[s.num];
            return (
              <div key={s.num} className={`debug-step ${done ? 'done' : active ? 'active' : 'pending'}`}>
                <div className="debug-step-num">{done ? '✓' : s.num}</div>
                <div className="debug-step-body">
                  <div className="debug-step-label">{s.label}</div>
                  {active && (
                    <pre className="debug-step-desc">{s.desc(payload)}</pre>
                  )}
                  {done && res && (
                    <pre className="debug-step-result">
                      {res.skipped
                        ? `⚠ Пропущено: ${res.reason}`
                        : res.table_id
                          ? `✓ Строка ${res.row} в таблице ${res.table_id}\n  лист: ${res.sheet}`
                          : `✓ log_id=${res.log_id}  бот: ${res.photo_bot}`}
                    </pre>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {error && <div className="debug-error">Ошибка: {error}</div>}

        <div className="debug-modal-footer">
          {allDone ? (
            <button className="debug-btn debug-btn--done" onClick={onAllDone}>
              ✓ Готово
            </button>
          ) : (
            <>
              <div className="debug-next-label">
                Шаг {currentStep + 1} из {DEBUG_STEPS.length}: {step?.label}
              </div>
              <button className="debug-btn" onClick={runStep} disabled={loading}>
                {loading ? 'Выполняется...' : 'Подтвердить и выполнить →'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// DEBUG_TODO: remove after testing ───────────────────────────────────────────

const NEW_CATALOG_STEPS = [
  { num: 0, label: 'Предпросмотр (без записи)',    desc: (p) => `Читаем конфиг из листа "Добавить НОВЫЙ товар"\nПроверяем артикул поставщика на дубликаты\nПоказываем что будет записано` },
  { num: 1, label: 'Запись в лист поставщика',     desc: (p) => `Таблица MAIN (1yF59...)\nЛист «${p.supplier}»\nA=${p.supplier_article}  C=ФОТО  D=${p.photo_link||'—'}  E=${p.photo_url?.slice(0,40)}...` },
  { num: 2, label: 'Запись в ALLDROP ART',         desc: (p) => `Личная таблица поставщика\nЛист «ALLDROP ART» — та же строка` },
  { num: 3, label: 'Вставка в КаталогТоваров',     desc: (p) => `Таблица 1ldtPwF...\nВставить строку ПОСЛЕ "${p.after_id}"\nB=новый_артикул  C=${p.product_name}  G=${p.color||'—'}  H=${p.season}` },
  { num: 4, label: p => p.new_list_name ? 'Бот CreateNewList (новая модель)' : 'Копирование блоков ALL + Setting',
            desc: (p) => p.new_list_name
              ? `POST /api/CreateNewList\nНовый лист: "${p.new_list_name}"`
              : `СпопироватьДиапазон → ALL таблица\nСпопироватьДиапазонSetting → Setting таблица` },
  { num: 5, label: 'Список товаров + E1 + лог',    desc: () => `Запись в лист "Список товаров"\nОбновление E1 (следующий артикул)\nЛог в БД + фото-бот` },
];

const NewCatalogDebugStepper = ({ payload, onClose, onAllDone }) => { // DEBUG_TODO
  const [currentStep, setCurrentStep] = useState(0);
  const [results, setResults]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const step = NEW_CATALOG_STEPS[currentStep] || null;
  const allDone = currentStep >= NEW_CATALOG_STEPS.length;

  const runStep = async () => {
    const s = NEW_CATALOG_STEPS[currentStep];
    setLoading(true);
    setError('');
    try {
      const res = await adminItemsAPI.debugNewCatalogStep(s.num, payload);
      setResults(prev => ({ ...prev, [s.num]: res.data }));
      setCurrentStep(prev => prev + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };

  const retryPrevStep = async () => {
    const prevIdx = currentStep - 1;
    if (prevIdx < 0) return;
    const s = NEW_CATALOG_STEPS[prevIdx];
    setCurrentStep(prevIdx);
    setLoading(true);
    setError('');
    try {
      const res = await adminItemsAPI.debugNewCatalogStep(s.num, payload);
      setResults(prev => ({ ...prev, [s.num]: res.data }));
      setCurrentStep(prevIdx + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };

  const stepLabel = (s) => typeof s.label === 'function' ? s.label(payload) : s.label;

  return (
    <div className="debug-overlay">
      <div className="debug-modal">
        <div className="debug-modal-header">
          <span>🆕 Пошаговое добавление нового товара в каталог</span>
          <button className="debug-close" onClick={onClose}>✕</button>
        </div>

        {NEW_CATALOG_STEPS.map((s, idx) => {
          const done    = idx < currentStep;
          const active  = idx === currentStep;
          const pending = idx > currentStep;
          const result  = results[s.num];
          return (
            <div key={s.num} className={`debug-step ${done ? 'done' : active ? 'active' : 'pending'}`}>
              <div className="debug-step-num">{done ? '✓' : s.num === 0 ? '👁' : s.num}</div>
              <div className="debug-step-body">
                <div className="debug-step-desc">
                  <strong>{stepLabel(s)}</strong>
                  {active && <pre className="debug-step-preview">{s.desc(payload)}</pre>}
                </div>
                {done && result && (
                  <pre className="debug-step-result">{JSON.stringify(result, null, 2)}</pre>
                )}
              </div>
            </div>
          );
        })}

        {error && <div className="debug-error">{error}</div>}

        <div className="debug-actions">
          {allDone ? (
            <button className="debug-btn debug-btn--done" onClick={() => onAllDone(results[5]?.catalog_id)}>
              ✓ Готово
            </button>
          ) : (
            <>
              {currentStep > 0 && (
                <button className="debug-btn debug-btn--retry" onClick={retryPrevStep} disabled={loading}>
                  {`↩ Повторить шаг ${NEW_CATALOG_STEPS[currentStep - 1]?.num}`}
                </button>
              )}
              <button className="debug-btn" onClick={runStep} disabled={loading}>
                {loading ? 'Выполняется...' : step?.num === 0 ? '👁 Проверить' : `Подтвердить шаг ${step?.num} →`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── SVG Bar Chart ─────────────────────────────────────────────────────────────

const ActivityTab = ({ activity, loading, days, onChangeDays }) => {
  const W = 820, H = 180, PAD_L = 32, PAD_R = 16, PAD_T = 12, PAD_B = 36;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const dayOptions = [7, 14, 30, 60, 90];

  if (loading || !activity) {
    return (
      <div className="activity-root">
        <div className="activity-loading">{loading ? 'Загрузка...' : ''}</div>
      </div>
    );
  }

  const data = activity.days;
  const maxVal = Math.max(1, ...data.map(d => d.total));
  const barGroupW = chartW / data.length;
  const barW = Math.max(2, Math.min(14, barGroupW * 0.4));
  const gap = Math.max(1, barW * 0.5);

  const yLabel = v => v;
  const yTicks = (() => {
    const step = maxVal <= 5 ? 1 : maxVal <= 20 ? 5 : maxVal <= 50 ? 10 : 20;
    const ticks = [];
    for (let v = 0; v <= maxVal; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] < maxVal) ticks.push(ticks[ticks.length - 1] + step);
    return ticks;
  })();
  const maxTick = yTicks[yTicks.length - 1];

  const toY = v => PAD_T + chartH - (v / maxTick) * chartH;

  // Show date labels every N days
  const labelStep = days <= 14 ? 1 : days <= 30 ? 5 : 10;

  return (
    <div className="activity-root">
      <div className="activity-header">
        <div className="activity-cards">
          <div className="activity-card">
            <span className="activity-card-value">{activity.totals.total}</span>
            <span className="activity-card-label">Добавлено товаров</span>
          </div>
          <div className="activity-card activity-card--new">
            <span className="activity-card-value">{activity.totals.new}</span>
            <span className="activity-card-label">Новых в каталоге</span>
          </div>
        </div>
        <div className="activity-days-picker">
          {dayOptions.map(d => (
            <button
              key={d}
              className={`activity-day-btn${days === d ? ' active' : ''}`}
              onClick={() => onChangeDays(d)}
            >{d}д</button>
          ))}
        </div>
      </div>

      <div className="activity-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="activity-svg">
          {/* Grid lines */}
          {yTicks.map(v => (
            <g key={v}>
              <line
                x1={PAD_L} y1={toY(v)} x2={W - PAD_R} y2={toY(v)}
                stroke="var(--border)" strokeWidth="1"
              />
              <text x={PAD_L - 4} y={toY(v) + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">
                {yLabel(v)}
              </text>
            </g>
          ))}

          {/* Bars */}
          {data.map((d, i) => {
            const cx = PAD_L + (i + 0.5) * barGroupW;
            const totalH = (d.total / maxTick) * chartH;
            const newH = (d.new / maxTick) * chartH;
            const totalX = cx - barW - gap / 2;
            const newX = cx + gap / 2;
            return (
              <g key={d.date}>
                {d.total > 0 && (
                  <rect
                    x={totalX} y={toY(d.total)} width={barW} height={totalH}
                    fill="#667eea" rx="2"
                  >
                    <title>{d.date}: {d.total} товаров</title>
                  </rect>
                )}
                {d.new > 0 && (
                  <rect
                    x={newX} y={toY(d.new)} width={barW} height={newH}
                    fill="#f59e0b" rx="2"
                  >
                    <title>{d.date}: {d.new} новых</title>
                  </rect>
                )}
                {i % labelStep === 0 && (
                  <text
                    x={cx} y={H - 4} textAnchor="middle"
                    fontSize="9" fill="var(--text-muted)"
                  >
                    {d.date.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <div className="activity-legend">
          <span className="activity-legend-item activity-legend-item--total">Добавлено</span>
          <span className="activity-legend-item activity-legend-item--new">Новые</span>
        </div>
      </div>

      {/* Таблица последних добавлений */}
      {activity.days.some(d => d.total > 0) && (
        <div className="activity-table-wrap">
          <table className="activity-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Добавлено</th>
                <th>Новых</th>
              </tr>
            </thead>
            <tbody>
              {[...activity.days].reverse().filter(d => d.total > 0).map(d => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td>{d.total}</td>
                  <td>{d.new > 0 ? <span className="activity-new-badge">{d.new}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Jobs Tab ──────────────────────────────────────────────────────────────────

const JOB_STATUS_LABEL = {
  pending:   'В очереди',
  running:   'Выполняется',
  completed: 'Готово',
  failed:    'Ошибка',
  cancelled: 'Отменено',
};

const JOB_STATUS_COLOR = {
  pending:   '#d97706',
  running:   '#2563eb',
  completed: '#16a34a',
  failed:    '#dc2626',
  cancelled: '#9ca3af',
};

const JobCard = ({ job, onRetry, onCancel }) => {
  const [expanded, setExpanded] = useState(job.status === 'failed');
  const steps = job.steps || [];
  const labels = job.step_labels || {};
  const color = JOB_STATUS_COLOR[job.status] || '#9ca3af';

  const fmt = (iso) => {
    if (!iso) return '';
    return formatKyivTime(iso, 'ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className={`job-card job-card--${job.status}`} onClick={() => setExpanded(e => !e)}>
      <div className="job-card-header">
        <div className="job-card-meta">
          <span className="job-card-id">#{job.id}</span>
          <span className="job-card-supplier">{job.supplier}</span>
          <span className="job-card-article">
            {job.job_type === 'rename_article' && job.new_article
              ? <>{job.supplier_article} <span style={{opacity:0.5}}>→</span> {job.new_article}</>
              : job.supplier_article}
          </span>
          <span className="job-card-type">
            {job.job_type === 'supplier_item' ? 'К существующему'
              : job.job_type === 'rename_article' ? 'Переименование'
              : job.job_type === 'update_supplier_item' ? 'Обновление товара поставщика'
              : job.job_type === 'replace_catalog_photo' ? 'Замена фото каталога'
              : job.job_type === 'delete_catalog_product' ? 'Удаление товара'
              : job.job_type === 'merge_catalog_products' ? 'Объединение товаров'
              : 'Новый в каталог'}
          </span>
        </div>
        <div className="job-card-right">
          <span className="job-card-status" style={{ color }}>{JOB_STATUS_LABEL[job.status]}</span>
          <span className="job-card-time">{fmt(job.created_at)}</span>
          {(job.status === 'pending' || job.status === 'failed') && (
            <button className="job-cancel-btn" onClick={e => { e.stopPropagation(); onCancel(job.id); }}>✕</button>
          )}
        </div>
      </div>

      <div className="job-steps">
        {steps.map(s => {
          const done    = s <= job.current_step;
          const isFail  = s === job.failed_step;
          const isCur   = job.status === 'running' && s === job.current_step + 1;
          return (
            <div key={s} className={`job-step${done ? ' done' : isFail ? ' failed' : isCur ? ' current' : ''}`}>
              <span className="job-step-dot">{done ? '✓' : isFail ? '✗' : isCur ? '…' : s}</span>
              <span className="job-step-label">{labels[s] || `Шаг ${s}`}</span>
            </div>
          );
        })}
      </div>

      {job.status === 'failed' && (
        <div className="job-error-block">
          <div className="job-error-msg">{job.error_message}</div>
          <button className="job-retry-btn" onClick={e => { e.stopPropagation(); onRetry(job.id); }}>
            ↩ Повторить шаг {job.failed_step}
          </button>
        </div>
      )}

      {expanded && job.step_results && job.step_results.length > 0 && (
        <div className="job-results" onClick={e => e.stopPropagation()}>
          {job.step_results.map(sr => (
            <div key={sr.step} className="job-step-result">
              <strong>Шаг {sr.step}:</strong>
              <pre>{JSON.stringify(sr.result, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobsTab = ({ jobs, loading, onRetry, onCancel }) => {
  const order = ['running', 'pending', 'failed', 'completed', 'cancelled'];
  const sorted = [...jobs].sort((a, b) => {
    const oi = order.indexOf(a.status);
    const oj = order.indexOf(b.status);
    if (oi !== oj) return oi - oj;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="jobs-tab">
      <div className="jobs-tab-header">
        <span className="jobs-tab-title">Фоновые задачи</span>
        <span className="jobs-tab-hint">Задачи выполняются последовательно. При ошибке нажмите «Повторить».</span>
      </div>
      {loading && jobs.length === 0 && <div className="jobs-loading">Загрузка...</div>}
      {!loading && jobs.length === 0 && <div className="jobs-empty">Нет задач</div>}
      <div className="jobs-list">
        {sorted.map(j => (
          <JobCard key={j.id} job={j} onRetry={onRetry} onCancel={onCancel} />
        ))}
      </div>
    </div>
  );
};

export default AdminPage;
